const en = {
  errors: {
    missingPermission:
      "You don't have permission for this action ({{permission}}).",
  },
  common: {
    scrollLeft: "Scroll left",
    scrollRight: "Scroll right",
    unsavedTitle: "Unsaved changes",
    unsavedDesc: "You have unsaved changes. Close without saving?",
    unsavedLeave: "Close",
    unsavedStay: "Stay",
    appName: "UnrealSoft",
    installBannerKicker: "PWA",
    installBannerTitle: "Install UnrealSoft",
    installBannerDesc: "Add the app to your home screen for quick access.",
    installBannerCta: "Install the app",
    restaurantSystem: "Restaurant management system",
    logout: "Logout",
    fullscreen: "Fullscreen",
    exitFullscreen: "Exit fullscreen",
    language: "Language",
    chooseLanguage: "Choose language",
    theme: "Theme",
    chooseTheme: "Choose theme",
    themeLight: "Light",
    themeDark: "Dark",
    newVersionTitle: "New version available",
    newVersionMessage: "Please reload the app to get it.",
    newVersionReload: "Reload",
    qrTitle: "QR code",
    qrSubtitle: "Scan to open the restaurant",
    backoffice: "Backoffice",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    close: "Close",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    forbidden: "You do not have permission for this operation",
    success: "Success",
    warning: "Warning",
    info: "Info",
    yes: "Yes",
    no: "No",
    ok: "OK",
    total: "Total",
    date: "Date",
    refresh: "Refresh",
    apply: "Apply",
    clear: "Clear",
    filters: "Filters",
    noData: "No data",
    product: "Product",
    today: "Today",
    yesterday: "Yesterday",
    print: "Print",
    options: "Options",
    quantity: "Quantity",
    price: "Price",
    amount: "Amount",
    status: "Status",
    comment: "Comment",
    details: "Details",
    view: "View",
    min: "min",
    selected: "Selected",
    select: "Select",
    max: "Max",
    totalRecords: "Total {{count}} records",
    totalReports: "Total {{count}} reports",
    securedJwt: "Secured with JWT • Anti-brute-force active",
    callPhone: "Call {{phone}}",
  },

  login: {
    welcomeKicker: "WELCOME",
    eyebrow: "Restaurant platform",
    heroHeadline: "Run your restaurant from a single calm place.",
    feature1: "Orders, bills and sales in real time",
    feature2: "Revenue reports and analytics",
    feature3: "Menu, tables and operator configuration",
    title: "Sign in",
    subtitle: "Please enter your credentials.",
    usernameLabel: "Username",
    usernamePlaceholder: "admin",
    usernameRequired: "Enter username",
    usernameMin: "Minimum 3 characters",
    passwordLabel: "Password",
    passwordPlaceholder: "••••••••",
    passwordRequired: "Enter password",
    passwordMin: "Password must be at least 3 characters",
    loginButton: "Sign in",
    loginSuccess: "Login successful",
    loginSuccessDesc: "Welcome back!",
    loginFailed: "Login failed",
    loginFailedDesc: "Check username and password.",
    heroDescription:
      "Manage all processes in your restaurant easily and quickly.",
    activeSession: "Active session",
    activeSessionDesc:
      "There is an active session with this user. Are you sure you want to terminate it?",
    terminateAndLogin: "Terminate & Login",
    insufficientLicenses: "Insufficient licenses",
    selectUserToLogout: "Please select a user to log out:",
    forceLogout: "Logout & Login",
    lastActivity: "Last activity:",
  },

  dashboard: {
    greeting: "Hello, {{name}}",
    subtitle:
      "Select a module and continue to the corresponding functionality.",
  },

  modules: {
    pageTitle: "Modules",
    welcomeKicker: "Welcome",
    ordering: "Ordering",
    orderingDesc:
      "Create orders, mark products by table and manage bills in real time",
    configuration: "Configuration",
    configurationDesc:
      "Configure monitors, fiscal printers and system settings for your establishment",
    monitors: "Monitors",
    monitorsDesc:
      "Monitor active orders by station and manage their status in real time",
    reports: "Reports",
    reportsDesc:
      "View bill history, personal and establishment reports, cancellations and split bills",
    reservations: "Reservations",
    reservationsDesc:
      "Plan and manage table reservations, schedules and availability by date",
    inventory: "Inventory",
    inventoryDesc:
      "Track stock levels, manage deliveries and control the movement of goods",
    discounts: "Discounts",
    discountsDesc:
      "Create and manage discounts, promotions and customer loyalty programs",
    delivery: "Delivery",
    deliveryDesc:
      "Organize deliveries, track couriers and manage delivery orders",
    changelog: "Changelog",
    changelogDesc:
      "Browse all versions, new features, improvements and fixes to the system",
    monitoring: "Monitoring",
    monitoringDesc:
      "Action audit log and fiscal issues — service access for the administrator",
    accessModal: {
      title: "Organization code",
      subtitle: "Enter the organization code",
      clear: "Clear",
      backspace: "Backspace",
      submit: "Log in",
      close: "Close",
    },
    noRoles: "No roles assigned",
    noRolesDesc:
      "To access modules, you need to be assigned roles/permissions. Please contact an administrator.",
    comingSoonKicker: "Coming soon",
    comingSoonTitle: "{{module}} is still in the works",
    comingSoonDesc:
      "We're building this module. Stay tuned — we'll let you know as soon as it ships.",
    comingSoonCta: "Got it",
    accessOn: "Access active",
    revokeAccess: "Exit restricted access",
    rolesLabel: "Roles",
  },

  navbar: {
    reportsAndAnalysis: "Reports & Analysis",
    configurationSubtitle: "System configuration",
    monitorsSubtitle: "Order monitoring",
    deliverySubtitle: "Delivery management",
    discountsSubtitle: "Discounts management",
    inventorySubtitle: "Inventory management",
    reservationsSubtitle: "Reservations management",
  },

  sidebar: {
    dashboard: "Dashboard",
    sales: "Sales",
    settings: "Settings",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
  },

  ordering: {
    // A new table with no internet (`NewTableModal`): the text ALSO serves as
    // the marker — the line about what a manager can do is shown under it.
    tableCheckOffline:
      "No connection to the cloud — the table was not created. Check the internet and try again.",
    tableCheckOfflineHint:
      "If the internet is down, a manager can switch the venue to working against the till.",
    tableCheckSkipped:
      "The name was not checked ({{reason}}) — the table was created anyway; a taken name would have been refused.",
    // Renaming a table (`RenameTableModal`): the name check failed. This is
    // NOT a refusal — "Save" stays enabled.
    renameCheckOffline:
      "No connection to the cloud — the name was not checked. \"Save\" still works: a taken name is refused by the rename itself.",
    renameCheckFailed:
      "The name check did not go through: {{reason}}. \"Save\" still works.",
    // Quick close with no cloud: the new table number comes from the tables on
    // THIS screen, not from the backend.
    nextTableNameOffline:
      "No connection to the cloud — the number was picked from the tables on this screen: {{name}}. Check with your colleagues before announcing it.",
    nextTableNameLocal:
      "The number was picked from the tables on this screen ({{name}}): {{reason}}",
    // An order sent to a cloud that never answered. It is not retried
    // automatically — hence the text sends the person to look at the table.
    orderCloudUnreachable:
      "No connection to the cloud. It is not certain the order arrived — check the table before sending it again.",
    tableKicker: "Table",
    loadingMenu: "Loading menu...",
    searchProduct: "Search product...",
    noProductsFound: "No products found",
    searchTable: "Search table, bill or waiter...",
    cardNoReceiptTag: "Card — no receipt",
    tableClosing: "Closing",
    // Tooltip of the guest-name tag on the table card
    seatedReservationTag: "Seated reservation — {{name}}",
    seatedReservation: "Reservation",
    noTablesFound: "No bills found",
    noTablesFoundHint: "Try a different keyword or clear the search.",
    noOpenTables: "No open bills yet",
    noOpenTablesHint:
      "Create a new table from the button below to start an order.",
    info: "Info",
    modify: "Modify",
    order: "Order",
    newTable: "New table",
    guestCount: {
      title: "Guests",
      quickPick: "Quick pick",
      keyboardHint: "Press a digit for a quick pick, ↑ ↓ to change.",
      confirm_one: "Confirm · {{count}} guest",
      confirm_other: "Confirm · {{count}} guests",
      saveError: "The guest count was not saved",
    },
    closeTable: "Close",
    cancelItem: "Cancel",
    split: "Split",
    clients: "Clients",
    billDiscountClient: "Client",
    billDiscountBase: "Base",
    billDiscountLabel: "Discount",
    billSurchargeLabel: "Surcharge",
    discounts: "Discounts",
    lockedPriceNoDiscount:
      "This product has a locked price — discounts cannot be applied.",
    customPrice: "Change price",
    preliminaryBill: "Preliminary bill",
    preliminaryBillPrinting: "Printing preliminary bill…",
    preliminaryBillSuccess: "The preliminary bill has been printed.",
    preliminaryBillError: "Could not print the preliminary bill.",
    report: "Report",
    operations: "Fiscal operations",
    invoicing: "Invoicing",
    restore: "Restore",
    selectTableToView:
      "Create a new table from the button below to start a new order.",
    noTableSelected: "No table selected",
    noTableSelectedHint:
      "Pick a table on the left to see its orders, or start a new one from the button below.",
    noOrdersForTable: "No orders for this table",
    noOrdersForTableHint:
      "Add products from the menu to start an order for this table.",
    tableNumber: "Table number",
    tableCreatedSuccess: 'Table "{{name}}" created and products added',
    tableJoinedSuccess: 'Table "{{name}}" opened and products added',
    productsAddedSuccess: "Products added to the table",
    selectProductsWarning: "Please select products to order",
    home: "Home",
    modifiers: "Modifiers:",
    addonRequired: "select",
    addonRecommended: "recommended",
    addonOptional: "optional",
    itemComment: "Comment",
    itemCommentPlaceholder: "Extra note for the product",
    orderPreview: "Order preview",
    summarized: "Summarized",
    orderComment: "Order comment:",
    commentPlaceholder: "Enter comment...",
    reservationNotice: {
      blockedTitle: "The table belongs to a seated reservation",
      linkedTable: "Table {{table}} belongs to {{guest}}'s seated reservation — the bill is on table {{billTable}}. Order there.",
      understood: "Got it",
      title: "The table has a reservation",
      question: "{{notice}}. Do you want to continue?",
      continue: "Continue",
      seated: "Table {{table}} is taken by {{guest}}'s reservation (seated {{time}})",
      upcoming_one: "Table {{table}} is reserved for {{time}} — {{guest}}, {{count}} guest",
      upcoming_other: "Table {{table}} is reserved for {{time}} — {{guest}}, {{count}} guests",
      // "Cancel" on quick open → offer the next free number
      openInstead: "Table {{reserved}} is reserved. Open table {{free}} instead?",
      openTable: "Open table {{name}}",
      noFreeTable: "No free table without a reservation — open one manually",
      searchingFreeTable: "Looking for a free table without a reservation…",
    },
    tableAlreadyExists: 'Table "{{name}}" already exists',
    pleaseChooseDifferentName: "Please choose a different table name",
    add: "Add",
    options: "Options",
    // Влачене между „Опции" и лентата под масите (TableFooterBar)
    optionsDragHint: "Press and hold a button, then drag it anywhere on the bar below — even between the main buttons. Drag it back the same way.",
    optionsDropHere: "Drop here to move the button back to Options",
    optionsEmpty: "All buttons are on the bar. Drag one here to move it back.",
    footerLayoutSaveError: "The bar layout was not saved",
    // Гласова поръчка (микрофонът до търсачката на масите)
    voiceOrder: "Voice order",
    voiceListening: "Speak…",
    voiceHold: "Hold and speak",
    voiceListeningHint:
      "For example: “Open table 1 and add 2 caesar salads with 2 colas”",
    voiceTranscribing: "Recognizing speech…",
    voiceProcessing: "Understanding the order…",
    voiceNotSupported:
      "This browser does not give access to the microphone. Open the app over https:// in an up-to-date browser.",
    voiceNoSpeech: "I did not hear anything. Please try again.",
    voiceMicDenied:
      "Microphone access denied — allow it from the browser settings.",
    voiceMicUnavailable:
      "The microphone is not available — check that another app is not using it.",
    voiceErrAudioTooLarge: "The recording is too long — try a shorter order.",
    voiceErrUnsupportedAudio:
      "The recording format is not supported by this browser.",
    voiceParseError: "Could not understand the order. Please try again.",
    // Codes come from the BE (502 → detail.code); an unknown code falls back
    // to voiceParseError. See cloudTS-restaurant-be/docs/cross-repo-contract.md.
    voiceErrTooLong: "The order is too long — split it into shorter ones.",
    voiceErrUnreadable:
      "The model returned an unreadable answer. Please try again.",
    voiceErrEmpty: "The model returned an empty answer. Please try again.",
    voiceErrRefused: "The model refused the request. Please try again.",
    voiceErrUpstream:
      "The recognition service is unavailable right now. Please try again.",
    voiceErrNotConfigured:
      "Voice orders are not configured. Please contact an administrator.",
    voiceNoItems: "No products were recognized.",
    // "Seat the reservation of …" by voice (voice/useVoiceSeat.tsx).
    voiceSeatNotFound: "No reservation to seat under the name “{{name}}”",
    voiceSeatNotAllowed: "You are not allowed to seat reservations",
    voiceRetry: "Try again",
    voiceContinue: "Continue",
    voiceChooseHint:
      "What you said matches several options — pick the one you meant.",
    voiceNeedsConfig: "Pick the required add-ons",
    voiceTable: "Table",
    voiceTableNew: "new table",
    voiceTableExisting: "adding to the open table",
    voiceTablePlaceholder: "type a table…",
    voiceNoMatch: "no match in the menu",
    voiceConfirm: "Order",
    voiceCancel: "Cancel",
    voiceSuccess: "The order for table “{{name}}” was sent.",
    itemServed: "Item marked as served",
    itemUnserved: "Serving undone",
    itemServedError: "Error marking item as served",
    servedStatus: "Served",
    statusLegend: "Status legend",
    statusNotStarted: "Not started",
    statusInProgress: "In progress",
    statusCooked: "Cooked",
    statusIssued: "Issued",
    closeTableSuccess: "Bill closed successfully.",
    closeTableError: "Error closing the bill.",
    renameTable: "Change table",
    renameTableTitle: "Rename table",
    renameTableSubtitle: "New table name",
    renameTableSuccess: 'Table renamed to "{{name}}"',
    renameTableError: "Error renaming the table",
    transferBill: "Change waiter",
    transferBillTitle: "Transfer bill to another waiter",
    transferBillSubtitle: "Waiters: {{count}}",
    transferBillConfirm: "Transfer",
    transferBillSuccess: "Bill transferred successfully",
    transferBillError: "Error transferring the bill",
    transferAllBills: "Transfer all my {{count}} tables",
    transferAllBillsSuccess: "{{count}} tables transferred",
    transferAllBillsError: "Error transferring the tables",
    discardItemsConfirmTitle: "Leave order",
    discardItemsConfirmContent:
      "Are you sure? The selected products will be removed.",
    productsShortCount: "{{count}} items",
    itemsShortCount: "{{count}} items",
    orderSheetTitle: "Order",
    modifyDone: "Add · {{price}}",
    modifyOrder: "Order · {{price}}",
    reorderUnavailable: "This product is not available in the menu",
    allCategories: "All categories",
    noGroup: "No group",
    emptyOrder: "Select products on the left to start an order.",
    emptyOrderShort: "No products yet.",
    allRows: "All",
    sortName: "ABC",
    sortPlu: "CODE",
    allStations: "All stations",
    viewTiles: "Tiles",
    viewTable: "Table",
    keyboard: "Keyboard",
    hideKeyboard: "Hide",
    space: "Space",
    clearSearch: "Clear",
    backspace: "Delete character",
    searchKicker: "Search",
    searchHint: "type a name or code",
    kbBds: "BDS",
    kbPhonetic: "Phonetic",
    kbLatin: "ABC",
    previewStations: "Stations",
    previewGroups: "Groups",
  },

  codeKeypad: {
    title: "Enter code",
    enterCode: "Enter code",
    invalidCode: "Invalid code",
    codeVerified: "Code verified successfully",
    submit: "Enter",
    kicker: "Sign in with personal code",
    clear: "Clear",
    venue: "Restaurant",
    online: "Online",
    hint: "You can also type on the keyboard",
  },

  logoutConfirm: {
    title: "Logout confirmation",
    subtitle: "Please enter your password to confirm",
    passwordPlaceholder: "Password",
    invalidPassword: "Invalid password",
    verificationError: "Error verifying password",
  },

  closeTable: {
    table: "Table:",
    operator: "Operator:",
    paymentMethod: "Payment method:",
    selectMethod: "Select method:",
    notSelected: "Not selected",
    noMethodsTitle: "No payment methods available",
    noMethodsDescription:
      "Add and activate methods in Configuration → Payment methods.",
    selectPaymentWarning: "Please select a payment method",
    sumMismatch: "Amount must be {{total}} Entered: {{entered}}.",
    totalChanged:
      "The bill has changed — the new total is {{total}}. Check and confirm again.",
    closeSuccess: "Bill closed successfully",
    closeError: "Error closing bill",
    alreadyClosed: "The bill is already closed",
    printerError: "Bill closed, but the printer returned an error",
    printerTimeout: "Bill closed, but the printer did not respond in time",
    closeButton: "Close bill",
    paymentFailed: "Payment failed — table {{table}}: {{text}}",
    stillClosing: "This table is still closing — wait for the receipt.",
    tableNotice: "Table {{table}} — {{text}}",
    paymentDistribution: "Payment distribution:",
    cash: "Cash",
    card: "Card",
    mixedTotal: "Total: {{entered}} / {{total}}",
    totalToPay: "Total to pay:",
    banknotes: "Banknotes:",
    received: "Received:",
    remaining: "Remaining to pay",
    amountBeforeDiscount: "Amount before discount",
    amountBeforeSurcharge: "Amount before surcharge",
    clientDiscount: "Client discount ({{percent}})",
    clientSurcharge: "Client surcharge ({{percent}})",
    change: "Change due",
    exactAmount: "Exact",
    cardTerminalHint: "Payment via card terminal.",
  },

  cancelItem: {
    titleAssortments: "Void assortments",
    titleRecipes: "Void recipes",
    subtitle: "Table {{name}} · Bill #{{id}} · total {{total}} €",
    ofQuantity: "of {{max}}",
    confirmButton: "Void",
    defective: "Defective",
    selectAll: "Select all",
    reasonLabel: "Reason",
    reasonWrongMark: "Marked by mistake",
    reasonCustomerRefused: "Customer refused",
    reasonOrderChanged: "Order changed",
    reasonOther: "Other",
    reasonPlaceholder: "Describe the reason...",
    // A failed void: the dialog STAYS open with the selection.
    failed:
      "The void did not go through. The dialog stays open — check the bill and try again.",
    failedReason:
      "The void did not go through: {{reason}}. Check the bill — the credit note may still have printed.",
  },

  splitBill: {
    title: "Split bill",
    subtitle: "Table {{name}} · {{total}} €",
    sumLabel: "Amount:",
    modeByProducts: "By products",
    modeEqual: "Equally",
    modifiersLabel: "Modifiers:",
    addonsLabel: "Addons:",
    selectedProducts: "Selected products: {{count}}",
    selectAll: "Select all",
    splitButton: "Split",
    cancelButton: "Cancel",
    splitError: "Error splitting bill",
    transferWaiterTitle: "Transfer to waiter",
    transferWaiterSubtitle: "Waiters: {{count}}",
    transferWaiterSearch: "Search waiter...",
    transferWaiterNone: "No waiters found",
    transferWaiterSkip: "Skip",
    transferWaiterConfirm: "Transfer",
    transferWaiterCurrent: "Current",
    transferWaiterResponsibility: "Responsibility for {{total}} EUR is transferred",
    transferWaiterFrom: "From",
    transferWaiterTo: "To",
    transferWaiterNoTarget: "choose a waiter",
    transferWaiterTablesOne: "1 table",
    transferWaiterTables: "{{count}} tables",
    transferWaiterConfirmTo: "Transfer to {{name}}",
    splitAndPayButton: "Split & pay",
    splitAndPayTitle: "Split & pay",
    splitAndPaySubtitle: "From bill #{{name}}",
    splitAndPaySuccess: "Bill split and paid successfully",
    splitAndPayError: "Error splitting and paying",
    billChanged: "The bill has changed — review the selected items and try again.",
  },

  confirmSplit: {
    title: "Table is occupied",
    transferQuestion:
      "Would you like to transfer the selected products to the already open table?",
    transferButton: "Transfer",
    occupiedByOtherWaiter: "Table “{{name}}” is already occupied by another waiter.",
    noTransferPermission:
      "You don't have permission to transfer split products to another waiter.",
    understoodButton: "Got it",
  },
  duplicateTableName: {
    title: "The table will be duplicated",
    transferMessage:
      "The waiter you are transferring to already has an open table “{{name}}”.",
    restoreMessage: "There is already an open table “{{name}}”.",
    transferAllMessage:
      "The waiter you are transferring to already has open tables with some of your names.",
    transferAllQuestion:
      "If you continue, there will be two open tables with the same name.",
    question: "If you continue, there will be two open tables named “{{name}}”.",
    confirm: "Continue",
  },

  itemDiscounts: {
    title: "Discounts",
    subtitle: "Table {{name}} · {{total}} €",
    emptyBill: "This bill has no products.",
    generalTitle: "General discount",
    generalHint: "Applies to the selected rows without their own discount",
    ownPercentCount_one: "{{count}} row has its own discount and stays unchanged",
    ownPercentCount_other: "{{count}} rows have their own discount and stay unchanged",
    none: "None",
    selectAll: "Select all",
    discountedOfTotal: "{{discounted}} of {{total}} discounted",
    removeAll: "Remove all",
    forThisRow: "For this row",
    followGeneral: "General · {{percent}}%",
    ownSuffix: "row",
    currentPercentTitle: "Now: −{{percent}}%",
    discountLabel: "Discount",
    customPriceHint: "custom price",
    applySuccess: "Discounts saved",
  },

  customPrice: {
    title: "Change price",
    subtitle: "Tap the pencil next to a product or addition and enter a new price",
    emptyBill: "This bill has no products.",
    changedCount: "Changed prices: {{count}}",
    invalidRows: "Invalid price on {{count}} row(s) — empty or 0.",
    restore: "Restore menu price",
    applySuccess: "Prices updated",
  },

  clients: {
    title: "Discount clients",
    total: "Total: {{count}}",
    noClients: "No clients found",
    onAccountBadge: "On account",
    select: "Select",
    searchPlaceholder: "Search by name or card...",
    applySuccess: "Discount applied successfully",
    applySurchargeSuccess: "Surcharge applied successfully",
    removeDiscount: "Remove applied discount ({{discount}})",
    removeDiscountNamed: "Remove discount - {{name}} ({{discount}})",
    removeSurcharge: "Remove applied surcharge ({{discount}})",
    removeSurchargeNamed: "Remove surcharge - {{name}} ({{discount}})",
    removeSuccess: "Discount removed successfully",
    removeSurchargeSuccess: "Surcharge removed successfully",
    active: "Active",
    inactive: "Inactive",
    from: "From",
    to: "To",
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    // Attached-customer banner (tables + close modal)
    bannerDiscountAssortments: "{{percent}} discount on all assortments",
    bannerDiscountRecipes: "{{percent}} discount on all recipes",
    bannerSurchargeAssortments: "{{percent}} surcharge on all assortments",
    bannerSurchargeRecipes: "{{percent}} surcharge on all recipes",
    // Some rows carry a different percent (row discount / custom price)
    bannerDiscountPartialAssortments:
      "{{percent}} discount on {{count}} of {{total}} assortments",
    bannerDiscountPartialRecipes:
      "{{percent}} discount on {{count}} of {{total}} recipes",
    bannerSurchargePartialAssortments:
      "{{percent}} surcharge on {{count}} of {{total}} assortments",
    bannerSurchargePartialRecipes:
      "{{percent}} surcharge on {{count}} of {{total}} recipes",
    bannerNoDiscount: "no discount",
    bannerSaved: "Saved",
    bannerSurcharged: "Surcharge",
  },

  invoicing: {
    selectBillTitle: "Select bill",
    selectBillSubtitle:
      "All closed bills not yet part of any report. Pick one or more to continue.",
    selectBillSubtitleWindow:
      "Uninvoiced closed bills from the last {{days}} days. Pick one or more to continue.",
    searchPlaceholder: "Search by table, waiter or payment",
    selectAll: "Select all",
    selectRow: "Select bill",
    colId: "Time",
    colTable: "Table",
    colWaiter: "Waiter",
    colPayment: "Payment",
    colTotal: "Total",
    confirm: "Select",
    confirmWithCount: "Select ({{count}})",
    selectedCount: "Selected bills: {{count}}",
    emptyAll: "No uninvoiced closed bills.",
    emptyFiltered: "No bills match your search.",
    loadError: "Failed to load bills",
    needDefaultCompany:
      "Set a current company first in Configuration → Companies.",
    warnAlreadyInvoiced: "The invoice has already been issued",
    warnOnlyClosed: "Only closed bills can be invoiced",
    warnCashNoReceipt:
      "The bill was closed in cash without a receipt and cannot be invoiced",
    warnWindowExpired: "Invoicing window expired ({{days}} days)",
  },

  invoicesReport: {
    title: "Invoices report",
    listKicker: "Invoices list",
    resultOne: "{{count}} invoice",
    results: "{{count}} invoices",
    searchPlaceholder: "Search by Invoice №, Customer or EIK",
    typeTax: "Tax",
    typeSimplified: "Simplified",
    clearFilters: "Clear filters",
    cancelledOnly: "Cancelled only",
    empty: "No invoices issued in the selected period.",
    view: "View",
    colNumber: "Invoice №",
    colDate: "Date",
    colTime: "Time",
    colType: "Type",
    colCustomer: "Customer",
    colEik: "EIK",
    colTaxable: "Taxable",
    colNonTaxable: "Non-taxable",
    colVatRate: "VAT %",
    colVat: "VAT",
    colGrand: "Grand total",
    colView: "Actions",
    kpiCount: "Invoices",
    kpiTaxable: "Taxable",
    kpiNonTaxable: "Non-taxable",
    kpiVat: "VAT",
    kpiGrand: "Grand total",
    cardRevenueTitle: "Invoiced revenue",
    cardRevenueCountLabel: "Count",
    cardRevenueAvgLabel: "Average",
    cardVatTitle: "Taxable / VAT / non-taxable split",
  },

  invoicePreview: {
    title: "Invoice № {{number}}",
    subtitle: "Issued on {{date}}",
    editCustomer: "Edit recipient",
    editCustomerSubtitle:
      "Recipient details only — totals, lines, number and dates stay unchanged.",
    editCustomerSuccess: "Recipient updated",
    editCustomerError: "Failed to update the recipient",
    recipient: "Recipient",
    supplier: "Supplier",
    titleOriginal: "ORIGINAL INVOICE",
    titleDuplicate: "DUPLICATE INVOICE",
    titleSimplified: "SIMPLIFIED INVOICE",
    docNumber: "Number",
    docDate: "Date",
    supplyDate: "Supply date",
    supplyPlace: "Supply place",
    colDescription: "Goods / service",
    colVatRate: "VAT %",
    colQty: "Qty",
    colUnitPrice: "Unit price",
    colTotal: "Total",
    totalWords: "Grand total in words",
    sumTotal: "Subtotal",
    sumTaxable: "Tax base",
    sumNonTaxable: "Non-taxable",
    sumVat: "VAT",
    sumGrand: "Grand total (EUR)",
    goodsReceived: "Goods / service received:",
    receivedAt: "Date",
    receivedBy: "By",
    paymentMethod: "Payment method",
    noteLabel: "Note",
    preparedBy: "Prepared by",
    print: "Print",
    printDuplicate: "Print duplicate",
    printPos: "Print (POS)",
    printPosConfirmTitle: "Print invoice",
    printPosConfirmDescription: "Print the invoice on the POS printer?",
    printPosSuccess: "Invoice sent to the POS printer",
    printPosError: "Error printing on the POS printer",
    cancel: "Cancel invoice",
    cancelConfirmTitle: "Cancellation confirmation",
    cancelConfirmContent:
      "Are you sure you want to cancel invoice № {{number}}?",
    cancelSuccess: "Invoice cancelled",
    cancelError: "Error cancelling invoice",
    cancelledBadge: "CANCELLED",
  },

  invoiceCreate: {
    title: "Issue invoice",
    subtitle: "Document over {{count}} selected bills",
    secDocument: "Document",
    secCustomer: "Customer",
    secReceiving: "Receipt",
    secItems: "Items",
    secTotals: "Totals",
    fieldNumber: "Number",
    numberHint: "auto +1 from last issued",
    fieldType: "Type",
    typeTax: "Tax invoice",
    typeSimplified: "Simplified invoice",
    fieldIssueDate: "Issue date",
    fieldSupplyDate: "Supply date",
    fieldSupplyPlace: "Supply place",
    fieldPaymentMethod: "Payment method",
    optional: "optional",
    fieldName: "Company name",
    fieldVat: "VAT №",
    fieldCity: "City",
    fieldAddress: "Address",
    fieldMol: "Accountable person",
    fieldPickCompany: "Pick a company",
    fieldPickCompanyPh: "Search by name or EIK",
    fieldCustomerEik: "EIK №",
    phCustomerEik: "9 or 13 digits",
    phCustomerCity: "e.g. Sofia",
    phCustomerName: 'e.g. "Test Ltd"',
    phCustomerAddress: "Registered address",
    phCustomerMol: "Full name",
    noCustomers: "No saved customers yet",
    noCustomersForQuery: "No matches",
    noDefaultCompany: "No current company set",
    lookupTitle: "Fetch data from the Bulgarian Commercial Registry",
    lookupInvalidEik: "Enter a valid EIK first",
    lookupSuccess: "Data filled from the Commercial Registry",
    lookupFailed: "Lookup failed. Try again or fill manually.",
    fieldReceivedBy: "Received by",
    fieldReceivedByPh: "Full name",
    fieldReceivedAt: "Receipt date",
    colName: "Name",
    colUnit: "Unit",
    colVat: "VAT %",
    colQty: "Qty",
    colUnitPrice: "Unit price",
    colVatAmount: "VAT amount",
    colTotal: "Net amount",
    colGross: "Total incl. VAT",
    noLines: "No items.",
    totalSum: "Subtotal",
    totalTaxable: "Tax base",
    totalNonTaxable: "Non-taxable",
    totalVat: "VAT",
    totalGrand: "Grand total",
    totalWords: "Grand total in words",
    note: "Note",
    noteOneBill: "Bill: {{id}}",
    noteManyBills: "Bills: {{ids}}",
    actionEdit: "Edit",
    actionDelete: "Delete",
    actionSummarize: "Summarize",
    actionAdd: "New row",
    save: "Save",
    editLineTitle: "Edit line",
    editLineSubtitle:
      "Change name, unit, VAT, quantity and unit price. Total is recalculated.",
    newLineTitle: "New line",
    newLineSubtitle: "Add an item manually. Only the name is required.",
    addLine: "Add",
    summarizeTitle: "Summarize bill",
    summarizeSubtitle: "Choose how items will appear on the final invoice.",
    summarizePerItem: "By name and price",
    summarizePerItemDesc: "Each item on its own row — as it was ordered.",
    summarizeSingle: "All on a single row",
    summarizeSingleDesc:
      'A single "Consumption" row with quantity 1 and the bill total.',
    summarizeApply: "Apply",
    summarizedName: "Consumption",
    deleteConfirmTitle: "Delete row",
    deleteConfirmDesc: 'Are you sure you want to delete "{{name}}"?',
    deleteConfirmOk: "Delete",
    mismatchOneBill: "the bill",
    mismatchManyBills: "the bills",
    mismatchTitle: "Invoice total differs from {{context}} amount",
    mismatchBody: "Do you want to continue saving?",
    mismatchBillsTotal: "Bill(s) total",
    mismatchInvoiceTotal: "Invoice total",
    errInvoiceNumber: "Enter a valid invoice number",
    errDates: "Pick issue and supply dates",
    errPaymentMethod: "Pick a payment method",
    errCustomerEik: "Invalid customer EIK",
    errCustomerVat: "VAT number must be in BG + 9 or 10 digits format",
    errCustomerFields: "Fill in company name, city and address",
    errNoLines: "Add at least one item",
    saveSuccess: "Invoice saved successfully",
    saveError: "Failed to save invoice",
  },

  reportType: {
    selectType: "Select report type",
    personal: "Personal report",
    establishment: "Establishment report",
  },

  operations: {
    selectType: "Select operation type",
    xReport: "X report (no reset)",
    zReport: "Z report (with reset)",
    // Fiscal-memory report for a period — a different document, not an X with dates.
    fmReport: "Reports by date",
    fmReportDetail: "Report type",
    fmReportShort: "Summary",
    fmReportFull: "Detailed",
    fmReportShortHint: "The period totals in one short report.",
    fmReportFullHint:
      "One block per Z report in the period — a long period is metres of paper.",
    fmReportPeriod: "Report period",
    fmPresetLastMonth: "Last month",
    fmPresetThisMonth: "This month",
    fmPresetPeriod: "Period",
    fmPresetLastMonthHint:
      "From {{from}} to {{to}}. The monthly report is due by the {{deadlineDay}}th of the following month.",
    fmPresetThisMonthHint: "From {{from}} until today ({{to}}).",
    fmPeriodFrom: "From",
    fmPeriodTo: "To",
    fmPeriodLongSpanHint:
      "No limit. A detailed report over {{days}} days asks for confirmation before printing.",
    // Long detailed report — a question to the operator, not a ban.
    fmLongPeriodTitle: "Long detailed report",
    fmLongPeriodMessage:
      "The selected period is {{days}} days. The detailed report prints one block per Z report in it — that is metres of paper and several minutes of printing.",
    fmLongPeriodQuestion:
      "Print it anyway? If you only need the period totals, choose “Summary”.",
    fmLongPeriodConfirm: "Print",
    fmReportPeriodRequired: "Select a period",
    fmReportInvalidPeriod: "The end date is before the start date",
    fmReportFuturePeriod: "The period is in the future",
    fmReportSuccess: "The period report has been printed",
    xReportSuccess: "The X report has been printed",
    zReportSuccess: "The Z report has been printed",
    cashIn: "Cash In",
    cashOut: "Cash Out",
    enterAmount: "Enter amount",
    amountRequired: "Please enter an amount greater than 0",
    cashInSuccess: "Cash in completed successfully",
    cashOutSuccess: "Cash out completed successfully",
    error: "Error executing operation",
  },

  generateReport: {
    title: "Generate report",
    establishmentTitle: "Generate establishment report",
    selectUser: "Select user:",
    selectUserWarning: "Please select a user",
    reportDate: "Report date:",
    selectDate: "Select date",
    previewReport: "Preview report",
    // In warehouse mode the sellable is a recipe → same checkbox, different name.
    showSoldAssortments: "Show sold assortments",
    showSoldRecipes: "Show sold recipes",
    loadUsersError: "Error loading users",
    previewError: "Error previewing report",
    establishmentPreviewError: "Error previewing establishment report",
    reportCreated: "Report created successfully",
    establishmentReportCreated: "Establishment report created successfully",
    generateError: "Error generating report",
    establishmentGenerateError: "Error generating establishment report",
    resetReport: "Reset report",
    resetFromReport: "Reset from report?",
    confirmAgain: "Confirm once more?",
    resetButton: "Reset",
    noUsersAvailable: "No users with unreported accounts",
    resetConfirmDescription:
      "This operation will reset all unreported bills and generate a new report. This action cannot be undone.",
    resetConfirmEstablishmentDescription:
      "This operation will generate an establishment report from all personal reports for the selected date. This action cannot be undone.",
    resetConfirmEstablishmentWithPersonalDescription:
      "This operation will generate all personal reports and an establishment report for the selected date. This action cannot be undone.",
    unreportedUsersTitle: "There are unfinished reports for these waiters:",
    unreportedUsersTitleSingle: "There is an unfinished report for this waiter:",
    zReportConfirmTitle: "Reset daily financial report",
    zReportConfirmDescription:
      "Reset the daily financial report (Z-report)? This operation sends a Z-report to the fiscal device.",
    printPosConfirmTitle: "Print report",
    printPosConfirmDescription: "Print report on POS printer?",
  },

  personalReport: {
    title: "Personal report № {{number}} / {{date}}",
    testRestaurant: "Test Restaurant",
    turnoverByPayment: "Turnover by payment types",
    paymentType: "Payment type",
    bills: "Bills",
    alone: "Alone",
    inBrigade: "In brigade",
    compliment: "Compliment",
    deposit: "Deposit",
    cashPayment: "Cash",
    cardPayment: "Card",
    cancelledByStation: "Cancelled items by station",
    bar: "Bar",
    qty: "Qty.",
    firstLastOrder: "First / last order",
    cashDescription: "Cash inventory",
    coins: "cents",
    firstLastBill: "First / last bill",
    amountToReport: "Amount to report:",
    accepted: "Accepted:",
    created: "Created:",
    dateLabel: "Date:",
    printPos: "Print (POS)",
    printPosSuccess: "Report sent to the POS printer",
    printPosError: "Failed to print on the POS printer",
  },

  establishmentReport: {
    title: "Establishment report № {{number}} / {{date}}",
    includedPersonalReports: "Included personal reports: {{count}}",
    turnoverByPayment: "Turnover by payment types",
    paymentType: "Payment type",
    bills: "Bills",
    amount: "Amount",
    cashPayment: "Cash",
    cardPayment: "Card",
    otherPayment: "Other",
    firstLastOrder: "First / last order",
    firstLastBill: "First / last bill",
    stationBreakdown: "Breakdown by stations",
    station: "Station",
    totalBills: "Total bills",
    totalAmount: "Total amount",
    userBreakdown: "Breakdown by users",
    user: "User",
    reports: "Reports",
    cancelledByStation: "Cancelled products by station",
    totalForStation: "Total for {{name}}",
    // In warehouse mode the sellable is a recipe → same section, different name.
    soldAssortments: "Sold assortments",
    soldRecipes: "Sold recipes",
    assortment: "Assortment",
    recipe: "Recipe",
    printPos: "Print (POS)",
    printPosSuccess: "Report sent to the POS printer",
    printPosError: "Failed to print on the POS printer",
    resetWillFinalizePersonalReports:
      "Resetting the <b>Establishment report</b> will finalize all <b>Personal reports</b> for the day",
  },

  reports: {
    billsHistory: "Bills report",
    splitBills: "Split bills",
    cancellations: "Cancellations",
    personalReports: "Personal reports",
    establishmentReports: "Establishment reports",
  },

  billsHistory: {
    title: "Bills history",
    table: "Table",
    statusCol: "Status",
    open: "Open",
    closed: "Closed",
    openedAt: "Opened",
    closedAt: "Closed",
    duration: "Duration",
    hours: "h",
    minutes: "min",
    orders: "Orders",
    cancelledItems: "Cancelled",
    cancelledLabel: "Cancelled: {{count}}",
    payment: "Payment",
    cashLabel: "Cash: {{amount}}",
    cardLabel: "Card: {{amount}}",
    tables: "Tables",
    totalAmount: "Total amount",
    statusFilter: "Status",
    openFilter: "Open",
    closedFilter: "Closed",
    waiterFilter: "Waiter",
    paymentMethodFilter: "Payment method",
    fromDate: "From date",
    toDate: "To date",
    noData: "No bills data",
  },

  billsSplits: {
    title: "Split bills",
    dateCol: "Date",
    fromTable: "From table",
    toTable: "To table",
    products: "Products",
    totalSplits: "Total splits",
    transferredProducts: "Transferred products",
    fromTableFilter: "From table (ID)",
    toTableFilter: "To table (ID)",
    noData: "No split bills data",
  },

  cancellationsReport: {
    title: "Cancelled products report",
    bill: "Bill",
    product: "Product",
    qtyCol: "Qty",
    unitPrice: "Unit price",
    reason: "Reason",
    statusCol: "Status",
    defective: "✓ Defective",
    cancelled: "Cancelled",
    totalCancelled: "Total cancelled",
    defectiveItems: "Defective items",
    billIdFilter: "Bill ID",
    selectUsers: "Select users",
    defectiveOnly: "Defective products only",
    noData: "No cancelled products",
  },

  personalReportsPage: {
    title: "Personal reports",
    allUsers: "All users",
    userCol: "User",
    billsCol: "Bills",
    cashCol: "Cash",
    cardCol: "Card",
    totalCol: "Total amount",
    noData: "No reports found",
  },

  establishmentReportsPage: {
    title: "Establishment reports",
    personalReportsCol: "Personal reports",
    billsCol: "Bills",
    cashCol: "Cash",
    cardCol: "Card",
    totalCol: "Total amount",
    noData: "No reports found",
  },

  billDetails: {
    tableTitle: "Table {{name}}",
    openedAt: "Opened:",
    closedAt: "Closed:",
    noOrders: "No orders",
    fromTable: "from table {{name}}",
    split: "Split",
    commentLabel: "Comment:",
    totalOrders: "Total orders: {{orders}} | Products: {{items}}",
    cancelledCount: "Cancelled: {{count}}",
  },

  configuration: {
    title: "Configuration",
    monitors: "Monitors",
    devices: "Devices",
  },

  // The shared side menu of modules with per-permission pages (Configuration, Reservations)
  moduleSidebar: {
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    collapse: "Collapse",
    backToModules: "Back to modules",
  },

  configSidebar: {
    monitors: "Monitors",
    devices: "Devices",
    ordering: "Ordering",
    paymentMethods: "Payment methods",
    companies: "Companies",
    workingHours: "Working hours",
    configurationLabel: "Configuration module",
    sectionMain: "Settings",
  },

  workingHours: {
    sectionKicker: "Week",
    sectionTitle: "Working hours by day",
    days: {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
    open: "Open",
    closed: "Day off",
    closedDay: "Day off",
    opensAt: "Opens",
    closesAt: "Closes",
    hintClosed: "Day off — no reservations",
    hintAfterMidnight: "Closes at {{time}} the next day",
    errors: {
      sameTime: "Opening and closing cannot be at the same time",
      overlapsNextDay: "Closing after midnight must be before the next day opens",
    },
    save: "Save",
    discard: "Discard changes",
    saveSuccess: "Working hours saved",
    saveError: "Failed to save working hours",
    guide: {
      link: "How does it work?",
      text: "Working hours set the default hours in reports and the hours in which reservations are accepted.",
      modalTitle: "How working hours work",
      modalSubtitle: "Examples with three days of one week",
      tab: {
        reports: "Reports",
        reservations: "Reservations",
      },
      lead: {
        reports: "The report for one day runs from the opening to the closing of that day.",
        reservations: "A reservation can start from opening until 15 minutes before closing.",
      },
      columnDay: "Day",
      columnHours: "Working hours",
      columnResult: {
        reports: "Report period",
        reservations: "Reservation time",
      },
      afterMidnight: "closes after midnight",
      result: {
        reports: {
          tuesday: "Tue 10:00 → Tue 22:00",
          friday: "Fri 12:00 → Sat 02:00",
          monday: "Mon 00:00 → Mon 23:59",
        },
        reservations: {
          tuesday: "from 10:00 to 21:45",
          friday: "from 12:00 to 01:45",
          monday: "Not accepted",
        },
      },
      goodToKnow: "Good to know",
      notes: {
        reports: {
          afterMidnight: {
            title: "A bill after midnight",
            text: "Friday is open until 02:00. A bill paid on Saturday at 01:30 goes into the Friday report, not Saturday.",
          },
          closedAfterMidnight: {
            title: "A day off after a day that works late",
            text: "Sunday is open until 03:00 and Monday is a day off. The Monday report runs from 03:00 to 23:59 — bills until 03:00 stay in Sunday.",
          },
          notSet: {
            title: "No working hours saved",
            text: "Until working hours are saved, the day in reports runs from 06:00 to 05:59 the next day.",
          },
        },
        reservations: {
          lastSlot: {
            title: "Last time",
            text: "Wednesday is open 10:00–22:00. Reservation times are every 15 minutes and the last one is 21:45.",
          },
          afterMidnight: {
            title: "A day that works past midnight",
            text: "Friday is open until 02:00. When you pick Friday, you see all hours from 12 to 01 at once. If you pick a time after midnight (for example 01:15), the date at the top switches to Saturday by itself — the reservation is for Saturday at 01:15.",
          },
          closed: {
            title: "Day off",
            text: "Monday is a day off. The form shows “Day off — no reservations” and does not save.",
          },
          schedule: {
            title: "“Reservations table”",
            text: "The table for the day shows the hours from opening to closing. Wednesday 10:00–22:00 → from 10:00 to 22:00. Friday 12:00–02:00 → from 12:00 to 02:00 on one screen, without switching the day.",
          },
          notSet: {
            title: "No working hours saved",
            text: "Until working hours are saved, reservations are accepted from 11:00 to 22:45 every day.",
          },
        },
      },
      close: "Close",
    },
  },

  reservationsSidebar: {
    reservations: "Reservations",
    table: "Reservations table",
    restaurantSchema: "Floor plan",
    tablesConfig: "Table setup",
    moduleLabel: "Reservations module",
  },

  nonFiscalDisplay: {
    // Database optimization
    cleanupTitle: "Database optimization",
    cleanupDesc: "Permanently deletes cash-closed bills that have no fiscal receipt",
    cleanupRun: "Run",
    cleanupHint:
      "Deletes permanently, together with the history. The remaining bills are renumbered without gaps, and every till clears its own history (printer settings stay).",
    cleanupConfirmContent:
      "This operation permanently deletes every cash-closed bill without a fiscal receipt together with its history, renumbers the remaining bills without gaps, and clears each till's history — issued receipts, card payments and offline events. Printer settings stay. Reversing a receipt issued before the optimization will no longer be possible. Back up the database before continuing. Are you sure?",
    cleanupConfirmOk: "Yes, continue",
    cleanupConfirmCancel: "Cancel",
    cleanupNoBills: "No bills to delete",
    cleanupSuccess: "Deleted bills: {{count}}",
    cleanupError: "Error cleaning up the database",
    // Codes come from the BE (`detail`); the texts live only here.
    cleanupErrors: {
      cleanup_failed:
        "Cleanup did not run. Nothing was deleted — check the server log.",
      cleanup_requires_fiscal_restaurant:
        "Cleanup is available only for a restaurant with a fiscal printer.",
      cleanup_no_restaurant_in_token:
        "No restaurant identifier found — please sign in again.",
      cleanup_requires_access:
        "Only a manager with full access can run the cleanup.",
      cleanup_no_session: "No session token found — please sign in again.",
      cleanup_no_tills:
        "Not every till is reporting in. The optimization needs a connection to each one — nothing was deleted.",
      cleanup_till_unreachable:
        "A till refused or did not answer. It may be in local mode or holding an unfinished payment — check it and try again.",
      cleanup_renumber_failed:
        "The bills were deleted and the tills cleared, but the numbers were not rearranged. Run the optimization again.",
    },
    // Local mode: the operation goes through the cloud and through every till.
    cleanupOfflineHint:
      "This terminal is running in local mode. The optimization is only possible with a connection to the cloud.",
    cleanupResultTitle: "Optimization result",
    cleanupResultClose: "Close",
    cleanupDeletedBills: "Deleted bills",
    cleanupDeletedReports: "Deleted reports",
    cleanupRecalculated: "Recalculated reports",
    cleanupRemaining: "Remaining bills",
    cleanupNewMaxBillId: "Last bill number",
    cleanupMessage: "Message",
  },

  orderingSettings: {
    sectionTablesKicker: "Tables",
    sectionTablesTitle: "Numbering, order and visibility of tables",
    sectionOrdersKicker: "Orders",
    sectionOrdersTitle: "Behaviour after “Order”",
    sectionNotificationsKicker: "Notifications",
    sectionNotificationsTitle: "Messages in Ordering",
    sectionCatalogKicker: "Catalog",
    sectionCatalogTitle: "Product view and order",
    sectionMaintenanceKicker: "Maintenance",
    sectionMaintenanceTitle: "Database",
    sharedNumberingName: "Shared table numbering",
    sharedNumberingDesc: "Whether table numbers are unique for the whole restaurant",
    optionSharedNumberingOff: "Off",
    hintSharedNumberingOff:
      "Each waiter has their own numbering — two waiters can both have table “1” at the same time",
    optionSharedNumberingOn: "On",
    hintSharedNumberingOn:
      "Numbers are unique for the whole restaurant — an occupied table “1” errors for other waiters too",
    tableOrderName: "Order of tables in the list",
    tableOrderDesc: "How the open tables are sorted",
    optionTableOrderChronological: "Chronological",
    hintTableOrderChronological:
      "By opening time — the oldest table comes first, a new one goes to the bottom",
    optionTableOrderAlphanumeric: "By number and name",
    hintTableOrderAlphanumeric:
      "Numbered tables first, by number (1, 2, 10, 33), then named tables alphabetically",
    nonFiscalName: "Unfinalized tables",
    nonFiscalDesc: "When bills that are not yet fiscalized are shown",
    optionNonFiscalNever: "No",
    hintNonFiscalNever: "Bills disappear after closing",
    optionNonFiscalAlways: "Always",
    hintNonFiscalAlways: "Bills remain until fiscalized",
    optionNonFiscalOnDemand: "When needed",
    hintNonFiscalOnDemand: "Dynamic show/hide with ALT + F1 command",
    quickCloseName: "Close immediately after ordering",
    quickCloseDesc: "What happens to the table after “Order”",
    optionQuickCloseOff: "Off",
    hintQuickCloseOff:
      "New table asks for a number, and the order is saved without closing",
    optionQuickCloseOn: "On",
    hintQuickCloseOn:
      "New table auto-picks the smallest free number, and after “Order” the “Close” dialog opens",
    successNotificationsName: "Success messages",
    successNotificationsDesc: "The green “Success…” messages after every action",
    optionSuccessNotificationsOff: "Off",
    hintSuccessNotificationsOff:
      "The green “Success…” messages in Ordering are not shown — errors and warnings still are",
    optionSuccessNotificationsOn: "On",
    hintSuccessNotificationsOn:
      "A short message appears after every successful action (products added, customer saved, table closed…)",
    catalogFilterName: "Bar above the catalog",
    catalogFilterDesc:
      "Which filter sits in the bar above the products; the other goes to the right panel, above the order",
    optionFilterGroups: "Groups",
    hintFilterGroups:
      "Top: groups (Burgers, Salads, Grill…). Right, above the order: stations (Grill, Bar…)",
    optionFilterStations: "Stations",
    hintFilterStations:
      "Top: stations (Grill, Bar…). Right, above the order: groups (Burgers, Salads, Grill…)",
    illustrationGroups: "Groups",
    illustrationStations: "Stations",
    illustrationCatalog: "Catalog",
    illustrationOrder: "Order",
    colorByNameAssortments: "Coloring of assortments",
    colorByNameRecipes: "Coloring of recipes",
    colorByDesc: "Where the color of rows and cards comes from",
    optionColorGroup: "By group",
    hintColorGroupAssortments: "Each assortment carries its group's color",
    hintColorGroupRecipes: "Each recipe carries its group's color",
    optionColorStation: "By station",
    hintColorStationAssortments: "Each assortment carries its station's color",
    hintColorStationRecipes: "Each recipe carries its station's color",
    optionColorNone: "No coloring",
    hintColorNone: "Rows and cards have no colored background",
    searchFieldName: "Search box above the catalog",
    searchFieldDesc: "Shows or hides the search box on desktop; phones and tablets always have it",
    optionSearchFieldOff: "Hidden",
    hintSearchFieldOff:
      "You search straight from the keyboard — what you type pops up over the catalog and fades after a second",
    optionSearchFieldOn: "Shown",
    hintSearchFieldOn:
      "The field sits above the catalog and takes typing; − and + inside it still change the focused product's quantity",
    searchScopeName: "Search with a group open",
    searchScopeDesc:
      "Where the search looks when a group is open in the catalog (e.g. “Drinks”) — the lunch menu editor follows it too. With no group open it always searches the whole menu",
    optionSearchScopeMenu: "Whole menu",
    hintSearchScopeMenu:
      "E.g. in “Drinks” you type “lemon” → “Lemonade” and “Lemon chicken” (Grill) show up",
    optionSearchScopeGroup: "Only the group",
    hintSearchScopeGroup:
      "E.g. in “Drinks” you type “lemon” → only “Lemonade” shows up and you stay in “Drinks”",
    guestCountName: "Guest count",
    guestCountDesc: "Whether and when Ordering asks how many guests are at the table",
    optionGuestCountOff: "No",
    hintGuestCountOff: "The guest count is not entered",
    optionGuestCountNewTable: "On new table",
    hintGuestCountNewTable:
      "A “Guests” dialog before the “New table” dialog. Without a count the table is not opened",
    optionGuestCountClose: "On closing",
    hintGuestCountClose:
      "A “Guests” dialog before the “Close” dialog. Without a count the table is not closed",
    illustrationTableFlow: "Table flow",
    saveSuccess: "Setting saved successfully",
    saveError: "Error saving the setting",
  },

  monitorsConfig: {
    title: "Monitors",
    shpicker: "Expediter",
    shpickerHint: "This user is an expediter — the screen aggregates cooked items from all stations and issues them to waiters.",
    stepVisibility: "Visibility",
    visibilityCookedOnly: "Cooked only",
    visibilityCookedOnlyDesc: "Shows rows from \"Cooked\" onwards",
    visibilityAll: "All statuses",
    visibilityAllDesc: "Also shows not started / in progress with their statuses",
    stepColorBy: "Coloring",
    colorByShort: "Coloring",
    colorByTable: "By table",
    colorByTableDesc: "Rows of one table share a color",
    colorByWaiter: "By waiter",
    colorByWaiterDesc: "Rows of one waiter share a color",
    colorByNone: "None",
    colorByNoneDesc: "No color grouping",
    name: "Name",
    stations: "Stations",
    actions: "Actions",
    noMonitors: "No configured monitors",
    addMonitor: "Add monitor",
    editMonitor: "Edit monitor",
    deleteMonitor: "Delete monitor",
    deleteConfirm: "Are you sure?",
    assignUsers: "Users",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",
    configuredMonitors: "Configured monitors",
    monitors: "Monitors",
    deleteSuccess: "Monitor configuration deleted successfully",
    deleteError: "Error deleting configuration",
    deleteConfirmTitle: "Confirm deletion",
    deleteConfirmContent:
      'Are you sure you want to delete the monitor configuration for "{{name}}"?',
    editSuccess: "Monitor configuration updated successfully",
    editError: "Error editing configuration",
    editTitle: "Edit monitor",
    editConfirmTitle: "Confirm edit",
    editConfirmContent: "Are you sure you want to save the changes?",
    saveChanges: "Save changes",
    selectMonitorPlaceholder: "Select a monitor",
    noStations: "No available stations",
    monitorConfig: "Monitor configuration",
    selectUserAndStation: "Please select a user and at least one station",
    saveSuccess: "Monitor configuration saved successfully",
    saveError: "Error saving configuration",
    saveConfig: "Save configuration",
    selected: "Selected: {{count}}",
    aggregatedQuantities: "Aggregated quantities",
    // Redesign keys
    configKicker: "New monitor",
    configKickerShpicker: "New expediter",
    configTitle: "Link a user to stations",
    listKicker: "Monitors list",
    listKickerShpickers: "Expediters list",
    shpickers: "Expediters",
    addShpicker: "Add expediter",
    noShpickers: "No expediters configured",
    // Numbers are added dynamically in MonitorFormModal (expediter adds a step).
    stepUser: "User",
    stepScreen: "Screen",
    stepStations: "Stations",
    stepView: "View",
    stepOptions: "Options",
    screenSingle: "Single screen",
    screenSingleDesc: "All stations on one shared screen",
    screenSplit: "Split in 2",
    screenSplitDesc: "Left and right halves with their own stations",
    leftHalf: "Left half",
    rightHalf: "Right half",
    leftHalfShort: "L",
    rightHalfShort: "R",
    selectBothHalves: "With a split screen, pick stations for both halves",
    aggregatedShort: "Grouping",
    completeFullQuantity: "Complete full quantity",
    completeFullQuantityShort: "Full quantity",
    viewShort: "View",
    viewGrid: "Grid",
    viewList: "List",
    viewGridDesc: "Products are arranged in columns",
    viewListDesc: "Products are arranged in a single list",
  },

  devices: {
    name: "Name",
    uniqueNumber: "Unique number",
    type: "Type",
    actions: "Actions",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",
    users: "Access & Workpoints",
    connectedClients: "FiscalAPI",
    connectedClientsCount: "{{count}} connections",
    connectedClientsConflict:
      "Two installations print on this device — one receipt will come out twice.",
    connectedClientsUnknown:
      "No data. Older FiscalAPI versions do not announce themselves — update them to see which computer prints here.",
    connectedClientsStale: "not connected",
    fiscalapiDownload: "Download the latest FiscalAPI",
    fiscalapiUpdateButton: "Update to v{{version}}",
    fiscalapiUpdateStarted:
      "Update started — the installation will restart within minutes and the new version will show up here.",
    fiscalapiUpdateFailed: "Update did not start",
    deleteSuccess: "Device deleted successfully",
    deleteError: "Error deleting device",
    deleteConfirmTitle: "Confirm deletion",
    deleteConfirmContent: 'Are you sure you want to delete device "{{name}}"?',
    editSuccess: "Device updated successfully",
    editError: "Error editing device",
    editConfirmTitle: "Confirm edit",
    editConfirmContent: "Are you sure you want to save the changes?",
    saveChanges: "Save changes",
    pickerSearch: "Search by name",
    pickerWorkpointsCount_one: "{{count}} workpoint",
    pickerWorkpointsCount_other: "{{count}} workpoints",
    pickerUsersCount_one: "{{count}} user",
    pickerUsersCount_other: "{{count}} users",
    createSuccess: "Device created successfully",
    createError: "Error creating device",
    addDevice: "Add device",
    copySuccess: "Connection string copied",
    typeFiscal: "Fiscal printer",
    typePos: "POS printer",
    typePaymentTerminal: "Payment terminal",
    // Redesign
    // The three page sections — one per device type.
    posSectionKicker: "Order printing",
    fiscalSectionKicker: "Fiscalization",
    terminalSectionKicker: "Card payments",
    posDevices: "POS devices",
    fiscalDevices: "Fiscal printers",
    terminalDevices: "Payment terminals",
    addPosDevice: "Add POS device",
    addFiscalDevice: "Add fiscal printer",
    addTerminalDevice: "Add terminal",
    noPosDevices: "No configured POS devices",
    noFiscalDevices: "No configured fiscal printers",
    noTerminalDevices: "No configured payment terminals",
    deactivateWarning_one: "On save, access will be removed for:",
    deactivateWarning_other: "On save, {{summary}} will be removed:",
    deactivateUndo: "Undo",
    deactivateWarningAnd: " and ",
    orgUrlCopied: "Address copied",
    noStations: "No available stations",
    printLanguage: "Print language",
    printLanguagePlaceholder: "Bulgarian (default)",
    noLanguages: "No available languages",
    summarizeReceipt: "Summarized receipts",
    summarizeReceiptHint:
      "Identical products on the bill are printed on the fiscal receipt as one line with the total quantity.",
    summarizeReceiptOff: "Off",
    summarizeReceiptOn: "On",
    summarizeReceiptExampleWater: "Mineral water",
    summarizeReceiptExampleCola: "Cola",
    summarizeReceiptExampleCoffee: "Coffee",
    summarizeReceiptExampleCoffeeMilk: "Coffee + milk",
    summarizeReceiptActive: "Active",
    summarizeReceiptLines_one: "{{count}} line",
    summarizeReceiptLines_other: "{{count}} lines",
    nonFiscalCopy: "Non-fiscal copy on POS",
    nonFiscalCopyHint:
      "After every successful fiscal receipt, the POS printer of the same till prints a non-fiscal copy — handy for deliveries, to check that everything is packed. No POS printer, no copy.",
    paymentMethods: "Payment methods",
    stations: "Stations",
    stationsCount_one: "{{count}} station",
    stationsCount_other: "{{count}} stations",
    usersCount_one: "{{count}} user",
    usersCount_other: "{{count}} users",
    searchPlaceholder: "Search by name, GUID or user",
    noPaymentMethodsForDevice: "—",
    // The payment terminal modal (create and edit) — TerminalModal.
    terminalModal: {
      kickerEdit: "Edit device · Payment terminal",
      kickerCreate: "New device · Payment terminal",
      accountsTitle: "Card payments",
      accountsDesc:
        "Workpoints and users attached to this terminal. An account can be attached to one terminal only.",
      accountsDescShort: "Accounts attached to this terminal.",
      takenNote_one:
        "{{count}} account is already attached to {{where}} and can't be selected here. To move it, first remove it from the other terminal.",
      takenNote_other:
        "{{count}} accounts are already attached to {{where}} and can't be selected here. To move them, first remove them from the other terminal.",
      takenWhereMany: "other terminals",
      summaryAccounts: "Card payments:",
      summaryCount_one: "{{count}} account",
      summaryCount_other: "{{count}} accounts",
    },
    // The fiscal printer modal (create and edit) — FiscalPrinterModal.
    fiscalModal: {
      kickerEdit: "Edit device · Fiscal printer",
      kickerCreate: "New device · Fiscal printer",
      accountsTitle: "Receipts",
      accountsDesc:
        "Fiscal receipts of the selected workpoints and users are issued by this device. An account fiscalizes on one device only.",
      accountsDescShort: "Receipts of the selected accounts are issued here.",
      inactiveBannerTitle: "The device is off.",
      inactiveBannerText:
        "On save all attached workpoints and users are removed and become free for other fiscal devices.",
      takenNote_one:
        "{{count}} account already fiscalizes on {{where}} and can't be selected here. To move it, first remove it from the other device.",
      takenNote_other:
        "{{count}} accounts already fiscalize on {{where}} and can't be selected here. To move them, first remove them from the other device.",
      takenWhereMany: "other fiscal devices",
      methodsTitle: "Payment methods",
      methodsDesc:
        "Pick a device slot or “БЕЗ ФИСКАЛИЗИРАНЕ” (no fiscalization) for each method. “Not used” = the method isn't used on this device.",
      methodsDescShort: "A device slot or “БЕЗ ФИСКАЛИЗИРАНЕ” (no fiscalization) for each method.",
      methodLabel: "Method",
      slotLabel: "Device slot",
      fiscalizedPill: "Fiscalized: {{count}}",
      notUsed: "Not used",
      hintSlot: "Fiscal · {{slot}}",
      hintNoFiscal: "No fiscal receipt",
      hintNotUsed: "Not used here",
      receiptTitle: "Fiscal receipt",
      receiptDescShort: "Merged lines and a non-fiscal POS copy.",
      tabAccounts: "Accounts",
      tabMethods: "Payment",
      tabReceipt: "Receipt",
      tabOff: "off",
      summaryReceipts: "Receipts:",
      summaryMethods: "Methods:",
      summaryOff: "off",
      summaryAccounts_one: "{{count}} account",
      summaryAccounts_other: "{{count}} accounts",
      summaryFiscalized: "{{count}} fiscalized",
    },
    // The POS printer modal (create and edit) — PosPrinterModal.
    posModal: {
      kickerEdit: "Edit device · POS printer",
      kickerCreate: "New device · POS printer",
      namePlaceholder: "Name, e.g. Bar",
      editName: "Edit name",
      nameAria: "Printer name",
      close: "Close",
      cancel: "Cancel",
      docsTitle: "Till documents",
      docsDesc:
        "Documents of the selected workpoints and users come out on this printer. An account prints on one POS printer only.",
      docsDescShort: "Receipts of the selected accounts print here.",
      kitchenDescShort: "Orders from the selected stations print here as kitchen tickets.",
      tabDocuments: "Documents",
      tabKitchen: "Kitchen",
      tabOff: "off",
      active: "Active",
      inactive: "Inactive",
      inactiveBannerTitle: "Documents are off.",
      inactiveBannerText:
        "On save all attached workpoints and users are removed and become free for other POS printers. Kitchen tickets keep printing.",
      accountsLabel: "Workpoints and users",
      selectedPill_one: "{{count}} selected",
      selectedPill_other: "{{count}} selected",
      allPill: "All · {{count}}",
      allAccounts: "All accounts",
      allAccountsNote: "All <b>{{count}}</b> free workpoints and users print here.",
      searchPlaceholder: "Search workpoint or user",
      filterAll: "All",
      filterWorkpoints: "Workpoints",
      filterUsers: "Users",
      filterSelected: "Selected",
      workpoint: "Workpoint",
      user: "User",
      noAccounts: "No accounts found",
      shownOf: "Showing {{shown}} of {{total}}",
      selectShown: "Select shown",
      clear: "Clear",
      takenNote_one:
        "{{count}} account already prints documents on {{where}} and can't be selected here. To move it, first remove it from the other printer.",
      takenNote_other:
        "{{count}} accounts already print documents on {{where}} and can't be selected here. To move them, first remove them from the other printer.",
      takenWhereMany: "other POS printers",
      kitchenTitle: "Kitchen tickets",
      kitchenDesc:
        "Ordered products from the selected stations come out on this printer as kitchen tickets — no matter which account placed the order.",
      kitchenExampleTitle: "Ticket example",
      examplePrinterNamed: "Printer “{{name}}”",
      exampleThisPrinter: "This printer",
      exampleListens: "{{printer}} listens to <b>{{stations}}</b>.",
      exampleNoStations: "{{printer}} listens to no stations — it prints no kitchen tickets.",
      exampleSplit: "An order with products for both printers is split into two tickets — one on each.",
      exampleSingle: "An order with products from these stations prints here as a kitchen ticket — no matter which account placed it.",
      exampleListAnd: " and ",
      exampleListMore: "{{list}} and {{count}} more",
      ticketTitle: "KITCHEN TICKET",
      ticketMeta: "Table 12 · Maria · 12:41",
      ticketItem1: "Mojito",
      ticketMod1: "+ no sugar",
      ticketItem2: "Pork chop",
      ticketMod2: "+ well done",
      ticketEmpty: "No stations selected — no tickets are printed.",
      stationsLabel: "Stations",
      stationsSelected: "Selected: {{count}}",
      selectAll: "Select all",
      removeAll: "Remove all",
      stationsNote:
        "A station can print on several printers. Stations don't depend on the accounts or their status.",
      languageLabel: "Kitchen ticket language",
      languageNote:
        "Names of products, modifiers and stations on the ticket. Doesn't affect till documents.",
      summaryDocs: "Documents:",
      summaryKitchen: "Kitchen tickets:",
      summaryDocsOff: "off",
      summaryDocsAll: "all free accounts ({{count}})",
      summaryDocsCount_one: "{{count}} account",
      summaryDocsCount_other: "{{count}} accounts",
      summaryNoStations: "no stations",
    },
  },

  devicePaymentMethods: {
    noFiscalOption: "БЕЗ ФИСКАЛИЗИРАНЕ",
    duplicateValuesError:
      "Two different methods cannot share the same value on a device.",
    invalidValueError: "Values cannot be empty.",
    duplicateMethodsError: "A method can only appear once.",
    noPaymentMethods: "No payment methods available",
    viewButton: "View",
    viewTitle: "Device payment methods",
    viewEmpty: "No payment methods configured for this device.",
    colMethod: "Method",
    colSlot: "Value",
  },

  paymentMethodsConfig: {
    title: "Payment methods",
    listKicker: "Payment methods list",
    addMethod: "Add payment method",
    searchPlaceholder: "Search by name",
    colName: "Name",
    colSystem: "Type",
    colSortIndex: "Sort",
    colActions: "Actions",
    badgeSystem: "System",
    badgeCustom: "Custom",
    systemTooltip:
      "System payment methods cannot be deleted, but can be reordered",
    noMethods: "No payment methods yet",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",

    createTitle: "New payment method",
    createSubtitle: "Add a custom payment method",
    editTitle: "Edit payment method",

    fieldNameBg: "Name in Bulgarian",
    fieldNameBgPlaceholder: "Payment method",
    fieldNameEn: "Name in English",
    fieldNameEnPlaceholder: "Payment method",
    fieldSortIndex: "Sort",

    save: "Save",
    create: "Create",

    createSuccess: "Payment method created successfully",
    editSuccess: "Payment method updated successfully",
    deleteSuccess: "Payment method deleted successfully",
    createError: "Failed to create",
    editError: "Failed to update",
    deleteError: "Failed to delete",

    deleteConfirmTitle: "Confirm delete",
    deleteConfirmContent: 'Are you sure you want to delete "{{name}}"?',

    enterNameBgWarning: "Please enter a Bulgarian name",
    enterNameEnWarning: "Please enter an English name",
  },

  restaurantSchema: {
    title: "Floor plan",
    subtitle: "Visual layout of tables and zones by region",
    loadError: "The floor plan could not be loaded",
    loadErrorHint: "Check the connection and reload the page.",
    offlineHint:
      "The floor plan lives in the cloud — editing is possible once the internet is back.",
    empty: {
      title: "Draw your venue",
      description:
        "Start with a region — a hall, terrace or bar. Arrange tables and objects in it, then pick tables straight from the plan when ordering.",
      cta: "Create the first region",
    },
    counts: {
      tables_one: "{{count}} table",
      tables_other: "{{count}} tables",
      seats_one: "{{count}} seat",
      seats_other: "{{count}} seats",
      objects_one: "{{count}} object",
      objects_other: "{{count}} objects",
    },
    kinds: {
      table: "Table",
      bar: "Bar",
      wall: "Wall",
      plant: "Plant",
      entrance: "Entrance",
      stage: "Stage",
      dj: "DJ",
    },
    toolbar: {
      undo: "Undo (⌘Z)",
      redo: "Redo (⇧⌘Z)",
      saving: "Saving…",
      saved: "Saved",
      rotateDrag: "Drag to rotate (Shift — 15° steps)",
      duplicate: "Duplicate (⌘D)",
      delete: "Delete (Del)",
      duplicateShort: "Duplicate",
      deleteShort: "Delete",
    },
    canvas: {
      emptyTitle: "This region is empty",
      emptyHint: "Add a table or drag an object from the bar below.",
      zoomOut: "Zoom out",
      zoomIn: "Zoom in",
      fit: "Fit the plan",
      tableTitle: "Table {{name}} · {{seats}}",
    },
    addBar: {
      dragHint: "Drag onto the plan",
      hint: "Drag an object onto the plan",
    },
    inspector: {
      rotation: "Rotation",
      size: "Size",
      tableSubtitle: "{{shape}} · {{seats}}",
      objectSubtitle: "Size: {{size}}",
      seatsHint: "The top grows with the seats; the size makes it bigger or smaller.",
    },
    table: {
      title: "Table {{name}}",
      number: "Table number",
      numberPlaceholder: "e.g. 12",
      nameTrailingStar: "The table name cannot end with “*” (reserved for offline mode)",
      nameTaken: "A table with this number already exists",
      seats: "Seats",
      shape: "Shape",
      size: "Table size",
    },
    tableSizes: {
      small: "Small",
      medium: "Medium",
      large: "Large",
      xl: "XL",
    },
    tableShapes: {
      round: "Round",
      square: "Square",
      rectangle: "Rectangular",
    },
    tableCreate: {
      title: "New table",
      subtitle: "Number, seats, shape and size.",
      submit: "Create",
      addAnother: "Keep the window open for the next table",
      addAnotherHint: "After “Create” the number switches to the next free one so you can add another right away.",
      createdSoFar_one: "Created in this run: {{names}}",
      createdSoFar_other: "Created in this run ({{count}}): {{names}}",
      preview: "Preview",
      numberHint: "The next free number. It is also used in Ordering.",
      numberHintTyped: "The number is also used in Ordering.",
      placementHint: "Position and rotation are set directly on the plan.",
    },
    summary: {
      tables: "Tables",
      seats: "Seats",
      hint: "Pick a table on the plan or in the list to change its properties.",
    },
    list: {
      tables: "Tables",
      objects: "Objects",
      empty: "No tables in this region yet.",
    },
    regionTabs: {
      newRegion: "New region",
    },
    region: {
      name: "Region name",
      namePlaceholder: "e.g. Hall, Terrace",
    },
    regionCreate: {
      title: "New region",
      subtitle: "A zone of the venue with its own plan.",
      submit: "Create",
    },
    regionSettings: {
      title: "Edit region",
      button: "Edit region",
      subtitle: "Region name and deletion.",
      deleteTitle: "Delete region",
      deleteBlocked:
        "The region contains {{tables}} and {{objects}} — remove them first to delete it.",
      deleteEmpty: "The region is empty. We will ask for confirmation.",
      delete: "Delete…",
    },
    regionDelete: {
      title: "Delete “{{name}}”",
      description: "The region will be deleted.",
      confirm: "Delete",
    },
    sizes: {
      small: "Small",
      medium: "Medium",
      large: "Large",
      xl: "XL",
    },
    messages: {
      regionCreated: "Region created",
      tableCreated: "Table {{name}} created",
      regionSaved: "Region saved",
      regionDeleted: "Region deleted",
      saveError: "The change could not be saved",
      deleteError: "Delete failed",
      noRoom: "No room — table {{name}} is too close. Move the tables apart first.",
    },
  },
  tablesConfig: {
    title: "Table setup",
    subtitle: "Table numbers and seats by room — no floor plan needed",
    offlineHint:
      "Tables are stored in the cloud — changes are possible once the internet is back.",
    empty: {
      title: "Create a room first",
      description:
        "Tables live in rooms — a hall, terrace or bar. Create the first one and add tables with just a number and seats.",
      cta: "Create room",
    },
    listEmpty: "No tables in this room yet. Add the first one from “New tables”.",
    searchPlaceholder: "Search by number…",
    clearSearch: "Clear search",
    seatsLess: "One seat less",
    seatsMore: "One seat more",
    grid: {
      kicker: "Tables · {{region}}",
      selectAll: "Select all",
      filterAll: "All",
      selectTable: "Select table {{name}}",
      newTable: "New table",
      noMatches: "No tables match the search.",
    },
    newTables: {
      kicker: "New tables",
      title: "Add to “{{region}}”",
      single: "One table",
      series: "Number series",
      number: "Number",
      numbers: "Numbers from – to",
      from: "From",
      to: "To",
      seats: "Seats per table",
      add: "Add table",
      addMany_one: "Add {{count}} table",
      addMany_other: "Add {{count}} tables",
      seriesInvalid: "Enter numbers from – to, with “to” not smaller than “from”",
      seriesTooMany: "At most {{max}} tables at once",
      seriesNothingFree: "All numbers in this range are taken",
      seriesSkippedHint_one: "{{count}} taken number will be skipped",
      seriesSkippedHint_other: "{{count}} taken numbers will be skipped",
      placementHint:
        "The table is placed automatically in the room. Its position can be changed in “<link>Floor plan</link>”.",
    },
    edit: {
      title: "Table {{name}}",
      seats: "Seats",
      region: "Room",
      save: "Save",
      delete: "Delete table",
      close: "Close",
    },
    bulk: {
      title_one: "{{count}} table selected",
      title_other: "{{count}} tables selected",
      seats: "Seats for all",
      apply: "Apply",
      moveTo: "Move to room",
      chooseRegion: "Choose a room…",
      move: "Move",
      delete: "Delete selected",
      cancel: "Clear selection",
      edit: "Edit",
    },
    delete: {
      title: "Delete table {{name}}",
      description:
        "The table disappears from the floor plan and from reservations. A table with a reservation or an open bill cannot be deleted.",
      titleMany: "Delete {{count}} tables",
      descriptionMany:
        "Tables {{names}} disappear from the floor plan and from reservations. Tables with a reservation or an open bill are skipped.",
      confirm: "Delete",
    },
    messages: {
      created: "Table {{name}} added",
      seriesCreated_one: "{{count}} table added",
      seriesCreated_other: "{{count}} tables added",
      seriesSkipped: "Skipped taken numbers: {{names}}",
      seatsApplied_one: "Seats changed for {{count}} table",
      seatsApplied_other: "Seats changed for {{count}} tables",
      moved: "Table {{name}} moved to “{{region}}”",
      movedMany_one: "{{count}} table moved to “{{region}}”",
      movedMany_other: "{{count}} tables moved to “{{region}}”",
      deleted: "Table {{name}} deleted",
      deletedMany_one: "{{count}} table deleted",
      deletedMany_other: "{{count}} tables deleted",
      deleteSkippedInUse: "Not deleted (reservation or open bill): {{names}}",
      notFitted: "The table did not fit in the room — it can be arranged in “Floor plan”",
      saveError: "The change could not be saved",
      deleteError: "Deletion failed",
    },
  },
  companiesConfig: {
    title: "Companies",
    listKicker: "Companies list",
    addCompany: "Add company",
    searchPlaceholder: "Search by name, object or EIK",
    colName: "Name",
    colEik: "EIK",
    colVat: "VAT",
    colActions: "Actions",
    badgeDefault: "Current",
    noCompanies: "No companies yet",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",

    createTitle: "New company",
    createSubtitle: "Enter invoicing details",
    editTitle: "Edit company",
    editSubtitle: "Edit invoicing details",

    secBasic: "Basic details",
    secIdentity: "Identification and contacts",
    secStatus: "Status",
    secNumbering: "Document numbering",
    secBanks: "Bank accounts",

    fieldName: "Company name",
    fieldNamePlaceholder: 'e.g. "Test Ltd"',
    fieldObjectName: "Object / venue name",
    fieldObjectNamePlaceholder: 'e.g. "Test venue"',
    fieldAddress: "Address",
    fieldAddressPlaceholder: "Registered address",
    fieldMol: "Accountable person",
    fieldMolPlaceholder: "MOL",
    fieldEik: "EIK / BULSTAT",
    fieldEikHint: "9 or 13 digits",
    fieldEikValid: "Valid EIK — the checksum matches",
    fieldVatFromEik: "Fill in BG{{eik}}",
    fieldVat: "VAT number",
    fieldVatHint: "Format BG + 9 or 10 digits (optional)",
    fieldPhone: "Phone",
    fieldEmail: "E-mail",

    fieldDefault: "This is the current company",
    fieldDefaultDesc:
      "Documents will be issued with this company's data. Only one can be current.",

    fieldLastInvoice: "Last invoice number",
    fieldLastInvoicePlaceholder: "E.g. 0000000034",
    fieldLastInvoiceHint:
      "The last number ALREADY issued. 0 = no invoice issued yet.",
    fieldLastProforma: "Last proforma",
    fieldLastProformaHint: "Format: P-2026-0001",
    numberingNextKicker: "Next document",
    numberingNextInvoice: "Invoice No",
    numberingNextUnknown:
      "Enter the last issued number to see which number the next invoice starts from.",

    addBank: "Add bank",
    noBanks: "No bank accounts yet",
    bankIndex: "Bank #{{index}}",
    bankName: "Bank name",
    bankAddDraft: "New bank account",
    bankEditDraft: "Edit bank account",
    bankConfirm: "Save bank",
    bankMakeDefault: "Make default",
    draftDirtyBlock:
      "Save or cancel the current bank account before continuing.",

    save: "Save",
    create: "Create",

    createSuccess: "Company created successfully",
    editSuccess: "Company updated successfully",
    deleteSuccess: "Company deleted successfully",
    createError: "Failed to create",
    editError: "Failed to update",
    deleteError: "Failed to delete",

    deleteConfirmTitle: "Confirm delete",
    deleteConfirmContent: 'Are you sure you want to delete company "{{name}}"?',

    errRequired: "This field is required",
    errInvalidEik: "Invalid EIK / BULSTAT",
    errInvalidVat: "Expected format: BG + 9 or 10 digits",
    errInvalidEmail: "Invalid e-mail",
    errInvalidBic: "Invalid BIC",
    errInvalidIban: "Invalid IBAN",
    errInvalidInvoiceNumber: "Invalid number",
    errInvalidProforma: "Expected format: P-2026-0001",
  },

  monitors: {
    title: "Monitors",
    shpickerBadge: "Expediter",
    selectStation: "Select station",
    connected: "Connected",
    disconnected: "Disconnected",
    connecting: "Connecting...",
    noOrders: "No orders",
    unfulfilled: "Unfulfilled",
    fulfilled: "Fulfilled",
    noAssignedStations: "No assigned stations",
    contactAdmin: "Contact an administrator to be assigned stations",
    inProgress: "In progress",
    complete: "Complete",
    return: "Return",
    noActiveOrders: "No active orders",
    newOrdersWillAppear: "New orders will appear automatically",
    noConnection: "No connection",
    noConnectionHint: "This screen is offline — new orders are NOT arriving. Call a manager.",
    soundOn: "Sound on",
    soundOff: "Sound off",
    selected: "Selected: {{count}}",
    minuteShort: "m",
    hourShort: "h",
    pcs: "pcs",
    navUp: "Up one item",
    navDown: "Down one item",
    navUp2: "Up two items",
    navDown2: "Down two items",
    colStatus: "Status",
    colTable: "Table",
    colProduct: "Item",
    colElapsed: "Time",
    colQty: "Qty",
    colTotal: "Total",
    colOperator: "Operator",
    colOrderTime: "Ordered",
    additionToProduct: "(for {{product}})",
    additionToProductFromStation: "(for {{product}} from {{station}})",
    stationPartDone: "done ✓",
    stationPartInProgress: "in progress",
    stationPartWaiting: "waiting",
  },

  inventoryPage: {
    moduleLabel: "Inventory module",
    backToModules: "Back to modules",
    collapse: "Collapse",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    delivery: "Delivery",
    refresh: "Refresh",
    // Deliveries table
    newDelivery: "New delivery",
    colDate: "Date",
    colInvoice: "Invoice №",
    colSupplier: "Supplier",
    colWarehouse: "Warehouse",
    colProducts: "Products",
    colValue: "Value",
    colType: "Type",
    colQty: "Quantity",
    colUnitPrice: "Unit price",
    colBatch: "Batch",
    colExpiry: "Expiry",
    stxRestockLabel: "Restock",
    colProductName: "Product",
    colPackaging: "Packaging",
    colNet: "Net",
    lotSource: "Source",
    lotSource_delivery: "Delivery",
    lotSource_stocktake: "Stocktake",
    lotSource_transfer: "Transfer",
    lotSource_substitution: "Substitution",
    lotSource_writeoff: "Write-off",
    lotSource_scrap: "Scrap",
    lotSource_report: "Report posting",
    colGross: "Gross",
    colUnit: "Unit of measure",
    filterApply: "Apply",
    filterReset: "Reset",
    filterSearch: "Search in filters",
    filterSelectAll: "Select all",
    sectionConfig: "Settings",
    settings: "Settings",
    settingsVatSection: "VAT registration",
    settingsWhSection: "Default warehouse",
    settingsWhHint:
      "The warehouse the module uses by default (for reports and new operations).",
    settingsWhQuestion: "Default warehouse:",
    productWarehousesSection: "Warehouse per product",
    productWarehousesHint:
      "Products kept in a specific warehouse (for example lemons — in the kitchen). When posting a report and when scrapping on void they are written off from there, not from the warehouse of the recipe's station.",
    productWarehousesEmpty:
      "No products with their own warehouse — everything is written off from the warehouse of the recipe's station.",
    productWarehouseInvalid: "The warehouse no longer belongs to this restaurant",
    productWarehouseRemove: "Remove the warehouse of {{product}}",
    productWarehouseAddQuestion: "Add a product:",
    productWarehouseProductPh: "Select a product",
    productWarehouseAdd: "Add",
    productWarehouseSaved: "The product's warehouse is saved",
    productWarehouseRemoved: "The product's warehouse is removed",
    settingsVatQuestion: "Is the company VAT-registered?",
    settingsVatHint:
      "Determines how gross and net sums are calculated in the warehouse module.",
    settingsVatYes: "Yes, registered",
    settingsVatNo: "No",
    settingsSaved: "Settings saved",
    errScrapReason: "Select a reason for the scrap",
    fieldScrapReasonPh: "Select a reason",
    kpiDeliveries: "Deliveries",
    kpiValue: "Total value",
    kpiProducts: "Products",
    products: "Products",
    noWarehouse: "Select a warehouse to view deliveries.",
    noDeliveries: "No deliveries recorded for this warehouse yet.",
    noLines: "No products in this delivery.",
    // New delivery modal
    fieldDate: "Delivery date",
    fieldInvoice: "Invoice №",
    fieldInvoicePh: "Enter number",
    fieldSupplier: "Supplier",
    fieldSupplierPh: "Select supplier",
    productAdd: "New product",
    productAddTitle: "New stock product",
    productAddHint: "The product is saved in the organization's shared list.",
    productName: "Name",
    productNamePh: "Product name",
    productNameRequired: "Name is required",
    productExportCode: "Export code",
    productGroup: "Group",
    productGroupPh: "Select group",
    productUnit: "Unit of measure",
    productUnitPh: "Select unit",
    productUnitRequired: "Unit of measure is required",
    productVat: "VAT",
    productVatPh: "Select VAT",
    productVatRequired: "VAT is required",
    productMinQuantity: "Minimum quantity",
    productWastePct: "Waste",
    productPackagings: "Packagings",
    productPackagingsPh: "Select packagings",
    productPackagingsHint:
      "The packagings the product is delivered in (e.g. crate, pack).",
    productTrackBatch: "Track batch",
    productTrackExpiry: "Track expiry date",
    productSaved: "Product saved",
    supplierAdd: "New supplier",
    supplierAddTitle: "New supplier",
    supplierAddHint: "The supplier is saved in the organization's shared list.",
    supplierName: "Name",
    supplierNamePh: "Supplier name",
    supplierNameRequired: "Name is required",
    supplierEik: "Company ID",
    supplierEikPh: "e.g. 123456786",
    supplierEikInvalid: "Invalid company ID (9 or 13 digits with check digit)",
    supplierVatNumber: "VAT number",
    supplierVatNumberPh: "e.g. BG123456786",
    supplierVatNumberInvalid: "Invalid VAT number (BG + 9 or 10 digits)",
    supplierVatNumberHint:
      "Without a VAT number the delivery charges no VAT — gross stays equal to net.",
    supplierSaved: "Supplier saved",
    fieldTemplate: "From past delivery",
    fieldTemplatePh: "Select a delivery as template",
    templateOptionProducts: "{{count}} items",
    // Delivery worksheet page
    backToDeliveries: "Back to deliveries",
    wsSaveChanges: "Save changes",
    wsSearchPh: "Add a product — just start typing, e.g. “tomatoes”",
    wsClearRowHint: "Remove row from delivery",
    wsNoMatch: "No match",
    wsLastPrice: "last price",
    wsSoldWithoutStock: "−{{qty}} sold without stock",
    wsSoldWithoutStockHint: "More was sold than was in stock. A delivery dated after the sales comes in whole (the minus is written off); a delivery dated on the day of the sales covers it.",
    wsPricesFromSupplier: "Prices & last delivery from: {{name}}",
    wsResultsCount_one: "{{count}} result",
    wsResultsCount_other: "{{count}} results",
    wsAddedTag: "Added",
    wsNoLastDelivery: "no previous delivery",
    wsNoSupplierDelivery: "no delivery from this supplier",
    wsEmptyTitle: "No products added",
    wsEmptyHint:
      "Find a product with the search above and press Enter — the row is added here and focus jumps straight to “Quantity”.",
    wsEmptyKbdType: "type anywhere",
    wsEmptyKbdEnter: "adds the product",
    wsEmptyKbdEsc: "back to search",
    wsUnitTotals: "Total quantities",
    // Write-off worksheet pages (create/edit)
    backToWriteoffs: "Back to write-offs",
    woSearchPh: "Add a product or recipe — just start typing, e.g. “tomatoes”",
    woEmptyTitle: "No rows added",
    unitPortions: "portions",
    woEmptyHint:
      "Find a product or recipe with the search above and press Enter — the row is added here and focus jumps straight to “Quantity”.",
    draftRestoreTitle: "Unfinished delivery",
    draftRestoreTitleBy: "Unfinished delivery by {{name}}",
    draftRestoreSavedAt: "Saved at",
    draftRestoreTotal: "Total",
    draftRestoreQuestion: "Do you want to continue where you left off?",
    draftRestoreContinue: "Continue",
    draftRestoreStartOver: "Start over",
    fieldWarehouse: "Warehouse",
    fieldWarehousePh: "Select warehouse",
    fieldProduct: "Product",
    fieldProductPh: "Select product",
    productOptStock: "Stock",
    productOptLastDelivery: "Last delivery",
    fieldUnitPrice: "Unit price",
    fieldNote: "Note",
    fieldNotePh: "Optional note for the delivery",
    addRow: "Add row",
    total: "Total",
    // Base + VAT in the "Total" box (only for VAT-registered companies)
    totalBase: "Base",
    totalVat: "VAT",
    colComment: "Comment",
    colUser: "User",
    cancel: "Cancel",
    submit: "Record",
    created: "Delivery recorded",
    errWarehouse: "Select a warehouse",
    errNoWarehouses:
      "No warehouse exists. Create one in the organization before recording movements.",
    errDate: "Select a date",
    errNoLines: "Add at least one valid row",
    errCreate: "Failed to record delivery",
    // Write-offs
    writeoff: "Write-off",
    newWriteoff: "New write-off",
    noWriteoffs: "No write-offs for this warehouse yet.",
    kpiWriteoffs: "Write-offs",
    colReason: "Reason",
    colNote: "Note",
    colAvailable: "Available",
    fieldReason: "Reason",
    fieldReasonPh: "Free text",
    fieldComment: "Comment",
    fieldCommentPh: "Optional comment",
    recipe: "Recipe",
    selectRecipe: "Select recipe",
    splitToProducts: "Split into products",
    revertToRecipe: "Back to recipe",
    confirmContinue: "Continue",
    editDeliveryTitle: "Edit delivery",
    editWriteoffTitle: "Edit write-off",
    editSubmitWriteoff: "Save write-off",
    edit: "Edit",
    delete: "Void",
    confirmDeleteTitle: "Void this record?",
    confirmDeleteBody:
      "The record will be voided and stock recomputed. This cannot be undone.",
    deleted: "Record voided",
    errDelete: "Void error",
    errLoad: "Load error",
    confirmLeaveTitle: "Unsaved changes",
    confirmLeaveBody: "You have unsaved changes. Leave without saving?",
    confirmLeave: "Leave",
    confirmDeleteRowTitle: "Remove this row?",
    confirmDeleteRowBody: "This row will be removed.",
    confirmSplitTitle: "Split the recipe?",
    confirmSplitBody: "The row will be replaced with the recipe's products.",
    confirmRevertTitle: "Back to recipe?",
    confirmRevertBody:
      "The split products will be collapsed back into one recipe row.",
    notePh: "Note",
    portionsPh: "Portions",
    recipeGroupLabel: "Recipe",
    recipeEmpty: "Recipe has no ingredients",
    fifo: "FIFO (first in)",
    fieldWriteoffDate: "Write-off date",
    batchShort: "Batch",
    noBatch: "No batch",
    remainShort: "Left",
    expiryShort: "Exp.",
    noExpiry: "no expiry",
    writeoffSubmit: "Write off",
    writeoffCreated: "Write-off recorded",
    errWriteoff: "Failed to record write-off",
    errStock: "Cannot write off more than available: {{products}}",
    // Scrapping — 1:1 with write-off, different type + scrap reason
    scrap: "Scrapping",
    scrapReasonExpired: "Expired",
    scrapReasonSpoiled: "Spoiled",
    scrapReasonBroken: "Broken/spilled",
    scrapReasonMismatch: "Non-conformity",
    scrapReasonVoidMarking: "Scrap from Ordering",
    docScrapTitle: "Scrapping document",
    docScrapSubtitle: "Scrapping #{{id}}",
    docScrapHeading: "Stock scrapping",
    // Inter-warehouse transfer
    // Transfers — list and worksheet (0.0.155)
    backToTransfers: "Back to transfers",
    trxNotePh: "Optional note",
    trxDirection: "Direction",
    trxRemains: "Remains in {{warehouse}}",
    trxBecomes: "Becomes in {{warehouse}}",
    trxLastPrice: "last price {{price}}",
    trxNoPrice: "no delivery price",
    transfer: "Transfer",
    newTransfer: "New transfer",
    editTransferTitle: "Edit transfer",
    transferSubmit: "Transfer",
    editSubmitTransfer: "Save transfer",
    transferCreated: "Transfer recorded",
    errTransfer: "Failed to record transfer",
    noTransfers: "No transfers for this warehouse yet.",
    kpiTransfers: "Transfers",
    colFromWarehouse: "From warehouse",
    colToWarehouse: "To warehouse",
    fieldFromWarehouse: "From warehouse",
    fieldFromWarehousePh: "Source warehouse",
    fieldToWarehouse: "To warehouse",
    fieldToWarehousePh: "Destination warehouse",
    errFromWarehouse: "Select a source warehouse",
    errToWarehouse: "Select a destination warehouse",
    errSameWarehouse: "Source and destination must be different warehouses",
    docTransferTitle: "Transfer document",
    docTransferSubtitle: "Transfer #{{id}}",
    docTransferHeading: "Inter-warehouse transfer",
    errRecipe: "Failed to load recipe",
    // Split across warehouses + document
    // Hints for what's missing before "Receive" is enabled
    issueQty: "Enter a quantity for: {{products}}",
    issuePrice: "Enter a unit price for: {{products}}",
    issueWarehouse: "Select the row's warehouse for: {{products}}",
    issueBatch: "Enter a batch for “{{product}}” (required)",
    issueExpiry: "Enter an expiry date for “{{product}}” (required)",
    issueBatchFor: "Enter a batch (required) for: {{products}}",
    issueExpiryFor: "Enter an expiry date (required) for: {{products}}",
    issueEmpty: "Add at least one product with quantity, price and warehouse",
    // Stocktake
    stocktake: "Stocktake",
    sectionOperations: "Operations",
    sectionReports: "Reports",
    stocktakeSubmit: "Confirm stocktake",
    newStocktake: "New stocktake",
    editStocktakeTitle: "Edit stocktake",
    editSubmitStocktake: "Save changes",
    stocktakeCreated: "Stocktake recorded",
    errStocktake: "Stocktake error",
    errNoCounts: "Enter at least one counted product",
    colSystem: "Expected",
    colCounted: "Found",
    colDiff: "Difference",
    colStatus: "Status",
    statusEqual: "equal",
    statusSurplus: "surplus",
    statusShortage: "shortage",
    statusSubstitute: "substitution",
    kpiSurplus: "Surplus",
    kpiShortage: "Shortage",
    hideZeroStock: "Hide zero stock",
    stockAsOfField: "As of date",
    stockAsOfNow: "As of today",
    stockAsOfNote: "Quantities as of the end of {{date}} · values at current prices",
    stockGrossCurrentPrices: "Gross at current prices",
    showZeroStock: "Show all",
    stocktakeReasonPh: "Note / reason (optional)",
    noProducts: "No products in this warehouse",
    noStocktakes: "No stocktakes for the period",
    colSurplus: "Surplus",
    colShortage: "Shortage",
    colNetValue: "Net value",
    // "Stock on hand" report
    stockReport: "Stock on hand",
    searchStockPh: "Search by product, category, shrinkage…",
    colCategory: "Category",
    colGroup: "Group",
    stockLotsSubtitle: "Movement history",
    stockLotsEmpty: "No movements for this product.",
    colMinQty: "Min. stock",
    colWaste: "Shrinkage",
    colAvgPrice: "Avg price (gross)",
    colLastPrice: "Last delivery price",
    colLastPriceShort: "Last delivery price",
    underMin: "Below minimum",
    nearMin: "Up to 20% above minimum",
    overMin: "More than 20% above minimum",
    kpiStocktakes: "Stocktakes",
    // Stocktake — list and worksheet (0.0.152)
    backToStocktakes: "Back to stocktakes",
    groupByWarehouse: "Group by warehouse",
    groupStocktakesCount_one: "{{count}} stocktake",
    groupStocktakesCount_other: "{{count}} stocktakes",
    fieldStocktakeDate: "Date",
    colUnitShort: "Unit",
    fieldStocktakeReason: "Note / reason (optional)",
    stxSearchPh: "Search a product to count…",
    stxCountedTag: "counted",
    stxDraftTitle: "Unfinished stocktake",
    stxDraftTitleBy: "Unfinished stocktake by {{name}}",
    // Substitutions (the panel next to the table)
    subPanelTitle: "Substitutions",
    subPanelPairs_one: "{{count}} pair",
    subPanelPairs_other: "{{count}} pairs",
    subCoefficient: "Coef.",
    subApply: "Substitute",
    subRevert: "Undo",
    subChipHint: "Part of a substitution",
    subNoSurplusLeft:
      "This product's surplus is already used by another substitution",
    // Interchangeable products
    interchange: "Interchangeable products",
    newInterchange: "New rule",
    newInterchangeTitle: "Create interchangeable products",
    editInterchangeTitle: "Edit rule",
    backToInterchangeables: "Back to interchangeables",
    interchangeSubmit: "Save rule",
    interchangeGroupProducts: "Products in the group",
    interchangeProductsSubtitle: "All listed products can replace one another",
    interchangeAddProductPh: "Add a product to the group…",
    interchangePairExample:
      "The numbers are relative to the first product; any other pair follows from them — e.g.",
    interchangeHowApplies: "How it applies",
    interchangeCoef: "Coefficient",
    interchangeCoefHelp:
      "Coefficient = how many group base units equal 1 unit of the product (1:1 for equals, e.g. 1 paste = 0.7 juice). Priority = which is used first when covering; empty → the product with the oldest delivery.",
    interchangeSortIndex: "Priority",
    interchangeSortPh: "e.g. 1",
    interchangeNeedTwo: "A group must contain at least 2 products",
    interchangeHintCol: "Comment",
    interchangeHintPh: "Optional comment for the group",
    interchangeHint:
      "When one of the products in the group runs out, the system offers to write off the others by the shown ratios — during report posting and stocktake.",
    interchangeSearchPh: "Product or comment…",
    noInterchange: "No interchangeable product groups yet.",
    interchangeBadCoef: "Coefficient must be a positive number",
    interchangeSaved: "Group saved",
    back: "Back",
    substitutionsReport: "Substituted products",
    noSubstitutions: "No substitutions for the period.",
    subSearchPh: "Search by product or type…",
    // "Voided activities" report (soft delete trail)
    deletedReport: "Voided activities",
    deletedColWhen: "Voided at",
    deletedColDocDate: "Document date",
    deletedColBy: "Voided by",
    deletedKpiCount: "Voided records",
    deletedSearchPh: "Search by type, product or user…",
    noDeleted: "No voided activities for the period.",
    deletedSubtitle: "Voided by {{who}} at {{when}}",
    deletedType_delivery: "Delivery",
    deletedType_writeoff: "Write-off",
    deletedType_scrap: "Scrap",
    deletedType_stocktake: "Stocktake",
    deletedType_transfer: "Transfer",
    deletedType_substitution: "Substitution",
    deletedType_report: "Report posting",
    deletedNetValue: "Net value",
    deletedRestore: "Restore",
    deletedRestoreTitle: "Restore this record?",
    deletedRestoreBody:
      "The record will return to the regular lists and its quantities will be re-applied to the stock.",
    deletedRestored: "Record restored",
    errRestore: "Restore failed",
    colSubCount: "Substitutions",
    subSourceStocktake: "At stocktake",
    subColFrom: "From (donor)",
    subColTo: "To (recipient)",
    subWrittenOff: "Written off",
    subCovered: "Covered",
    // "Sales surplus" page (formerly "Shortage" — shortages from posted reports)
    negativeReport: "Sales surplus",
    negSearchPh: "Product, warehouse or category…",
    noNegative: "No products with negative stock.",
    kpiNegCount: "Products in shortage",
    kpiNegNet: "Net value",
    kpiNegGross: "Gross value",
    shortageTotal: "Total uncovered surplus",
    shortageProductsCount_one: "{{count}} product",
    shortageProductsCount_other: "{{count}} products",
    shortageRowDelivery: "Delivery",
    colDeficitQty: "Quantity",
    shortageBreakdownSubtitle: "Accumulated from reports",
    shortageBreakdownHint:
      "A delivery only covers reports AFTER its date. To clear an older shortage, post a back-dated delivery — on/before the report that caused it.",
    compensateBackdated: "Compensate (back-dated)",
    errSave: "Error while saving",
    docStocktakeTitle: "Stocktake document",
    docStocktakeSubtitle: "Stocktake #{{id}}",
    docStocktakeHeading: "Stocktake",
    docTitle: "Delivery document",
    docSubtitle: "Delivery #{{id}}",
    docHeading: "Goods received",
    docWriteoffTitle: "Write-off document",
    docWriteoffSubtitle: "Write-off #{{id}}",
    docWriteoffHeading: "Goods written off",
    // ─── Posting reports ───────────────────────────────────────────────────
    posting: "Posting reports",
    soldProducts: "Sold products",
    soldRecipes: "Sold recipes",
    newPosting: "New posting",
    editPostingTitle: "Edit posting",
    backToPostings: "Back to postings",
    kpiPostings: "Postings",
    kpiPostedProducts: "Written-off products",
    kpiCompensations: "Compensations",
    colReportNo: "Report #",
    colDateTime: "Date & time",
    periodTotal: "Period total",
    postingColSold: "Sold",
    postingSoldRecipes: "recipes",
    postingSoldProducts: "products",
    postingColCompensations: "Compensations",
    postingSubsBadge: "{{count}} sub.",
    postingPickReport: "Select a report",
    postingNoPending: "No reports to post",
    postingReportOption: "Report #{{number}} · {{date}}",
    postingPickSubtitle_one: "{{count}} pending venue report",
    postingPickSubtitle_other: "{{count}} pending venue reports",
    postingPickBills_one: "{{count}} bill",
    postingPickBills_other: "{{count}} bills",
    postingPickTurnover: "Turnover",
    postingProductTag: "Product",
    postingRecipeTag: "Recipe",
    postingColWarehouse: "Warehouse",
    postingWhSourceProduct: "The product's warehouse (Settings → Warehouse per product)",
    postingWhSourceStation: "The warehouse of the recipe's station",
    postingAlwaysFrom: "Always from {{warehouse}}",
    postingAlwaysFromTitle: "{{product}} — always from {{warehouse}}?",
    postingAlwaysFromBody:
      "In the next postings and when scrapping on void, the product will be written off from {{warehouse}}, not from the warehouse of the recipe's station.",
    postingAlwaysFromUndo: "Change or remove it in Settings → Warehouse per product.",
    postingColAvail: "Available",
    postingColToWriteoff: "To write off",
    colQtyShort: "Qty",
    postingColItem: "Item",
    postingTotalWriteoff: "Total write-off",
    postingPerWarehouse: "per warehouse",
    postingCovered: "covered",
    postingRemoveSub: "Remove substitution (returns stock to negative)",
    postingShortageSummary_one: "{{count}} product is short",
    postingShortageSummary_other: "{{count}} products are short",
    postingNoShortage: "All products available",
    postingSplitMismatch:
      "Allocated amount does not match the required quantity",
    postingCompTitle: "Shortage compensation",
    postingCompMarked: "{{applied}} of {{total}} selected",
    postingCompSelectAll: "select all",
    postingCompApplied_one: "{{count}} compensated",
    postingCompApplied_other: "{{count}} compensated",
    postingSoldTitle: "Sold",
    postingReportShort: "Report #{{number}}",
    postingSoldPositions_one: "{{count}} item",
    postingSoldPositions_other: "{{count}} items",
    postingSoldTotalQty: "{{qty}} pcs",
    postingSoldShow: "Show what was sold",
    postingSoldHide: "Hide what was sold",
    postingSoldStationWarehouse: "→ warehouse {{name}}",
    postingSoldStationNoWarehouse: "no warehouse",
    // The breakdown at the report (under the selected card / „Waiters and stations ▸")
    postingBreakdownTitle: "Waiters and stations",
    postingBreakdownWaiters: "Waiters",
    postingBreakdownStations: "Stations",
    postingBreakdownStationsCount_one: "{{count}} station",
    postingBreakdownStationsCount_other: "{{count}} stations",
    postingWriteoffTitle: "Warehouse write-off",
    postingWriteoffProducts_one: "{{count}} product",
    postingWriteoffProducts_other: "{{count}} products",
    postingShortBadge_one: "{{count}} shortage",
    postingShortBadge_other: "{{count}} shortages",
    postingSaveEdit: "Save changes",
    postingEditNone: "No posted reports for the selected period",
    postingPosted: "Report posted",
    postingUpdated: "Changes saved",
    postingErr: "Error posting the report",
    postingNoRecipes: "The report has no sold items",
    postingMissingWarehouse: "Select a warehouse for: {{names}}",
    postingPickWarehouse: "Select",
    postingNoWarehouseHint:
      "Select a warehouse — or set warehouses on the stations (Organization → Menu → Stations) or “Warehouse per product” (Settings)",
    postingConfirmTitle: "Close the day",
    postingConfirmEditTitle: "Save changes",
    postingConfirmBody:
      "Some products are short — they will be written off into negative stock. Continue?",
    postingConfirmBodyOk:
      "Post the report and write the quantities off from the warehouse?",
    postingConfirmOk: "Post the report",
    postingDocTitle: "Posting document",
    postingDocSubtitle: "Posting #{{id}}",
    postingDocHeading: "Report posting",
    // Reports: sold products / recipes
    soldColName: "Name",
    soldColSold: "Sold (pcs)",
    soldColTotalQty: "Total quantity",
    soldColAvgPrice: "Avg price (gross)",
    soldColIngredients: "Ingredients",
    soldSearchPh: "Search by name…",
    // "Last sale" (recipes only): column, card line, panel header, Excel
    soldColLastSold: "Last sale",
    soldLastSoldLine: "last sale {{date}}",
    // Under the search box — what a row click opens
    soldOpenHintRecipe: "Tap a recipe — sales by day and by report",
    soldOpenHintProduct: "Tap a product — its stock card for the period",
    // The permanent notice above the table (sold/SoldPostedOnlyNotice)
    soldPostedOnlyNotice:
      "Only sales from posted reports are counted here. Today's sales appear once the venue report is made and posted.",
    soldPendingReports_one: "Waiting to be posted: {{count}} report",
    soldPendingReports_other: "Waiting to be posted: {{count}} reports",
    soldPostAction: "Post",
    noSold:
      "No posted sales for the selected period (the report shows posted reports only)",
    soldKpiCount: "Count",
    soldKpiQty: "Total quantity",
    soldKpiRevenue: "Revenue",
    close: "Close",
    print: "Print",
    docEdit: "Edit",
    docDelete: "Void",
    navDeliveryPayments: "Delivery payments",
    navPostedReports: "Posted reports",
    navDeliveriesReport: "Deliveries",
    navWriteoffsReport: "Write-offs",
    navStocktakesReport: "Stocktakes",
    navTransfersReport: "Transfers",
    closeMenu: "Close menu",
    openMenu: "Open menu",
    kickerReport: "Report",
    exportExcel: "Export to Excel",
    viewDocument: "View document",
    rowMenu: "More actions",
    showMore: "Show more ({{count}})",
    pdfHint: "In the print dialog choose “Save as PDF”",
    statusPosted: "Posted",
    statusAnnulled: "Voided",
    statusAnnulledStamp: "VOIDED",
    annulledBanner: "Voided on {{date}}",
    annulReason: "Reason",
    annulReasonPh: "e.g. wrong recipe",
    annulledToast: "Document #{{number}} was voided",
    segAll: "All",
    segPosted: "Posted",
    segAnnulled: "Voided",
    colDocument: "Document",
    colReport: "Report",
    colWrittenOff: "Written off",
    colSubstitutions: "Substitutions",
    kpiValueGross: "Total value · gross",
    periodTotalActive: "Period total · excluding voided",
    postedNoDocs: "No documents for the selected period.",
    postedSearchPh: "Search by document, report, user…",
    filterPostedBy: "Posted by",
    filterNoMatch: "No matches",
    filterClear: "Clear",
    filterDone: "Done",
    filterClearAll: "Clear all",
    periodCustom: "Period",
    periodApply: "Apply",
    periodPickStart: "Pick a start date",
    period_today: "Today",
    period_yesterday: "Yesterday",
    period_last7: "Last 7 days",
    period_last30: "Last 30 days",
    period_thisMonth: "This month",
    period_lastMonth: "Last month",
    period_thisYear: "This year",
    prevMonth: "Previous month",
    nextMonth: "Next month",
    month0: "January",
    month1: "February",
    month2: "March",
    month3: "April",
    month4: "May",
    month5: "June",
    month6: "July",
    month7: "August",
    month8: "September",
    month9: "October",
    month10: "November",
    month11: "December",
    weekday_mon: "Mo",
    weekday_tue: "Tu",
    weekday_wed: "We",
    weekday_thu: "Th",
    weekday_fri: "Fr",
    weekday_sat: "Sa",
    weekday_sun: "Su",
    postingPendingSubtitle_one: "{{count}} unposted report · oldest first",
    postingPendingSubtitle_other: "{{count}} unposted reports · oldest first",
    postingGoPosted: "Posted reports →",
    postingAllPostedTitle: "All reports are posted",
    postingAllPostedText: "New reports appear here after the day is closed.",
    postingViewPosted: "View posted reports",
    postingPendingList: "Unposted reports",
    postingAgeToday: "Today",
    postingAgeYesterday: "Yesterday",
    postingAgeDays_one: "{{count}} day",
    postingAgeDays_other: "{{count}} days",
    postingAgeLate_one: "Late · {{count}} day",
    postingAgeLate_other: "Late · {{count}} days",
    postingReportTitle: "Report #{{number}}",
    postingCardNumber: "#{{number}}",
    postingMetaClosed: "closed {{time}}",
    postingMetaTurnover: "turnover {{amount}}",
    postingMetaWaiters_one: "{{count}} waiter",
    postingMetaWaiters_other: "{{count}} waiters",
    postingStatusShort_one: "{{count}} product short",
    postingStatusShort_other: "{{count}} products short",
    postingStatusWithSub: "{{count}} with substitute",
    postingStatusAllAvailable: "Everything is in stock",
    postingStatusNoWarehouse_one: "{{count}} product without a warehouse",
    postingStatusNoWarehouse_other: "{{count}} products without a warehouse",
    // the short ones — in the narrow pending-report card (the full ones are in the tooltip)
    postingStatusNoWarehouseShort: "{{count}} no warehouse",
    postingStatusShortShort: "{{count}} short",
    postingStatusAllAvailableShort: "In stock",
    postingStatusReturned: "Returned after voiding #{{number}}",
    postingCorrectsAnnulled: "Corrects voided #{{number}}",
    postingChipShort_one: "{{count}} product short",
    postingChipShort_other: "{{count}} products short",
    postingChipSubstituted: "{{count}} substituted",
    postingRemaining_one: "{{count}} product will go negative",
    postingRemaining_other: "{{count}} products will go negative",
    postingSubmitReport: "Post report #{{number}}",
    postingShortConfirmTitle: "Posting with shortage",
    postingShortConfirmBody: "These products will be written off into negative stock. Continue?",
    postingShortConfirmOk: "Post the report",
    postingPostedToast: "Report #{{number}} posted",
    postingViewDocument: "View document",
    postingLeaveBody: "The substitutions chosen for this report will be lost.",
    postingSubsTitle: "Substitute with interchangeable products",
    postingSubsHint: "Split the missing quantity between one or more substitutes.",
    postingAvailableShort: "in stock",
    postingMissing: "Missing {{qty}}",
    postingCoveredLabel: "Covered",
    postingSubQty: "Quantity of {{name}}",
    postingInsteadOf: "⇄ instead of {{name}}",
    postingCoveredNote: "⇄ {{qty}} {{unit}} covered by substitute",
    postingColAfter: "After",
    postingPositions_one: "{{count}} line",
    postingPositions_other: "{{count}} lines",
    postingDocTitleNo: "Posting document #{{number}}",
    postingRepost: "Correct and post again",
    postingRepostHint: "Voids and reopens the report",
    postingRepostTitle: "Correct document #{{number}}",
    postingRepostText: "Written-off quantities return to stock, and report #{{report}} reopens for posting with the previous substitutions.",
    postingRepostConfirm: "Void and correct",
    postingAnnulTitle: "Void document #{{number}}",
    postingAnnulText: "Written-off quantities return to stock, and report #{{report}} returns to “Report posting”.",
    printShort: "Print",
    chipSearch: "Search: “{{q}}”",
    colDocType: "Type",
    colLinks: "Links",
    colNoteNo: "Delivery note #",
    colPayment: "Payment",
    deliveriesNone: "No deliveries match this filter.",
    deliveriesResult_one: "{{count}} delivery",
    deliveriesResult_other: "{{count}} deliveries",
    deliveriesSearchPh: "Search by number, supplier, product, user…",
    deliveriesSumHint: "(delivery notes inside an invoice are not added twice)",
    deliveryAnnulTitle: "Void delivery",
    deliveryAnnulText: "The quantities are taken out of stock. The document stays in the report as voided.",
    deliveryAnnulOwnLines_one: "Its own {{count}} line is taken out of stock",
    deliveryAnnulOwnLines_other: "Its own {{count}} lines are taken out of stock",
    deliveryAnnulNoOwnLines: "It has no lines of its own — nothing is taken out of stock",
    deliveryAnnulNotesStay_one: "{{count}} delivery note stays received and returns to “Notes without invoice”",
    deliveryAnnulNotesStay_other: "{{count}} delivery notes stay received and return to “Notes without invoice”",
    deliveryAnnulStays: "The document stays in the report as voided.",
    deliverySavedToast: "Delivery {{number}} saved",
    docHeadingNote: "Delivery note",
    docTypeInvoice: "Invoice",
    docTypeNote: "Note",
    draftStatusDraft: "Draft",
    draftStatusHeld: "On hold",
    draftStatusBusy: "Being edited",
    draftStatusUnfinished: "Unfinished",
    enteredBy: "entered by {{name}}",
    filterEnteredBy: "Entered by",
    homeAllDeliveries: "All deliveries →",
    homeColleagues: "Colleagues in progress",
    homeContinueDelivery: "Continue the delivery",
    homeContinueDraft: "Continue the unfinished one",
    homeDiscardAndNew: "Delete and start new",
    homeDraftLines_one: "{{count}} product",
    homeDraftLines_other: "{{count}} products",
    homeDraftModalTitle: "You have an unfinished delivery",
    homeDraftModalText: "Continue it or delete it to start a new one.",
    homeDraftUpdated: "last change {{when}}",
    homeEnterInvoice: "Enter invoice",
    homeHasDraft: "You have an unfinished delivery",
    homeMyDraftTitle: "Unfinished delivery",
    homeMyDraftHint: "Each user can have only one unfinished delivery.",
    homeNoDraft: "You have no unfinished delivery.",
    homeNoSupplier: "No supplier",
    homeNotesInfo_one: "{{count}} note · {{from}}",
    homeNotesInfo_other: "{{count}} notes · {{from}} – {{to}}",
    homeTodayTitle: "Entered today",
    homeTodayCount_one: "{{count}} delivery",
    homeTodayCount_other: "{{count}} deliveries",
    homeTodayNone: "No deliveries entered today yet.",
    homeUninvoicedTitle: "Delivery notes without invoice",
    homeUninvoicedHint: "When the supplier issues a summary invoice, enter it from here.",
    homeUnpaidCount_one: "{{count}} unpaid document",
    homeUnpaidCount_other: "{{count}} unpaid documents",
    internalNo: "internal #{{id}}",
    invoiceLinksNotes_one: "The invoice summarises {{count}} delivery note:",
    invoiceLinksNotes_other: "The invoice summarises {{count}} delivery notes:",
    kpiUninvoicedNotes: "Notes without invoice",
    kpiUnpaid: "Unpaid",
    linkInvoice: "Invoice · internal #{{id}}",
    linkWaitingInvoice: "awaiting invoice",
    linkClosedNoInvoice: "closed without invoice",
    filterInvoiceState: "Invoice",
    invoiceState_waiting: "Awaiting invoice",
    invoiceState_closed: "Closed without invoice",
    invoiceState_inInvoice: "In an invoice",
    linkNote: "Note · internal #{{id}}",
    newDeliveryPlus: "+ New delivery",
    noDocNumber: "no number",
    noteInInvoiceBanner: "This note is included in an invoice — it is paid and corrected there.",
    openInvoice: "Open invoice",
    optional: "optional",
    payAge_one: "{{count}} day ago",
    payAge_other: "{{count}} days ago",
    payAllPaid: "All deliveries are paid.",
    payPaid: "Paid",
    payUnpaid: "Unpaid",
    payByInvoice: "By invoice",
    payAnnulled: "Voided",
    payPaidMany: "Paid",
    payUnpaidMany: "Unpaid",
    payAnnulledMany: "Voided",
    payUntracked: "No payment data",
    payMethod_cash: "Cash",
    payMethod_bank: "Bank transfer",
    payMethod_card: "Card",
    payModalTitle: "Payment",
    payDate: "Payment date",
    payMethod: "Method",
    payReference: "Reference",
    payConfirm: "Confirm payment",
    payDocsCount_one: "{{count}} document",
    payDocsCount_other: "{{count}} documents",
    paySelected_one: "{{count}} document selected",
    paySelected_other: "{{count}} documents selected",
    payMarkPaid: "Mark as paid",
    payPickFirst: "Tick at least one document",
    payDoneToast_one: "{{count}} document marked as paid",
    payDoneToast_other: "{{count}} documents marked as paid",
    payError: "The payment was not saved",
    payNeedsServerTitle: "Payments are not enabled on the server yet",
    payNeedsServerText: "This screen works once the backend is updated to the version with delivery payments.",
    docLabelInvoice: "Invoice {{number}}",
    docLabelNote: "Delivery note {{number}}",
    payPanelPaid: "Paid on {{date}}",
    payPanelReference: "ref. {{reference}}",
    payPanelMarkedBy: "marked by {{name}}",
    payPanelUnpaidToday: "Unpaid · since today",
    payPanelUnpaid_one: "Unpaid · for {{count}} day",
    payPanelUnpaid_other: "Unpaid · for {{count}} days",
    payPanelByInvoice: "Paid through the invoice",
    payPanelPaidSeparately: "paid separately on {{date}}",
    payPanelAnnulled: "The document is voided",
    payChange: "Change",
    payMarkPaidOne: "Mark as paid",
    payMarkUnpaid: "Mark as unpaid",
    payUnpayTitle: "Mark {{doc}} as unpaid?",
    payUnpayText: "{{doc}} becomes unpaid and returns to “Delivery payments”.",
    payUnpayTextNotes_one: "{{doc}} becomes unpaid and returns to “Delivery payments” together with its {{count}} delivery note — it is paid through the invoice. A note paid separately before the invoice stays paid.",
    payUnpayTextNotes_other: "{{doc}} becomes unpaid and returns to “Delivery payments” together with its {{count}} delivery notes — they are paid through the invoice. Notes paid separately before the invoice stay paid.",
    payUnpaidToast: "{{doc}} is marked as unpaid",
    payUnpayError: "The payment was not undone",
    payUndo: "Undo",
    payUndoneToast_one: "The payment of {{count}} document was undone",
    payUndoneToast_other: "The payment of {{count}} documents was undone",
    payNotesPaidSeparately_one: "{{count}} delivery note was paid separately — not in the amount",
    payNotesPaidSeparately_other: "{{count}} delivery notes were paid separately — not in the amount",
    noteDetach: "Take out of the invoice",
    noteDetachTitle: "Take the delivery note out of the invoice?",
    noteDetachText: "{{doc}} leaves the invoice and returns to “Notes without invoice”. Its stock stays received. If the invoice is paid, the note stays paid with the same payment.",
    noteDetachConfirm: "Take out",
    noteDetachedToast: "{{doc}} was taken out of the invoice",
    noteDetachError: "The delivery note was not taken out",
    noteClose: "Close without invoice",
    noteCloseTitle: "Close without invoice",
    noteCloseText: "The delivery note leaves “Notes without invoice” — no invoice will be awaited for it. If one comes after all, “Reopen for invoicing” brings it back.",
    noteCloseReason: "Reason",
    noteCloseReasonPh: "e.g. The supplier does not issue invoices",
    noteCloseMarkPaid_one: "and mark it as paid",
    noteCloseMarkPaid_other: "and mark them as paid",
    noteCloseStaysUnpaid: "Without the tick an unpaid note stays in “Delivery payments” until you pay it.",
    noteCloseConfirm: "Close",
    noteClosePick: "Tick at least one delivery note",
    noteClosedToast_one: "{{count}} delivery note was closed without invoice",
    noteClosedToast_other: "{{count}} delivery notes were closed without invoice",
    noteCloseError: "The delivery notes were not closed",
    noteClosedBanner: "Closed without invoice on {{date}}",
    noteReopen: "Reopen for invoicing",
    noteReopenedToast: "{{doc}} is awaiting an invoice again",
    noteReopenError: "The delivery note was not reopened",
    revClosedWithoutInvoice: "Closed without invoice",
    alreadyInDelivery: "“{{name}}” is already in the delivery",
    catalogOpen: "Full list",
    catalogTitle: "All products",
    catalogHint: "Click a row and enter the quantity and price — Enter moves to the next one, Ctrl+Enter adds them all to the delivery",
    catalogFilterPh: "Filter by name",
    catalogAllGroups: "All",
    catalogInDocument: "in the document",
    catalogPicked_one: "{{count}} product selected",
    catalogPicked_other: "{{count}} products selected",
    catalogHintToggle: "ticks",
    catalogHintAdd: "adds",
    catalogAdd: "Add selected",
    colVat: "VAT",
    deleteDraft: "Delete draft",
    deleteDraftTitle: "Delete the draft",
    deleteDraftBody: "The unfinished delivery will be deleted. Nothing has been received into stock.",
    fieldDocType: "Document type",
    fieldNoteNo: "Delivery note #",
    heldToast: "The delivery is on hold — nothing was received into stock",
    lockLost: "{{name}} took over — your changes are no longer saved",
    notesOffer_one: "The supplier has {{count}} delivery note without invoice. If this invoice covers it, pick it.",
    notesOffer_other: "The supplier has {{count}} delivery notes without invoice. If this invoice covers them, pick them.",
    notesPick: "Pick notes",
    notesLinked_one: "The invoice covers {{count}} note · {{total}}",
    notesLinked_other: "The invoice covers {{count}} notes · {{total}}",
    notesLinkedHint: "Their goods are already in stock — the invoice only links them and is paid instead of them. Add new lines below.",
    notesChange: "Change selection",
    notesModalTitle: "Which notes does the invoice cover?",
    notesModalSub: "The supplier's notes without invoice",
    notesModalHint: "Notes are not received twice. A linked note cannot be edited or voided on its own.",
    notesMoreLines: "… and {{count}} more lines",
    notesPicked_one: "{{count}} note · {{total}}",
    notesPicked_other: "{{count}} notes · {{total}}",
    notesApply: "Link to invoice",
    payMethodLong: "Payment method",
    presenceOnlyYou: "Only you are editing",
    probSaveTitle: "Some products have a problem",
    probSaveSub: "Choose how to continue",
    probSaveHold: "Hold the delivery",
    probSaveHoldHint: "Nothing is received until the problem is solved.",
    probSaveWithout: "Save without the problem ones",
    probSaveWithoutHint: "The other products are received now.",
    problemTitle: "Problem with a product",
    problemHint: "A flagged product does not block your work. On save you choose whether to hold the whole delivery or save it without the product.",
    problemNotePh: "e.g. 3 bottles broken, waiting for a credit note",
    problemDescription: "Problem description",
    problemMark: "Flag",
    problemClear: "Remove flag",
    problemFlagHint: "Flag a problem",
    problemReason_damaged: "Damaged",
    problemReason_short: "Short",
    problemReason_wrong: "Wrong product",
    problemReason_expired: "Expired",
    problemReason_other: "Other",
    productsTitle: "Products",
    saveModalTitle: "Save the delivery",
    saveModalSave: "Save",
    saveDateLabel: "What date is the document for?",
    saveNumberRequired: "The invoice number is required",
    saveTypeHintInvoice: "Invoice — the number is required",
    saveTypeHintNote: "Delivery note — can be added to an invoice later",
    saveDelivery: "Save the delivery",
    saveTemp: "Save for later",
    savedTemp: "Draft saved",
    homeLockTitle: "This delivery is being edited right now",
    homeLockText: "{{name}} is working on this delivery ({{supplier}}, {{lines}}, last change {{when}}). If you take it over, {{first}}'s changes stop being saved and {{first}} will see that you took over.",
    homeTakeOver: "Take over editing",
    homeTakeOverToast: "You took over {{name}}'s delivery",
    homeTakeOverFailed: "Could not take over the delivery — try again",
    homeDeleteDraftLocked: "{{name}} is editing the draft right now — it cannot be deleted",
    shortageSubtitle: "Uncovered sales surplus by warehouse · covered by a back-dated delivery",
    shortageSortName: "By product",
    shortageSortValue: "By value",
    shortagePickFirst: "Tick at least one product",
    shortageSelectedGross_one: "{{count}} product selected · gross",
    shortageSelectedGross_other: "{{count}} products selected · gross",
    shortageBackdatedTo: "delivery back-dated to {{date}}",
    shortageCompensate: "Compensate with a delivery",
    shortageReportsInfo_one: "{{count}} report · since {{date}}",
    shortageReportsInfo_other: "{{count}} reports · since {{date}}",
    shortageBreakdownOpen: "Breakdown by report",
    shortageCompensateOneHint: "A delivery for this product only",
    interchangeRulesCount_one: "{{count}} rule",
    interchangeRulesCount_other: "{{count}} rules",
    interchangeMembersCount_one: "{{count}} product in the group",
    interchangeMembersCount_other: "{{count}} products in the group",
    interchangeCatalogHint: "Tick the products that replace each other and add them at once",
    reportResult_one: "{{count}} result",
    reportResult_other: "{{count}} results",
    stockSubtitle_one: "{{count}} product · {{inStock}} in stock",
    stockSubtitle_other: "{{count}} products · {{inStock}} in stock",
    stockPickWarehouse: "Choose a warehouse to see its stock.",
    stockMinLine: "min. {{qty}}",
    stockLastPriceLine: "last {{price}}",
    soldSubtitleProduct_one: "{{count}} product in the period",
    soldSubtitleProduct_other: "{{count}} products in the period",
    soldSubtitleRecipe_one: "{{count}} recipe in the period",
    soldSubtitleRecipe_other: "{{count}} recipes in the period",
    soldAvgLine: "avg. price {{price}}",
    soldIngredientsLine_one: "{{count}} ingredient",
    soldIngredientsLine_other: "{{count}} ingredients",
    subSubtitle_one: "{{count}} substitution in the period · all warehouses",
    subSubtitle_other: "{{count}} substitutions in the period · all warehouses",
    deletedSubtitle_one: "{{count}} annulled record · the period is by annulment date",
    deletedSubtitle_other: "{{count}} annulled records · the period is by annulment date",
    newTransferPlus: "+ New transfer",
    homeAllTransfers: "All transfers →",
    trxHomeSubtitle: "Stock moves between warehouses at cost — FIFO or a chosen batch.",
    trxHomeTodayTitle: "Transferred today",
    trxHomeTodayCount_one: "{{count}} transfer",
    trxHomeTodayCount_other: "{{count}} transfers",
    trxHomeTodayNone: "No transfers yet today.",
    trxSavedToast: "Transfer No. {{number}} saved",
    trxEnteredBy: "entered by {{name}}",
    trxAnnulTitle: "Annul transfer No. {{number}}",
    trxAnnulText: "The quantities return to the source warehouse. The document stays in the report as annulled.",
    trxCatalogTitle: "In stock at {{warehouse}}",
    trxCatalogHint: "Only products in stock at the source warehouse on the date — click a row and enter the quantity",
    trxSegActive: "Active",
    transfersSearchPh: "Search by No., warehouse, product, user, comment…",
    transfersResult_one: "{{count}} transfer",
    transfersResult_other: "{{count}} transfers",
    transfersNone: "No transfers match this filter.",
    newStocktakePlus: "+ New stocktake",
    stocktakeHomeAll: "All stocktakes →",
    stocktakeHomeHasDraft: "You have an unfinished stocktake",
    stocktakeHomeDraftHint: "The draft is saved automatically in this browser until you save or delete the stocktake.",
    stocktakeHomeNoDraft: "You have no unfinished stocktake.",
    stocktakeHomeContinue: "Continue the stocktake",
    stocktakeHomeCounted_one: "{{count}} counted product",
    stocktakeHomeCounted_other: "{{count}} counted products",
    stocktakeCountedShort_one: "{{count}} counted",
    stocktakeCountedShort_other: "{{count}} counted",
    stocktakeDraftModalTitle: "You have an unfinished stocktake",
    stocktakeLatestTitle: "Latest stocktake by warehouse",
    stocktakeLatestHint: "“Start” opens a new stocktake for that warehouse.",
    stocktakeLatestNever: "No stocktake yet",
    stocktakeStart: "Start",
    stkUnfinished: "Unfinished",
    stkUnfinishedYours: "yours",
    stkCountingNow: "{{name}} is counting now",
    stkWhenToday: "today {{time}}",
    stkWhenYesterday: "yesterday {{time}}",
    stkRowContinue: "Continue",
    stkRowTakeOver: "Take over",
    stkColleagueAskTitle: "{{name}} is already counting {{warehouse}}",
    stkColleagueAskText: "Continue the count already started or start a separate one — the two are saved as separate stocktakes.",
    stkColleagueAskCaption: "The count in progress",
    stkColleagueContinue: "Continue {{first}}'s count",
    stkColleagueContinueHint: "Opens the sheet with everything counted so far — nothing is counted twice",
    stkColleagueSeparate: "Start a separate one",
    stkColleagueSeparateHint: "A new stocktake of {{warehouse}} alongside {{first}}'s",
    stocktakeAgoToday: "today",
    stocktakeAgoDays_one: "{{count}} day ago",
    stocktakeAgoDays_other: "{{count}} days ago",
    stocktakeSavedToast: "Stocktake #{{number}} saved",
    stocktakeAnnulTitle: "Annul stocktake #{{number}}",
    stocktakeAnnulText: "Its surpluses and shortages are reversed — stock is recalculated without it. The document stays in the report as annulled.",
    stocktakesSearchPh: "Search by #, warehouse, user, reason, product…",
    stocktakeStatusActive: "Active",
    stocktakeStatusAnnulled: "Annulled",
    stocktakeNoGroup: "No group",
    woCatalogTitle: "Full list of products and recipes",
    woCatalogHint: "Click a row and enter the quantity — Enter moves to the next one, Ctrl+Enter adds them all at once",
    woCatalogRecipes: "Recipes",
    woLinesCount_one: "{{count}} line",
    woLinesCount_other: "{{count}} lines",
    newWriteoffPlus: "+ New write-off",
    woHomeAll: "All write-offs →",
    woHomeSubtitle: "Write-offs and scrap · warehouse {{warehouse}}",
    woQuickTitle: "Quick start",
    woQuickHint: "The type and reason are filled in on the sheet",
    woTodayTitle: "Written off today",
    woTodayNone: "No write-offs in this warehouse today yet.",
    woDocsCount_one: "{{count}} document",
    woDocsCount_other: "{{count}} documents",
    woNoReason: "No reason",
    woProductsCount_one: "{{count}} product",
    woProductsCount_other: "{{count}} products",
    woSavedToast: "Write-off #{{number}} saved",
    woScrapSavedToast: "Scrap #{{number}} saved",
    woAnnulTitle: "Annul write-off #{{number}}",
    woAnnulText: "The written-off quantities return to the warehouse. The document stays in the report as annulled.",
    woReportSearchPh: "Search by #, reason, comment, product, user…",
    woReportNone: "No write-offs for this filter.",
    woStatusActive: "Active",
    wsCreateNamed: "New product “{{name}}”",
    stoCatalogTitle: "Whole list — what you count",
    stoCatalogHint: "Tick the products you are about to count — they move to the top of the table in the order you picked them, and you count them one after another with ↓.",
    // "Whole list" (components/search/CatalogPickerModal) — columns, groups, selection tray
    catAllVisible: "All visible",
    catColPrice: "Price",
    catEmpty: "The catalogue is empty",
    catNoMatchInGroup: "No matches in “{{group}}”",
    catShowAllGroups_one: "Show all groups · {{count}} match",
    catShowAllGroups_other: "Show all groups · {{count}} matches",
    catCreateNamed: "New product “{{name}}”",
    catGroups: "Groups",
    catOnlyInStock: "Only in stock",
    catHideAdded: "Hide added",
    catSelected_one: "{{count}} selected",
    catSelected_other: "{{count}} selected",
    catNothingSelected: "Nothing ticked — Enter or click a row",
    catUnpick: "Remove “{{name}}” from the selection",
    catAddCount_one: "Add {{count}} selected",
    catAddCount_other: "Add {{count}} selected",
    catShownOf: "{{shown}} of {{total}}",
    catKeyRow: "row",
    catKeyGroup: "group",
    catKeyEscClear: "clears the filter",
    catKeyEscClose: "closes",
    catMore: "+{{count}} more",
    // in-row entry, "Min.", the total, the "Below minimum" / "From this supplier" chips, overwrite
    catColMin: "Min.",
    catColSum: "Amount",
    catFieldPrice: "Net unit price",
    catFieldPriceHint: "In a ticked row — net price per base unit, prefilled with the last delivery price; in the others — the last delivery price",
    catSelectedSum: "total",
    catBelowMinimum: "Below minimum",
    catBelowMinimumHint: "Products whose stock is below the minimum quantity — to reorder",
    catFromSupplier: "From this supplier",
    catFromSupplierHint: "Products with a delivery price from “{{name}}”",
    catKeyEdit: "tick and enter",
    catKeyPrice: "to the price",
    catOverwriteTitle_one: "Overwrite {{count}} row in the sheet?",
    catOverwriteTitle_other: "Overwrite {{count}} rows in the sheet?",
    catOverwriteText: "Rows already filled in on the sheet get what you entered here; the other picked products are added as they are.",
    catOverwriteCaption: "On the sheet → new",
    catOverwriteConfirm: "Overwrite and add",
    catOverwriteBack: "Back",
    ichGroupTitle: "Which products replace each other",
    ichGroupLead: "Enter the quantities that are worth the same. When a product is short during report posting or a stocktake, the system offers to write off another product from the group at the same ratio.",
    ichOrderLabel: "Which is written off first",
    ichOrderOldest: "Oldest delivery",
    ichOrderManual: "In this order",
    ichOrderOldestHelp: "Among the substitutes, the one with the oldest delivery is used first — older stock runs out first.",
    ichOrderManualHelp: "No. 1 is used first, then No. 2… — reorder with the arrows, Alt+↑/↓ or by dragging the number.",
    ichSlotLabel: "Product {{index}}",
    ichSlotFirstHint: "type its name or pick from the full list",
    ichSlotSecondHint: "…and a product that can replace it",
    ichSlotMoreLabel: "Another product",
    ichSlotMoreHint: "optional — every product in the group replaces the others",
    ichKeysAmounts: "between quantities",
    ichKeysMove: "moves the product",
    ichDragHandle: "Drag to reorder",
    ichPriorityAria: "Priority {{index}}",
    ichPriorityNone: "No priority — oldest delivery first",
    ichAmountAria: "Quantity of {{name}}",
    ichMoveUp: "Move up",
    ichMoveDown: "Move down",
    ichRemove: "Remove {{name}} from the group",
    ichUnitFallback: "u.",
    ichReady: "Ready to save",
    ichMissingAmount: "Enter a quantity for: {{names}}",
    ichExampleLabel: "Example",
    ichExampleA: "Coffee A",
    ichExampleB: "Coffee B",
    ichExampleUnit: "kg",
    ichExampleScenario: "If 10 {{unit}} of {{a}} are short, the system offers to write off 7 {{unit}} of {{b}} — and vice versa: when {{b}} is short, {{a}} is offered.",
    ichPreviewTitle: "How it will work",
    ichPreviewPick: "If short",
    ichPreviewShortage: "Short:",
    ichPreviewOffer: "the system offers to write off:",
    ichPreviewOr: "or",
    ichPreviewNeedAmount: "(enter a quantity)",
    ichPreviewOldestNote: "the one with the oldest delivery first",
    ichPreviewNextNote: "The next one is used if the previous is not enough.",
    ichPreviewNeedRecipient: "Enter a quantity for {{name}} to see the example.",
    ichPreviewWhere: "Applies to report posting and stocktakes. The substitution is offered — you confirm it.",
    ichPriorityOldest: "The one with the oldest delivery is written off first",
    ichPriorityOrder: "Written off first: {{names}}",
    ichPriorityRest: "the rest — by oldest delivery",
    ichEmptyTitle: "No substitution rules yet",
    ichEmptyText: "A rule says which products can replace each other and in what quantity. When one is short during report posting or a stocktake, the system offers to write off another from the same rule.",
    ichNoMatch: "No rule matches “{{query}}”",
    ichClearSearch: "Clear search",
    ichListKeys: "pick and open a rule",
    // Suggestions of the write-off "Reason" field (writeoff/components/WriteoffReasonField)
    wrPreviousReasons: "Previous reasons",
    wrReasonUsed_one: "Used {{count}} time",
    wrReasonUsed_other: "Used {{count}} times",
    wrKeyPick: "picks",
    // Change history of a document (components/revisions/)
    revTitle: "Change history",
    revNone: "No changes since it was created",
    revEdits_one: "{{count}} edit",
    revEdits_other: "{{count}} edits",
    revLast: "last {{when}} · {{name}}",
    revChanges_one: "{{count}} change",
    revChanges_other: "{{count}} changes",
    revNoValueChanges: "saved without changing any value",
    revCreated_delivery: "created",
    revCreated_writeoff: "created",
    revCreated_stocktake: "created",
    revCreated_transfer: "created",
    revAdded: "added",
    revRemoved: "removed",
    revLinkedNotes: "Summarised notes",
    revTotalNet: "Net total",
    revTotalGross: "Gross total",
    revSubstitution: "Substitution",
    revAutoPrice: "automatic",
    revUnknownUser: "unknown user",
    revLoadError: "The change history could not be loaded",
    revShowHistory: "Show the change history",
    revHideHistory: "Hide the change history",
    // Confirmations (components/confirm): a question naming the object, what happens, a preview, a specific verb
    cfmMore: "+{{count}} more",
    cfmFinal: "This cannot be undone.",
    cfmPortions_one: "{{value}} portion",
    cfmPortions_other: "{{value}} portions",
    cfmLeaveTitle: "Leave {{document}} without saving?",
    cfmLeaveDraftTitle: "Leave {{document}}?",
    cfmLeaveText: "Unsaved changes are discarded — nothing changes in the warehouse.",
    cfmLeaveDraftText: "What you counted stays as a draft in this browser and you will be offered to continue next time. Nothing is saved to the warehouse yet.",
    cfmLeavePostingText: "The substitutions chosen for report #{{number}} are discarded. The report stays unposted in the list — nothing is written off.",
    cfmLeaveCaption: "Unsaved lines",
    cfmLeaveSheetCaption: "Lines in the sheet",
    cfmLeaveDraftCaption: "Kept in the draft",
    cfmLeaveFinal: "Discarded changes cannot be recovered.",
    cfmLeaveConfirm: "Leave without saving",
    cfmLeaveDraftConfirm: "Leave, keep the draft",
    cfmLeaveStay: "Stay",
    cfmDocNewWriteoff: "the new write-off",
    cfmDocNewScrap: "the new scrap",
    cfmDocEditWriteoff: "editing write-off #{{number}}",
    cfmDocEditScrap: "editing scrap #{{number}}",
    cfmDocNewTransfer: "the new transfer",
    cfmDocEditTransfer: "editing transfer #{{number}}",
    cfmDocNewStocktake: "the new stocktake",
    cfmDocEditStocktake: "editing stocktake #{{number}}",
    cfmDocEditDelivery: "editing delivery #{{number}}",
    cfmDocPosting: "posting report #{{number}}",
    cfmRemoveRowTitle: "Remove “{{name}}” from the sheet?",
    cfmRemoveRowTitlePlain: "Remove the line from the sheet?",
    cfmRemoveRowText: "The line with {{qty}} is removed from the sheet. Nothing changes in the warehouse until you save.",
    cfmRemoveRowTextEmpty: "The line is removed from the sheet. Nothing changes in the warehouse until you save.",
    cfmRemoveRowUndo: "You can add it again from the search.",
    cfmRemoveRowConfirm: "Remove the line",
    cfmSplitRecipeTitle: "Split the recipe “{{name}}”?",
    cfmSplitRecipeText_one: "The line is replaced by {{count}} product line — the recipe ingredient for {{portions}}, with a quantity and batch you can change.",
    cfmSplitRecipeText_other: "The line is replaced by {{count}} product lines — the recipe ingredients for {{portions}}, each with a quantity and batch you can change separately.",
    cfmSplitRecipeCaption: "Lines to be added",
    cfmSplitRecipeDefaultPortions: "Portions are empty — quantities are for 1 portion.",
    cfmSplitRecipeUndo: "You can merge them back with “{{action}}”.",
    cfmSplitRecipeConfirm_one: "Split into {{count}} line",
    cfmSplitRecipeConfirm_other: "Split into {{count}} lines",
    cfmRevertTitle: "Merge back into the recipe “{{name}}”?",
    cfmRevertText_one: "{{count}} product line is merged back into one “{{name}}” line for {{portions}} — changes to its quantity and batch are lost.",
    cfmRevertText_other: "{{count}} product lines are merged back into one “{{name}}” line for {{portions}} — changes to their quantities and batches are lost.",
    cfmRevertCaption: "Lines to be merged",
    cfmRevertUndo: "You can split it again with “{{action}}”.",
    cfmRevertConfirm: "Merge into the recipe",
    cfmDeleteDraftTitle: "Delete the unfinished delivery?",
    cfmDeleteDraftTitleSupplier: "Delete the unfinished delivery from “{{supplier}}”?",
    cfmDeleteDraftText: "The draft and all its lines are deleted. Nothing has been received, so the warehouse does not change.",
    cfmDeleteDraftCaption: "To be deleted",
    cfmDraftDeliveryTitle: "Continue the unfinished delivery?",
    cfmDraftDeliveryTitleBy: "Continue {{name}}'s unfinished delivery?",
    cfmDraftStocktakeTitle: "Continue the unfinished stocktake?",
    cfmDraftStocktakeTitleBy: "Continue {{name}}'s unfinished stocktake?",
    cfmDraftSavedAt: "The draft was saved automatically on {{when}}. Continue from it or start over.",
    cfmDraftCaption: "In the draft",
    cfmDraftContinue: "Continue the draft",
    cfmDraftContinueHint: "Its lines are loaded into the sheet and you carry on from there.",
    cfmDraftStocktakeContinueHint: "What you counted is loaded into the sheet and you carry on counting.",
    cfmDraftDiscard: "Delete and start over",
    cfmDraftDiscardHint: "The draft is deleted for good and the sheet starts empty.",
    cfmDraftAskDeliveryTitle: "Continue the delivery from “{{supplier}}”?",
    cfmDraftAskDeliveryTitlePlain: "Continue the unfinished delivery?",
    cfmDraftAskDeliveryText: "Each user has only one unfinished delivery — to start a new one, this one has to be saved or deleted.",
    cfmDraftAskStocktakeTitle: "Continue the stocktake of “{{warehouse}}”?",
    cfmDraftAskStocktakeTitlePlain: "Continue the unfinished stocktake?",
    cfmDraftAskStocktakeText: "This browser already has a stocktake in progress — to start a new one, this one has to be saved or deleted.",
    cfmDraftAskCaption: "Unfinished",
    cfmDraftAskContinueHint: "Opens the sheet with its lines.",
    cfmDraftAskStocktakeContinueHint: "Opens the sheet with what you counted so far.",
    cfmDraftAskDiscardHint: "The draft is deleted for good and an empty sheet opens.",
    cfmLockTitle: "Take over {{name}}'s delivery?",
    cfmLockText: "{{name}} is editing it right now. If you take it over, {{first}}'s changes stop being saved and {{first}} will see that you took over.",
    cfmLockCaption: "The draft",
    cfmLockConfirm: "Take over from {{first}}",
    cfmProblemTitle_one: "{{count}} product with a problem — how should the delivery be saved?",
    cfmProblemTitle_other: "{{count}} products with a problem — how should the delivery be saved?",
    cfmProblemText: "A product with a problem is not received until the problem is solved. Choose what happens to the rest.",
    cfmProblemCaption: "With a problem",
    cfmProblemHoldHint: "Nothing is received — the delivery stays a draft with status “On hold” until the problem is solved.",
    cfmProblemWithoutHint_one: "{{count}} product is received now, the problem ones stay in a held draft.",
    cfmProblemWithoutHint_other: "{{count}} products are received now, the problem ones stay in a held draft.",
    cfmShortageTitle_one: "Post report #{{number}} with a shortage of {{count}} product?",
    cfmShortageTitle_other: "Post report #{{number}} with a shortage of {{count}} products?",
    cfmShortageText: "These products are short in the warehouse and will be written off into negative stock until a later delivery or stocktake evens it out.",
    cfmShortageCaption: "Short",
    cfmShortageUndo: "If it turns out to be a mistake, the posting can be voided from “{{report}}”.",
    cfmShortageConfirm: "Post with shortage",
    cfmAnnulDeliveryTitle: "Void delivery #{{number}}?",
    cfmAnnulDeliveryTitleSupplier: "Void delivery #{{number}} from “{{supplier}}”?",
    cfmAnnulDeliveryCaption: "Leaving the warehouse",
    cfmAnnulDeliveryConfirm: "Void the delivery",
    cfmAnnulWriteoffTitle: "Void write-off #{{number}}?",
    cfmAnnulScrapTitle: "Void scrap #{{number}}?",
    cfmAnnulWriteoffCaption: "Returning to the warehouse",
    cfmAnnulWriteoffConfirm: "Void the write-off",
    cfmAnnulScrapConfirm: "Void the scrap",
    cfmAnnulTransferTitle: "Void transfer #{{number}}?",
    cfmAnnulTransferCaption: "Returning to “{{warehouse}}”",
    cfmAnnulTransferConfirm: "Void the transfer",
    cfmAnnulStocktakeTitle: "Void stocktake #{{number}}?",
    cfmAnnulStocktakeCaption: "Differences to be undone",
    cfmAnnulStocktakeConfirm: "Void the stocktake",
    cfmAnnulUndo: "The document can be restored from the “{{report}}” report.",
    cfmRestoreTitle: "Restore {{document}}?",
    cfmRestoreDoc_delivery: "delivery #{{number}}",
    cfmRestoreDoc_writeoff: "write-off #{{number}}",
    cfmRestoreDoc_scrap: "scrap #{{number}}",
    cfmRestoreDoc_stocktake: "stocktake #{{number}}",
    cfmRestoreDoc_transfer: "transfer #{{number}}",
    cfmRestoreDoc_substitution: "substitution #{{number}}",
    cfmRestoreDoc_report: "posting #{{number}}",
    cfmRestoreDocOther: "record #{{number}}",
    cfmRestoreCaption: "Applied again",
    cfmRestoreUndo: "The document can be voided again from its view.",
    cfmRestoreVerb_delivery: "Restore the delivery",
    cfmRestoreVerb_writeoff: "Restore the write-off",
    cfmRestoreVerb_scrap: "Restore the scrap",
    cfmRestoreVerb_stocktake: "Restore the stocktake",
    cfmRestoreVerb_transfer: "Restore the transfer",
    cfmRestoreVerb_substitution: "Restore the substitution",
    cfmRestoreVerb_report: "Restore the posting",
    cfmRestoreVerbOther: "Restore the record",
    // Column header filters of the reports (components/filters/ColumnHeader, ColumnFilterPanel, FilterSummaryLine)
    cfFilters: "Filters",
    cfSelectAll: "All",
    cfOnly: "Only this",
    cfFrom: "from",
    cfTo: "to",
    cfFilterAria: "Filter by “{{column}}”",
    cfRemove: "Remove the filter “{{filter}}”",
    cfKeyToggle: "ticks",
    cfKeyOnly: "only this",
    cfKind: "Kind",
    cfKindReason: "Kind and reason",
    cfNoMatch: "Nothing matches the search and the filters",
    cfClearFilters: "Clear the filters",
    cfWithSurplus: "With a surplus",
    cfNoSurplus: "No surplus",
    cfWithShortage: "With shortages",
    cfNoShortage: "No shortages",
    scTitle: "Stock card",
    scCardAria: "Stock card of {{name}}",
    scClose: "Close the card",
    scInStock: "In stock",
    scInStockAsOf: "In stock on {{date}}",
    scTabMovements: "Movements",
    scTabLots: "Batches",
    scTabSubstitutions: "Substitutions",
    scOpening: "Opening balance",
    scClosing: "Closing balance",
    scColIn: "In",
    scColOut: "Out",
    scColPrice: "Price",
    scColUser: "Entered by",
    scColNote: "Note",
    scTypeAll: "All",
    scTypeFilterAria: "Movement type",
    scDoc_delivery: "Delivery",
    scDoc_report: "Report",
    scDoc_writeoff: "Write-off",
    scDoc_scrap: "Scrap",
    scDoc_stocktake: "Stocktake",
    scDoc_transfer: "Transfer",
    scDoc_substitution: "Substitution",
    scDocNumber: "{{label}} #{{number}}",
    scCoveredBy: "⇄ covered by {{name}}",
    scBatchNote: "batch {{batch}}",
    scExpiryNote: "until {{date}}",
    scEmptyPeriod: "No movements in this period.",
    scEmptyFiltered: "No movements of this type in the period.",
    scExcel: "Excel",
    scInconsistent: "The movements do not add up to the closing balance — check the product history.",
    scNegativeHint: "Below zero — more was written off than was in stock",
    scSoldWithoutStock: "⚠ {{qty}} sold without stock",
    scWrittenOffWithoutStock: "⚠ {{qty}} written off without stock",
    scCoveredNegative: "({{qty}} cover what was sold without stock)",
    scColReceived: "Received",
    scColRemaining: "Remaining",
    scLotsHint: "Open batches right now — in the order they will be written off (oldest first).",
    scLotsEmpty: "No open batches.",
    scColSubstitution: "Substitution",
    scSubsEmpty: "No substitutions in this period.",
    scSubsCovered: "covered {{qty}}",
    scSubsTaken: "written off {{qty}}",
    scSubsCountReplaced_one: "replaced another product once",
    scSubsCountReplaced_other: "replaced another product {{count}} times",
    scSubsCountReplacedBy_one: "was replaced once",
    scSubsCountReplacedBy_other: "was replaced {{count}} times",
    scBadgeReplaced: "⇄ replaced {{name}}",
    scBadgeReplacedBy: "⇄ replaced by {{name}}",
    scBadgeTipTitle: "Substitutions for {{period}}",
    scBadgeTipReplaced_one: "Replaced {{name}} — once, {{qty}}",
    scBadgeTipReplaced_other: "Replaced {{name}} — {{count}} times, {{qty}}",
    scBadgeTipReplacedBy_one: "Replaced by {{name}} — once, {{qty}}",
    scBadgeTipReplacedBy_other: "Replaced by {{name}} — {{count}} times, {{qty}}",
    scMasterListAria: "Products in the warehouse",
    scKeysSwitch: "product",
    scKeysClose: "closes",
    ichDocNewRule: "the new rule",
    ichDocEditRule: "the rule “{{name}}”",
    ichDeleteTitle: "Delete the rule “{{name}}”?",
    ichDeleteText: "Its products will no longer be offered as interchangeable when posting reports or counting stock. Stock levels and documents already saved do not change.",
    ichDeleteCaption: "Products in the rule",
    ichDeleteFinal: "This cannot be undone — the rule has to be created again.",
    ichDeleteConfirm: "Delete the rule",
    cfmPostingAnnulTitle: "Void the posting of Report #{{report}}?",
    cfmPostingRepostTitle: "Correct the posting of Report #{{report}}?",
    cfmPostingAnnulNote: "The voided posting stays in this report, struck through, with the reason; the report can be posted again.",
    cfmPostingAnnulConfirm: "Void the posting",
    cfmPostingRepostConfirm: "Void and post again",
    upPriceFromSum: "Calculated from the entered line total — the total is saved exactly",
    // Shared report toolbar and period picker (round 4)
    tbPeriodPickRange: "pick the first and the last day",
    tbPeriodPickEnd: "pick the last day",
    tbToolbarAria: "Report toolbar",
    tbColAnnulReason: "Void reason",
    tbColAgeDays: "Days since delivery",
    tbColReports: "Reports",
    tbColOldestReport: "Oldest report",
    stkStatusAll: "All",
    stkStatusCounted: "Counted",
    stkStatusUncounted: "Not counted",
    stkStatusDiff: "With difference",
    stkAllGroups: "All groups",
    stkGroupsAria: "Product groups",
    stkBandProgress: "{{counted}} of {{total}}",
    stkBandPinned_one: "{{count}} picked to count",
    stkBandPinned_other: "{{count}} picked to count",
    stkBandRestHint: "they stay unchanged; type 0 if the product is missing",
    stkEmptyCounted: "Nothing counted yet.",
    stkEmptyUncounted: "Every product is counted.",
    stkEmptyDiff: "No differences — the count matches the expected stock.",
    stkTotalsSurplus: "Surpluses",
    stkTotalsShortage: "Shortages",
    stkTotalsNetCaption: "Net result",
    stkIssuePrice: "Enter a unit price for: {{products}}",
    stkIssueMore_one: "and {{count}} more",
    stkIssueMore_other: "and {{count}} more",
    stkPricePh: "price *",
    stkPriceRequiredHint: "No delivery price in this warehouse — enter the surplus unit price, otherwise it enters at 0.",
    sdGroupRecipe: "By recipe",
    sdGroupRecipeTip: "Current recipes × what was sold on bills closed in the period, at the current average cost.",
    sdGroupPosted: "Posted",
    sdGroupPostedTip: "What posting the reports dated in the period wrote off, at FIFO cost — including what was written off instead of another product (substitution).",
    sdGroupDiff: "Difference",
    sdGroupDiffTip: "Posted − by recipe. “+” means more was written off than the recipes say; “−” means less.",
    sdColQty: "Qty",
    sdDatesHint: "By recipe counts bills by the time they were closed; Posted counts reports by their date.",
    sdDatesHintTip: "The two differ when a recipe changed after posting, the price differs (current average vs FIFO), a product was substituted, a quantity was corrected by hand while posting, or the report is dated outside the period (for example, run after midnight).",
    sdDonorLine: "{{qty}} written off instead of another product",
    sdCoveredLine: "{{qty}} covered by another product",
    sdKeysSwitch: "row",
    sdPostedByWarehouse: "Posted by warehouse: {{list}}",
    sdCardPeriodNote: "The card uses the report period ({{period}}) and opens on the postings — the “All” chip shows every movement.",
    sdRecipeKicker: "Sold recipe",
    sdRecipePanelAria: "Sold recipe {{name}}",
    sdClosePanel: "Close the panel",
    sdSoldLabel: "Sold",
    sdTabDays: "By day",
    sdTabReports: "Reports",
    sdTabIngredients: "Ingredients",
    sdDaysHint: "Sales by the day the bill was closed — only the days with sales.",
    sdDaysHoursHint: "By hour — in Reports → “Sales by periods” (the “Items” filter).",
    sdReportsHint: "The period's bills by the posted report they belong to. Click or press Enter to open the posting.",
    sdReportsEmpty: "No posted reports with this recipe in the period.",
    sdIngredientsNote: "By the CURRENT recipe × {{sold}} sold — theoretical usage, not what was written off. The write-off is in “Sold products” → “Posted”.",
    sdIngredientsEmpty: "The recipe has no ingredients.",
    sdColPerPortion: "Per portion",
    sdColTotalQty: "Total",
    sdPerPortionLine: "{{qty}} per portion",
    sdIngredientsTotal: "Cost by recipe",
    sdXlsDonor: "Posted · incl. instead of another product",
    sdXlsCovered: "Covered by another product",
    stqStatusPinned: "To count",
    stqBandPinnedHint: "(picked from the list)",
    stqBandRestHint: "they stay as they are; type 0 if missing",
    stqEmptyPinned: "Nothing to count — pick products from “Full list”.",
    stqBatchLink: "+ batch / expiry",
    stqTotalsWithVat: "(incl. VAT {{value}})",
    // “Zero the warehouse” on the new stocktake sheet (useStocktakeZeroing)
    stqZeroButton: "Zero the warehouse",
    stqZeroNeedsWarehouse: "Choose a warehouse first",
    stqZeroNothing: "No products with stock in “{{warehouse}}” as of {{date}}.",
    stqZeroTitle: "Zero the “{{warehouse}}” warehouse as of {{date}}?",
    stqZeroText_one: "{{count}} product gets “Counted” 0.",
    stqZeroText_other: "{{count}} products get “Counted” 0.",
    stqZeroTextNegative_one: "{{count}} of them is below zero and also becomes 0.",
    stqZeroTextNegative_other: "{{count}} of them are below zero and also become 0.",
    stqZeroTextCounted_one: "The {{count}} product already counted stays as it is.",
    stqZeroTextCounted_other: "The {{count}} products already counted stay as they are.",
    stqZeroTextWhole: "Applies to the whole warehouse — the filters above the table do not limit it.",
    stqZeroCaption: "Stock as of {{date}}",
    stqZeroNegativeTag: "below zero",
    stqZeroAll: "Zero all ({{count}})",
    stqZeroAllHint: "Everything becomes 0. Shortage of about {{value}} (at the average price; the exact one is calculated on save).",
    stqZeroAllHintNoPrice: "Everything becomes 0. The shortage value is calculated on save.",
    stqZeroNegative: "Only below zero ({{count}})",
    stqZeroNegativeHint: "Only the products below zero become 0, with no value. The rest wait to be counted.",
    stqZeroNegativeNone: "No products below zero.",
    stqZeroNoteDraft: "Nothing is saved until you confirm the stocktake — you can fix single rows.",
    stqZeroNoteUndo: "A saved stocktake is annulled from Reports → Stocktakes and the stock comes back.",
    stqZeroReason: "Warehouse zeroing",
    stqZeroApplied_one: "“Counted” 0 for {{count}} product — review and confirm the stocktake.",
    stqZeroApplied_other: "“Counted” 0 for {{count}} products — review and confirm the stocktake.",
    dashNav: "Dashboard",
    dashTitle: "Warehouse",
    dashGroupShow: "Expand “{{name}}”",
    dashGroupHide: "Collapse “{{name}}”",
    dashGridAria: "Warehouse operations",
    dashLoadFailed: "Could not load",
    dashOpenAria: "Open “{{name}}”",
    dashAttention: "needs attention",
    dashLocalOff: "Local mode — the dashboard does not show the warehouse state until the cloud is reachable again.",
    dashKeysOpen: "opens the operation",
    dashKeysMove: "move between boxes",
    dashKeysEnter: "opens the selected one",
    dashBoxPosting: "Posting",
    dashBoxDelivery: "Delivery",
    dashBoxPayments: "Payment",
    dashBoxWriteoff: "Write-off",
    dashBoxStocktake: "Stocktake",
    dashBoxTransfer: "Transfer",
    dashBoxShortage: "Sales surplus",
    dashBoxInterchange: "Interchangeable",
    dashNewFem: "+ New",
    dashNewNeut: "+ New",
    dashContinue: "Continue",
    dashPay: "Pay",
    dashPostingNone: "All reports are posted",
    dashPendingLabel_one: "report waiting",
    dashPendingLabel_other: "reports waiting",
    dashOverdue_one: "{{count}} overdue",
    dashOverdue_other: "{{count}} overdue",
    dashOldestReport: "oldest — No. {{number}} of {{date}}",
    dashPostOldest: "Post the oldest",
    dashDraftsNone: "No unfinished deliveries",
    dashDraftsLabel_one: "unfinished delivery",
    dashDraftsLabel_other: "unfinished deliveries",
    dashDraftHeld_one: "{{count}} on hold",
    dashDraftHeld_other: "{{count}} on hold",
    dashDraftMine: "Yours: {{supplier}}",
    dashDraftColleagues: "Colleagues: {{names}}",
    dashAllPaid: "All deliveries are paid",
    dashUnpaidLabel_one: "unpaid delivery",
    dashUnpaidLabel_other: "unpaid deliveries",
    dashOldestWaits_one: "the oldest has waited {{count}} day",
    dashOldestWaits_other: "the oldest has waited {{count}} days",
    dashWriteoffsNone: "No write-offs today",
    dashWriteoffsToday_one: "write-off today",
    dashWriteoffsToday_other: "write-offs today",
    dashTransfersNone: "No transfers today",
    dashTransfersToday_one: "transfer today",
    dashTransfersToday_other: "transfers today",
    dashAllWarehouses: "all warehouses",
    dashNoWarehouses: "No warehouses",
    dashNeverCounted: "{{warehouse}} has never been counted",
    dashCountedToday_one: "Counted today",
    dashCountedToday_other: "Every warehouse was counted today",
    dashDaysSinceCount_one: "day since the last one",
    dashDaysSinceCount_other: "days since the last one",
    dashStalestOf: "{{warehouse}} · the longest of {{total}} warehouses",
    dashNewStocktakeFor: "New stocktake · {{warehouse}}",
    dashShortageNone: "No sales surplus",
    dashShortageLabel_one: "product oversold",
    dashShortageLabel_other: "products oversold",
    dashInWarehouses: "in {{total}} warehouses",
    dashRulesNone: "No substitution rules",
    dashRulesLabel_one: "substitution rule",
    dashRulesLabel_other: "substitution rules",
    dashRulesHint: "used when posting and counting",
    dashCritical: "urgent",
    dashBannerLabel: "Urgent — waiting for action",
    dashStkUnfinished_one: "unfinished stocktake",
    dashStkUnfinished_other: "unfinished stocktakes",
    dashStkMore: "+ {{count}} more",
    dashTakeOver: "Take over",
    dashAlertOpen: "Open",
    dashPayOverdue_one: "{{count}} over 14 days",
    dashPayOverdue_other: "{{count}} over 14 days",
    dashAlertStocktake: "Unfinished stocktake: {{line}}",
    dashAlertOverdue_one: "{{count}} report has waited over 2 days — oldest #{{number}} from {{date}}",
    dashAlertOverdue_other: "{{count}} reports have waited over 2 days — oldest #{{number}} from {{date}}",
    dashAlertHeld_one: "{{count}} delivery on hold — problem with the goods",
    dashAlertHeld_other: "{{count}} deliveries on hold — problem with the goods",
    dashAlertOverduePay_one: "{{count}} delivery unpaid for over 14 days",
    dashAlertOverduePay_other: "{{count}} deliveries unpaid for over 14 days",
    // Invoice over delivery notes: the notes' lines in the worksheet and the document
    notesRowsBand: "Delivery note {{number}} · {{date}} · internal #{{id}}",
    notesRowsHint: "already in stock — the invoice only covers it",
    notesRowsCount_one: "{{count}} product from the note",
    notesRowsCount_other: "{{count}} products from the notes",
    docNoteSection: "From delivery note {{number}} · {{date}} · internal #{{id}}",
    docNotesLoading: "Loading the delivery notes' lines…",
    // New delivery without a supplier
    issueSupplier: "Select a supplier",
    saveDateFuture: "A delivery cannot be dated in the future — pick today or an earlier day",
    saveNegTitle: "Sold without stock",
    saveNegHead: "{{name}}: {{qty}} sold without stock on {{days}}.",
    saveNegWrittenOff: "Dated {{date}}, they are written off and all {{incoming}} come in.",
    saveNegCovered: "Dated {{date}}, the delivery will cover {{covered}} and {{rest}} will come in.",
    saveNegMixed: "Dated {{date}}, {{writtenOff}} are written off, the delivery will cover {{covered}} and {{rest}} will come in.",
    saveNegMoveDate: "If the goods arrived on {{day}}, set that date — then the delivery covers what was sold.",
    postingZeroHint: "Nothing is written off. Post it to take it off the pending list — it stays in \"Posted reports\" at 0.00.",
    sdDaysEmpty: "The recipe has no sales in the period",
    // "Correct the total" in the delivery
    tadjNetLabel: "Total net — type the document's amount; the difference goes to the line with the largest sum",
    tadjGrossLabel: "Total gross — type the document's amount; the difference goes to the line with the largest sum",
    tadjNoRows: "No line with a quantity and a sum can take the difference",
    tadjTooLarge: "The difference {{delta}} is larger than the line \"{{product}}\" — check the lines",
    tadjApplied: "{{delta}} of the total went to \"{{product}}\"",
    tadjConfirmTitle: "A difference of {{delta}} from the lines?",
    tadjConfirmText: "That is more than rounding. The sum of \"{{product}}\" becomes {{value}} and its price is derived from it.",
    tadjConfirmUndo: "You can fix it by typing the line's sum by hand",
    tadjConfirmOk: "Put it into \"{{product}}\"",
    tadjRowNote: "{{delta}} from correcting the total",
    // Write-off: the recipe's value and the ingredient shortage (wr6)
    wr6IssueShort: "Insufficient stock: {{items}}",
    wr6ShortItem: "{{name}} — needed {{needed}}, available {{available}}",
    wr6ShortItemFrom: "{{name}} — needed {{needed}}, available {{available}} (for {{recipes}})",
    wr6ShortMore_one: "and {{count}} more",
    wr6ShortMore_other: "and {{count}} more",
    wr6Needed: "needed",
    wr6Available: "available",
    wr6OwnShare: "here {{value}}",
    wr6NeededTotal: "needed in total {{value}}",
    wr6FitsLabel: "Enough for",
    wr6BandTitle: "Not enough for {{portions}}:",
    wr6FitAction: "Reduce to {{portions}}",
    wr6DialogTitle: "Not enough stock for “{{name}}”",
    wr6DialogText_one: "For {{portions}}, {{count}} ingredient is short — stock as of {{date}} in warehouse “{{warehouse}}”.",
    wr6DialogText_other: "For {{portions}}, {{count}} ingredients are short — stock as of {{date}} in warehouse “{{warehouse}}”.",
    wr6DialogCaption: "Short",
    wr6DialogFitHint: "That is what the stock covers alongside the other lines. Then save again.",
    wr6DialogSplitHint_one: "The recipe becomes {{count}} product line — correct its quantity or remove it.",
    wr6DialogSplitHint_other: "The recipe becomes {{count}} product lines — correct the short ones or remove them.",
    wr6DialogNoFit: "The ingredients do not cover even 1 portion alongside the other lines.",
    // Prepared recipes in a stocktake (sr6)
    sr6SectionTitle: "Prepared recipes",
    sr6SectionHint: "their ingredients are added to the count",
    sr6PortionsUnit: "port.",
    sr6PerPortion: "per portion:",
    sr6ContentsLoading: "Loading ingredients…",
    sr6ContentsError: "Ingredients failed to load",
    sr6ContentsEmpty: "No stock ingredients — remove the recipe",
    sr6RemoveRecipe: "Remove “{{name}}”",
    sr6RemoveCaption: "Leave the count",
    sr6InRecipesChip: "+{{qty}} in recipes",
    sr6InRecipesTitle: "In prepared recipes",
    sr6InRecipesPart: "{{recipe}} × {{portions}} — {{qty}}",
    sr6InRecipesTotal: "total found {{total}}",
    sr6CountLooseHint: "count it outside the recipe too (or 0)",
    sr6IssueCountLoose: "Count outside the recipe too (or 0): {{products}}",
    sr6IssuePortions: "Enter portions for: {{recipes}}",
    sr6IssueEmptyRecipe: "Recipe “{{name}}” has no stock ingredients — remove it",
    sr6BandPinnedRecipes: "(ingredients of prepared recipes)",
    sr6BandPinnedBoth: "(picked from the list and recipe ingredients)",
    sr6SearchPh: "Search a product or recipe to count…",
    sr6SearchAdded: "added",
    sr6CatalogHint: "Tick the products you will count now — they go to the top of the table in the order picked. Click a row to enter “Counted” right here — Enter moves to the next one. The “Recipes” group are prepared dishes — they go to “Prepared recipes” and their ingredients are added to the count.",
    sr6DocRecipes: "Prepared recipes",
    sr6DocInRecipes: "incl. {{qty}} in recipes",
    // Drafts of write-off, transfer and stocktake (round 6)
    dr6ContinueDraftNeut: "Continue the unfinished one",
    dr6DiscardAndNewNeut: "Delete and start new",
    dr6AskCaptionNeut: "Unfinished",
    dr6AskContinueHintNeut: "Opens the sheet with its lines.",
    dr6LockTextNeut: "{{name}} is editing it right now. If you take it over, {{first}}'s changes stop being saved and {{first}} will see that you took over.",
    dr6TakeOverFailed: "Could not take over the draft — try again",
    dr6DeleteDraftText: "The draft and all its lines are deleted. Nothing has been recorded in stock yet, so quantities do not change.",
    dr6NoWarehouse: "No warehouse",
    dr6WoDraftTitle: "Unfinished write-off",
    dr6WoDraftHint: "Saved automatically on the server too — colleagues and other devices see it. Everyone has one unfinished write-off.",
    dr6WoContinue: "Continue the write-off",
    dr6WoHasDraft: "You have an unfinished write-off",
    dr6WoAskTitle: "Continue the unfinished write-off?",
    dr6WoAskText: "Each user has only one unfinished write-off — to start a new one, this one has to be saved or deleted.",
    dr6WoLockTitle: "Take over {{name}}'s write-off?",
    dr6WoTakeOverToast: "You took over {{name}}'s write-off",
    dr6WoDeleteTitle: "Delete the unfinished write-off?",
    dr6WoPromptTitleBy: "Continue {{name}}'s unfinished write-off?",
    dr6TrxDraftTitle: "Unfinished transfer",
    dr6TrxDraftHint: "Saved automatically on the server too — colleagues and other devices see it. Everyone has one unfinished transfer.",
    dr6TrxContinue: "Continue the transfer",
    dr6TrxHasDraft: "You have an unfinished transfer",
    dr6TrxAskTitle: "Continue the unfinished transfer?",
    dr6TrxAskText: "Each user has only one unfinished transfer — to start a new one, this one has to be saved or deleted.",
    dr6TrxLockTitle: "Take over {{name}}'s transfer?",
    dr6TrxTakeOverToast: "You took over {{name}}'s transfer",
    dr6TrxDeleteTitle: "Delete the unfinished transfer?",
    dr6TrxPromptTitleBy: "Continue {{name}}'s unfinished transfer?",
    dr6StkDraftHint: "Saved automatically on the server too — colleagues and other devices see it until you save or delete the stocktake.",
    dr6StkAskText: "Each user has only one unfinished stocktake — to start a new one, this one has to be saved or deleted.",
    dr6StkLockTitle: "Take over {{name}}'s stocktake?",
    dr6StkTakeOverToast: "You took over {{name}}'s stocktake",
    dr6StkDeleteTitle: "Delete the unfinished stocktake?",
    dr6StkDeleteTitleLabel: "Delete the unfinished stocktake of \"{{label}}\"?",
    dr6DashDraftMine: "Your draft: {{label}}",
    // Stocktake draft: products + prepared recipes
    dr6StkCountedLines_one: "{{count}} counted line",
    dr6StkCountedLines_other: "{{count}} counted lines",
    sd6DeletedChip: "deleted",
    sd6DeletedHint: "The product was deleted in the organisation. Its history stays, but it is not offered for new documents.",
  },

  // Shared reports table (src/components/dataTable) — column menu, columns
  // panel, pagination and the totals row.
  dataTable: {
    hourShort: "h",
    minuteShort: "min",
    sortAsc: "Sort ascending",
    sortDesc: "Sort descending",
    hideColumn: "Hide column",
    clearColumnFilter: "Clear filter",
    columnMenuFor: "Column menu for {{column}}",
    filterTextHint: "Contains…",
    filterFrom: "from",
    filterTo: "to",
    filterSearch: "Search value",
    filterSelectAll: "All",
    filterClear: "Clear",
    filterNoValues: "No values",
    columns: "Columns",
    density: "Density",
    density_compact: "Compact",
    density_normal: "Normal",
    density_roomy: "Roomy",
    resetLayout: "Reset to default layout",
    pageRange: "{{from}}–{{to}} of {{total}}",
    goToPage: "Go to page {{page}}",
    perPage: "{{count}} per page",
    prev: "Back",
    next: "Next",
    emptyTitle: "No rows",
    emptyHint: "Clear the filters or widen the period.",
    totalsLabel: "Total · {{count}}",
    mobileControls: "Sort",
    mobileSortSection: "Sort by",
    mobileFilterSection: "Filters",
    clearAllFilters: "Clear all filters",
    columnMenuHint: "Sort, filter, hide the column",
    resizeColumn: "Resize column {{column}} (double-click resets the width)",
  },

  reportsPage: {
    kitchenMonitors: "Monitors",
    kmListKicker: "Rows by statuses",
    kmAvgKicker: "Average time",
    kmTable: "Table",
    kmStation: "Station",
    kmQty: "Qty",
    kmEnteredAt: "Received",
    kmStatusInProgress: "In progress",
    kmStatusCooked: "Cooked",
    kmStatusIssued: "Issued",
    kmStatusServed: "Served",
    kmType: "Type",
    kmStartedBy: "Started by",
    kmCookedBy: "Cooked by",
    kmIssuedBy: "Issued by",
    kmServedBy: "Served by",
    kmResultOne: "{{count}} row",
    kmResults: "{{count}} rows",
    kmNoData: "No status transitions in the selected period",
    // Sidebar
    reports: "Reports",
    exportExcel: "Export to Excel",
    exportPreparing: "Preparing file...",
    exportFailed: "Export failed",
    tableReports: "Control",
    generalReports: "Sales",
    reportsManagement: "Reports",
    fiscalReports: "Fiscal",
    categoriesReport: "By group",
    collapse: "Collapse",
    billsHistory: "Bills report",
    billsSplits: "Split bills",
    cancellations: "Cancellations",
    invoices: "Invoices",
    personalReports: "Personal reports",
    establishmentReports: "Establishment reports",
    assortmentReport: "By item",
    // In warehouse mode the sellable is a recipe → same report, different name.
    recipesReport: "By recipe",
    discountsReport: "Discounts",
    // The "Clients" report (client discounts and surcharges) — sidebar and title
    clientsReport: "Clients",
    priceChanges: "Price changes",
    priceChangesTitle: "Price changes",
    cardPayments: "Card payments",
    cardPaymentsTitle: "Card payments",
    cardPaymentsConsumedKicker: "WITH RECEIPT",
    cardPaymentsUnresolvedKicker: "PENDING",
    cardPaymentsVoidedKicker: "REVERSED",
    cardPaymentsResults: "{{count}} charges",
    cardPaymentsResultOne: "1 charge",
    cardPaymentsNoData: "No card charges for the period",
    cardPaymentsListKicker: "TERMINAL MOVEMENTS",
    cardPaymentsColTime: "Time",
    cardPaymentsColBill: "Bill",
    cardPaymentsColAmount: "Amount",
    cardPaymentsColStatus: "Status",
    cardPaymentsColPan: "Card",
    cardPaymentsColAuth: "Auth",
    cardPaymentsColReceipt: "Receipt #",
    cardPaymentsColMount: "Terminal",
    cardPaymentsColRefund: "Refunded (void)",
    cardPaymentsTerminal: "Till",
    cardPaymentsStatus_consumed: "With receipt",
    cardPaymentsStatus_voided: "Reversed",
    cardPaymentsStatus_unknown: "Unclear",
    cardPaymentsStatus_void_failed: "No receipt!",
    cardPaymentsMount_pc: "terminal at the till",
    cardPaymentsMount_fiscal: "terminal on the printer",
    fiscalCardPending: "Paid by card — do not charge again",
    priceChangesColKind: "Kind",
    priceChangesKindProduct: "Product",
    priceChangesKindAddition: "Addition",
    priceChangesOldPrice: "Old price",
    priceChangesNewPrice: "New price",
    priceChangesDelta: "Difference",
    priceChangesLineEffect: "Total difference",
    priceChangesActive: "Active",
    priceChangesReverted: "Reverted",
    priceChangesDirection: "Change type",
    priceChangesDown: "Lowered price",
    priceChangesUp: "Raised price",
    priceChangesCountKicker: "Changes in period",
    priceChangesCountTitle: "Changed prices",
    priceChangesDownTitle: "Total reduced",
    priceChangesUpTitle: "Total added",
    priceChangesListKicker: "Price changes list",
    priceChangesResults: "{{count}} changes",
    priceChangesResultOne: "{{count}} change",
    priceChangesNoData: "No price changes for the selected period.",
    waitersReport: "By waiter",
    stationsReport: "By station",
    dashboard: "Dashboard",
    dashboardSection: "Dashboard",
    dashboardTitle: "Dashboard",
    dashboardOpenTables: "Open Tables",
    dashboardOpenCount: "Table count",
    dashboardOpenAmount: "Total amount",
    dashboardClosedToday: "Closed Bills Today",
    dashboardClosedCount: "Bill count",
    dashboardCash: "Cash",
    dashboardCard: "Card",
    dashboardMixed: "Mixed",
    dashboardBills: "bills",
    dashboardDailyReports: "Daily Revenue (last 7 days)",
    dashboardNoReport: "No report",
    dashboardToday: "Today",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    dashboardBackToModules: "Back to modules",
    dashboardModuleLabel: "Reports module",
    dashboardAdminRole: "Administrator",
    dashboardRevenueByDays: "Revenue by day",
    dashboardLast7Days: "Last 7 days",
    dashboardDailyRevenue: "Daily revenue",
    dashboardAverage: "Average",
    dashboardNoRevenue: "No revenue",
    dashboardOpen: "Open",
    dashboardClosedTodayShort: "Closed today",
    dashboardTotalAmountLabel: "Total amount",
    dashboardBill: "bill",
    dashboardBillsMany: "bills",
    dashboardTableOne: "table",
    dashboardTableMany: "tables",
    dashboardTablesOpenNow: "Open now",
    dashboardTablesClosedToday: "Closed today",
    dashboardTablesOccupancy: "Open share",
    dashboardTablesNone: "No activity today",
    dashboardTotalShort: "Total",
    dashboardLogout: "Logout",
    dashboardSameTimeHint: "Compared up to the same hour of the day",
    dashboardAvgCheck: "Average check",
    dashboardAvgTableTime: "Avg. table time",
    dashboardVoidsToday: "Voids today",
    dashboardDiscountsToday: "Discounts today",
    dashboardMinShort: "min",
    dashboardHourShort: "h",
    dashboardPcs: "pcs",
    dashboardTopKicker: "Top 5 today",
    dashboardTopSubtitle: "Best sellers by revenue",
    dashboardTopEmpty: "No sales today",
    dashboardHeatKicker: "Busy hours",
    dashboardHeatSubtitle: "Closed bills by hour — last 8 days",
    dashboardHeatEmpty: "No data for the period",
    dashboardVsDay: "vs {{day}} {{date}}",
    dashboardVoidsHint:
      "Total value of voided products today (by time of void)",
    dashboardDiscountsHint:
      "Total value of all discounts — whole-bill and per-item — from today's closed bills",
    dashboardBillWithDiscount: "on 1 bill",
    dashboardBillsWithDiscount: "on {{count}} bills",
    dashboardStationsKicker: "Revenue by station",
    dashboardStationsSubtitle: "Today's split",

    // Common
    refresh: "Refresh",
    filters: "Filters",
    clear: "Clear",
    apply: "Apply",
    fromDate: "From date",
    toDate: "To date",
    totalRecords: "Total {{count}} records",
    totalReports: "Total {{count}} reports",
    totalLabel: "Total:",
    totalAmount: "Total amount",
    view: "View",
    noData: "No data",
    waiter: "Waiter",
    date: "Date",
    reportDate: "Report date",
    createdAt: "Creation date",
    amount: "Amount",
    fiscalAmount: "Amount FP",
    productName: "Product",
    category: "Category",
    station: "Station",
    quantity: "Quantity",
    averagePrice: "Average Price",
    totalProducts: "Total Products",
    totalQuantity: "Total Quantity",
    dateRange: "Date Range",
    total: "Total",

    // BillsHistory
    billsHistoryTitle: "Bills report",
    tables: "Tables",
    table: "Table",
    status: "Status",
    statusOpen: "Open",
    // Singular — the status of ONE bill (row dot, mobile card).
    statusOpenOne: "Open",
    statusClosedOne: "Closed",
    statusClosed: "Closed",
    opened: "Open",
    closed: "Closed",
    duration: "Duration",
    orders: "Orders",
    cancelledItems: "Cancelled",
    cancelledLabel: "Cancelled: {{count}}",
    payment: "Payment",
    paymentMethod: "Payment method",
    noTableData: "No table data",
    hours: "h",
    minutes: "min",
    cash: "Cash",
    card: "Card",
    billsHistoryActivityEmpty: "No activity in selected period",
    billsHistoryResults: "{{count}} results",
    billsHistoryResultOne: "{{count}} result",
    billsHistoryNoResults: "No bills match the selected filters",
    billsHistoryReceiptNumber: "Fiscal receipt",
    billsHistoryClearFilters: "Clear filters",

    // Redesign 0.0.218 — bento layout + shared data table.
    openMenu: "Open menu",
    durationShort: "Time",
    itemsShort: "Items",
    guestsCount: "Guests",
    billIdShort: "Bill ID",
    ordersShort: "Orders",
    cashShort: "Cash",
    cardShort: "Card",
    cancelledCountShort: "Voided qty",
    discount: "Discount",
    receiptNumber: "Receipt #",
    restored: "Restored",
    billsHistoryKpiBills: "Bills",
    billsHistoryKpiBillsUnit: "bills",
    billsHistoryKpiScopeNote:
      "The figures above cover the whole period — the status, payment and search filters change only the list and its Total row.",
    billsHistoryIsolateMarked: "Show only the bills carrying this mark",
    billsHistoryKpiRevenue: "Revenue",
    billsHistoryKpiRevenueSub: "closed bills",
    billsHistoryKpiAvgTicket: "Average bill",
    billsHistoryKpiAvgTime: "Average time at table",
    billsHistoryKpiAvgTimeSub: "per closed bill",
    billsHistoryHourlyTitle: "Bills per hour",
    billsHistoryDailyTitle: "Bills by day",
    billsHistoryDailyPeak: "peak {{day}} · {{count}} bills",
    billsHistoryByHour: "by hour",
    billsHistoryByDay: "by day",
    billsHistoryHourlyHint: "by closing hour",
    billsHistoryHourlyPeak: "by closing hour · peak {{from}}–{{to}} h",
    billsHistoryHourlyBar: "{{hour}}:00 · {{count}} pcs",
    billsHistoryComparePrevious: "Δ vs previous period",
    billsHistoryInvoicedOnly: "Invoiced only",
    billsHistoryTableKicker: "Bills",
    billsHistoryTableCount: "{{count}} bills",
    billsHistoryTableCountOne: "{{count}} bill",
    billsHistoryTableHint: "Click a row to open its operations history",
    billsHistorySearchPlaceholder: "Search table",
    billsHistoryEmptyHint:
      "Clear the filters, widen the period or search for another table.",

    // Dashboard 0.0.219 — period-aware bento layout.
    // Voids 0.0.220 — three views over the same rows.
    cardPaymentsRowOne: "{{count}} transaction",
    cardPaymentsRowMany: "{{count}} transactions",
    cardPaymentsListHint: "Click a row to open the bill",
    cardPaymentsEmptyHint: "No card terminal activity for this period.",
    cardPaymentsReconcileHint:
      "End-of-day reconciliation: \"With receipt\" must match the card line in the establishment report, \"With receipt + Pending\" must match the terminal's own end-of-day, and the bank sees only \"With receipt\" (voided ones cancel out).",
    cardPaymentsTerminalsKicker: "Per terminal",
    cardPaymentsTerminalsTitle: "{{count}} terminals",
    voidsViewList: "List",
    voidsByWaiter: "By server",
    voidsByProduct: "By product",
    voidsRowsKicker: "Voided rows",
    voidsInBills: "in {{count}} bills",
    voidsInBillsOne: "in {{count}} bill",
    voidsValue: "Value",
    voidsShareOfGross: "{{share}} % of closed revenue",
    voidsValueSub: "voided value",
    voidsAfterKitchen: "After kitchen",
    voidsRealLoss: "real product loss",
    voidsPerRow: "Average per row",
    voidsPerRowSub: "per voided row",
    voidsRows: "Voided rows",
    voidsTime: "Time",
    voidsCancelledBy: "Voided by",
    voidsScrap: "Scrap",
    voidsScrapShort: "Scrap",
    voidsBillOpenFailed: "The bill could not be opened",
    voidsScrapCount: "{{count}} scrap",
    voidsPlain: "Void",
    voidsSearchPlaceholder: "Table, product, waiter, reason…",
    voidsShareOfRevenue: "% of their turnover",
    voidsRowOne: "{{count}} row",
    voidsRowMany: "{{count}} rows",
    voidsListHint: "Click a row to open the bill",
    voidsWaiterHint: "Who voids how much against their own turnover",
    voidsProductHint: "What gets dropped most often",
    voidsEmptyTitle: "No voids for this period",
    voidsEmptyHint: "That is good news. Change the period or check the filters.",
    voidsBatchHint: "Voided together — {{count}} rows in one action",
    voidsBatchColumn: "Together",
    dashClosed: "Closed",
    dashBillsCount: "{{count}} bills",
    dashBillsCountOne: "{{count}} bill",
    dashAvgCheck: "Average bill",
    dashItemsCount: "{{count}} items",
    dashItemsCountOne: "{{count}} item",
    dashCashCard: "Cash / card",
    dashVoids: "Voided",
    dashVoidsCount: "{{count}} pcs",
    dashDiscounts: "Discounts",
    dashDiscountsSub: "{{count}} bills · {{share}} % of gross",
    dashDiscountsSubOne: "{{count}} bill · {{share}} % of gross",
    dashOpen: "Open",
    dashNoOpenTables: "no open tables",
    dashWaitersTitle: "Revenue by server",
    dashStationsTitle: "By station",
    dashTopTitle: "Top 5 by revenue",
    dashFullReport: "Full report",
    dashAllItems: "All items",
    dashPieces: "{{count}} pcs",
    dashNoData: "No data for this period",
    dashAlertsTitle: "Needs attention",
    dashAlertsNone: "nothing to check in this period",
    invoice: "Invoice",
    priceChangesSearchPlaceholder: "Search by product, table or user",
    priceChangesTableHint: "Click a row to open the operation history",
    priceChangesEmptyHint: "Change the period or clear the column filters.",
    kmRowsKpi: "Rows",
    kmRowsKpiSub: "with tracked transitions",
    kmTableHint: "durations are up to the first transition to the status",
    kmSearchPlaceholder: "Search table, item, operator…",
    kmEmptyHint:
      "Widen the period or check whether the monitors report transitions.",
    billsSplitsWaitersSub: "involved in the splits",
    billsSplitsProductsSub: "{{count}} transferred products",
    billsSplitsAvgSub: "value per split",
    billsSplitsRowOne: "{{count}} split",
    billsSplitsRowMany: "{{count}} splits",
    billsSplitsTableHint: "click a row to see the transferred products",
    billsSplitsSearchPlaceholder: "Search by table, waiter or comment",
    billsSplitsEmptyHint: "Change the period or clear the column filters.",
    billsSplitsComment: "Comment",
    billsSplitsNoItems: "No products recorded for this split",
    categoriesOpenItems: "Open the items in {{name}}",
    itemDaysSubtitle: "Revenue by day",
    itemDaysQuantity: "Quantity",
    itemDaysAvgPrice: "Average price",
    itemDaysDayOne: "{{count}} day with sales",
    itemDaysDaysMany: "{{count}} days with sales",
    itemDaysEmpty: "No sales in the selected period",
    itemDaysFailed: "The daily breakdown failed to load.",
    itemDaysClose: "Close",
    dashLoadFailed: "Could not load the data.",
    dashPaymentMixTitle: "How it was paid",
    dashPaymentMixEmpty: "No closed bills in this period.",
    dashPaymentOther: "Other method",
    dashAlertOpenLong: "Table {{table}} has been open {{time}}",
    dashAlertBigDiscount: "Discount {{percent}} % · table {{table}}",
    dashDaysTitle: "Revenue by working day",
    dashDaysHint: "{{range}} · the selected period is highlighted",
    dashDaysAverage: "average {{amount}}",
    dashThousandShort: "k",
    dashDaysEmpty: "No closed bills for {{range}}",
    periodYesterday: "Yesterday",
    periodToday: "Today",
    period7d: "7 days",
    periodThisMonth: "This month",
    periodPrev: "Previous period",
    periodNext: "Next period",

    // FiscalIssues
    fiscalIssues: "Fiscal issues",
    fiscalIssuesTitle: "Fiscal issues",
    fiscalIssuesSubtitle: "Bills and stornos without a confirmed fiscal receipt",
    fiscalIssuesListTitle: "Bills without a fiscal receipt",
    fiscalIssuesListSubtitle:
      "Closed bills whose fiscal receipt was not confirmed",
    fiscalIssuesTotal: "Total issues",
    fiscalIssuesFailed: "Rejected by printer",
    fiscalIssuesUnknown: "Unknown outcome",
    fiscalIssuesClosedAt: "Closed at",
    fiscalIssuesStatus: "Status",
    fiscalIssuesError: "Printer error",
    fiscalIssuesRetry: "Retry",
    fiscalIssuesRetrySuccess: "Fiscal receipt issued",
    fiscalIssuesStornoListTitle: "Storno without a document",
    fiscalIssuesStornoListSubtitle:
      "Bills with a fiscal receipt whose storno was not confirmed on paper",
    fiscalIssuesStornoRetrySuccess: "Storno document issued",
    fiscalIssuesReceiptNo: "Receipt no.",
    fiscalIssuesRetryTimeout:
      "The printer did not respond in time — check again in a minute",
    fiscalIssuesRetryError: "The printer returned an error",
    fiscalIssuesEmpty: "No fiscal issues for the selected period.",
    fiscalIssuesDays1: "Last 24 hours",
    fiscalIssuesDays7: "Last 7 days",
    fiscalIssuesDays30: "Last 30 days",
    fiscalStatus_failed: "Rejected",
    fiscalStatus_timeout: "Unknown",
    fiscalStatus_pending: "Waiting",
    fiscalStatusFailedHint:
      "The printer rejected the print — no receipt was issued. Fix the cause and retry.",
    fiscalStatusUnknownHint:
      "The result never reached the server — the document may already have printed. Check the printer's tape before retrying; a retry is safe only from the till the bill was closed on.",
    fiscalIssuesRetryUnknownTitle: "The document may already have printed",
    fiscalIssuesRetryUnknownBody:
      "The outcome is unknown. First check the printer's tape (journal) and \"Receipt no.\" in the bills report — if the document printed, do not retry. \"Retry\" here prints on YOUR session's printer; if that is a different device, a second document will be issued.",

    // BillsSplits
    billsSplitsTitle: "Split bills",
    totalSplits: "Total splits",
    transferredProducts: "Transferred products",
    fromTable: "From table",
    toTable: "To table",
    fromTableId: "From table (ID)",
    toTableId: "To table (ID)",
    products: "Products",
    noSplitsData: "No split bills data",
    billsSplitsSummaryKicker: "Splits in period",
    billsSplitsSummaryTitle: "Total count",
    billsSplitsWaiters: "Waiters",
    billsSplitsAmountTitle: "Total amount",
    billsSplitsAvg: "Avg per split",
    billsSplitsActivityKicker: "Activity",
    billsSplitsActivityHourly: "Splits by hour",
    billsSplitsActivityDaily: "Splits by day",
    billsSplitsActivityEmpty: "No activity in the selected period",
    billsSplitsListKicker: "Splits list",

    // Cancellations
    cancellationsTitle: "Cancellations",
    totalCancelled: "Total cancelled",
    defectiveItems: "Defective items",
    billId: "Bill ID",
    selectUsers: "Select users",
    defectiveOnly: "Defective items only",
    noCancellations: "No cancelled products",
    cancellationsSummaryKicker: "Cancellations in period",
    cancellationsSummaryTitle: "Total count",
    cancellationsAmountKicker: "Amount in period",
    cancellationsAmountTitle: "Cancelled value",
    cancellationsAvgLabel: "Avg per item",
    cancellationsActivityKicker: "Activity",
    cancellationsActivityHourly: "Cancellations per hour",
    cancellationsActivityDaily: "Cancellations per day",
    cancellationsActivityEmpty: "No cancellations in the selected period",
    cancellationsListKicker: "Cancellations list",
    cancellationsResults: "{{count}} records",
    cancellationsResultOne: "{{count}} record",
    cancellationsClearFilters: "Clear filters",
    cancellationsDefectiveLabel: "Defective",
    cancellationsRegularLabel: "Cancelled",

    // AssortmentReport
    assortmentProductsKicker: "Items in period",
    assortmentProductsTitle: "Unique products",
    assortmentQuantityKicker: "Sold quantity",
    assortmentQuantityTitle: "Sum across all items",
    assortmentAmountKicker: "Revenue in period",
    assortmentAmountTitle: "Total sales amount",
    assortmentAvgLabel: "Avg per item",
    assortmentListKicker: "Assortment list",
    assortmentResults: "{{count}} items",
    assortmentResultOne: "{{count}} item",
    assortmentClearFilters: "Clear filters",
    assortmentSearchPlaceholder: "Search item",
    assortmentNoItems: "No items in the selected period",
    // Row kind: standalone sale or sold addon
    assortmentColKind: "Type",
    assortmentKindAssortment: "Assortment",
    assortmentKindRecipe: "Recipe",
    assortmentKindAddon: "Addon",
    assortmentKindLunch: "Lunch menu",
    lunchTag: "Lunch",
    lunchScopeAll: "All",
    lunchScopeRegular: "Without lunch",
    lunchScopeLunch: "Lunch only",
    lunchScopeLabel: "Lunch menu",
    categoriesKindRegular: "Regular menu",
    bill: "Bill",
    product: "Product",
    qty: "Qty",
    unitPrice: "Unit price",
    comment: "Comment",
    defective: "✓ Defective",
    cancelled: "Cancelled",

    // PersonalReportsPage
    allUsers: "All users",
    noReportsFound: "No reports found",
    reportNumber: "No.",
    user: "User",
    bills: "Bills",
    cashAmount: "Cash",
    cardAmount: "Card",
    details: "Details",

    // DiscountsReport
    clientsTitle: "Clients",
    discountsBillId: "Bill",
    discountsClient: "Client",
    discountsColType: "Type",
    discountTag: "Discount",
    discountsType: "Discount type",
    discountsPercent: "Percent",
    discountsAmount: "Adjustment",
    discountsPaid: "Paid",
    discountsBillsCount: "Bills with discount",
    discountsTotalDiscount: "Total discount",
    discountsTotalPaid: "Total paid",
    discountsTotalOriginal: "Total amount",
    discountsNoData: "No discount data",
    discountTypeFixedPercent: "Fixed percent",
    discountTypeCashback: "Cashback",
    surchargeTag: "Surcharge",
    discountsBillsKicker: "Bills in period",
    discountsBillsTitle: "With discount or surcharge",
    discountsAmountKicker: "Discounts",
    discountsAmountTitle: "Total discounts given",
    discountsAmountAvgLabel: "Avg per bill",
    surchargesKicker: "Surcharges",
    surchargesTitle: "Total surcharges added",
    surchargesCountLabel: "Bills count",
    discountsPaidKicker: "Paid amount",
    discountsPaidTitle: "After adjustments",
    discountsPaidSharedLabel: "Share of total",
    discountsListKicker: "Discounts & surcharges list",
    discountsResults: "{{count}} bills",
    discountsResultOne: "{{count}} bill",
    discountsClearFilters: "Clear filters",

    // WaitersReport - Waiters report
    waitersTitle: "Waiters Report",
    methodCountColumn: "{{method}} · count",
    waitersCashCount: "Cash Count",
    waitersCardCount: "Card Count",
    waitersMixedCount: "Mixed Count",
    waitersTotalCash: "Total Cash",
    waitersTotalCard: "Total Card",
    waitersNoData: "No waiter data",
    waitersDetailTitle: "Details by date",
    waitersRevenueKicker: "Revenue in period",
    waitersRevenueTitle: "Total amount",
    waitersRevenueAvgLabel: "Avg per waiter",
    waitersSplitKicker: "Split",
    waitersSplitTitle: "By payment method",
    waitersListKicker: "Waiters list",
    waitersResults: "{{count}} waiters",
    waitersResultOne: "{{count}} waiter",
    waitersClearFilters: "Clear filters",

    // StationsReport - Stations report
    stationsTitle: "Stations Report",
    stationsName: "Station",
    stationsCashCount: "Cash Count",
    stationsCardCount: "Card Count",
    stationsMixedCount: "Mixed Count",
    stationsTotalCash: "Total Cash",
    stationsTotalCard: "Total Card",
    stationsRevenueKicker: "Revenue in period",
    stationsRevenueTitle: "Total amount",
    stationsRevenueAvgLabel: "Avg per station",
    stationsSplitKicker: "Split",
    stationsSplitTitle: "By payment method",
    stationsListKicker: "Stations list",
    stationsResults: "{{count}} stations",
    stationsResultOne: "{{count}} station",
    stationsClearFilters: "Clear filters",

    // Personal reports list
    personalTitle: "Personal reports",
    personalRevenueKicker: "Revenue in period",
    personalRevenueTitle: "Total amount",
    personalRevenueAvgLabel: "Avg per report",
    personalSplitKicker: "Split",
    personalSplitTitle: "By payment method",
    personalListKicker: "Reports list",
    personalResults: "{{count}} reports",
    personalResultOne: "{{count}} report",
    personalClearFilters: "Clear filters",
    personalBillsLabel: "Bills",

    // EstablishmentReportsPage
    personalReportsCount: "Personal reports",
    establishmentTitle: "Establishment report",
    establishmentRevenueKicker: "Revenue in period",
    establishmentRevenueTitle: "Total amount",
    establishmentRevenueAvgLabel: "Avg per report",
    establishmentSplitKicker: "Split",
    establishmentSplitTitle: "By payment method",
    establishmentListKicker: "Reports list",
    establishmentResults: "{{count}} reports",
    establishmentResultOne: "{{count}} report",
    billOpsBillKicker: "Bill",
    billOpsCardNoReceipt: "NO RECEIPT — do not charge again",
    billOpsCardRefunded: "refunded on void:",
    billOpsCardSection: "Card captures",
    billOpsCardVoided: "refunded to the card",
    billOpsCardWithReceipt: "with receipt",
    billOpsClient: "Customer: {{name}}",
    billOpsClose: "Close",
    billOpsDefective: "waste",
    billOpsDuration: "Duration {{value}}",
    billOpsEmpty: "No operations",
    billOpsEventAddItems: "Items added",
    billOpsEventCancel: "Void",
    billOpsEventClose: "Closed",
    billOpsEventPreliminaryBill: "Pre-bill printed",
    billOpsEventDiscountBill: "Bill discount",
    billOpsEventDiscountBillRemoved: "Bill discount removed",
    billOpsEventDiscountItem: "Item discount",
    billOpsEventDiscountItemRemoved: "Item discount removed",
    billOpsEventOpen: "Opened",
    billOpsEventPayment: "Payment",
    billOpsEventPriceChange: "Price changed",
    billOpsEventPriceRestored: "Menu price restored",
    billOpsEventRestore: "Bill restored",
    billOpsRestoreReceipt: "Receipt #{{number}} reversed",
    billOpsRestoreReport: "Excluded from personal report #{{number}}",
    billOpsRestoreReportBoth:
      "Excluded from personal report #{{number}} and establishment report #{{establishment}}",
    billOpsRestoredTimes_one: "restored {{count}} time",
    billOpsRestoredTimes_other: "restored {{count}} times",
    billOpsEventSplitIn: "Received from another bill",
    billOpsEventSplitOut: "Bill split",
    billOpsEventSurchargeBill: "Bill surcharge",
    billOpsEventSurchargeBillRemoved: "Bill surcharge removed",
    billOpsEventTransfer: "Guests moved",
    billOpsFullTransfer: "The whole bill #{{id}} was transferred",
    billOpsPaidCard: "Card",
    billOpsPaidCash: "Cash",
    billOpsReason: "Reason: {{reason}}",
    billOpsSectionOperations: "Operations",
    billOpsSector: "Sector: {{name}}",
    billOpsStatusClosed: "closed",
    billOpsStatusOpen: "open",
    billOpsTotalCancelled: "Voided",
    billOpsTotalDiscount: "Discount",
    billOpsTotalItems: "Items",
    billOpsTotalPaid: "Paid",
    billOpsTotalSurcharge: "Surcharge",
    billOpsTotalTransferred: "Transferred",
    categoriesColDelta: "Δ prev. period",
    categoriesCountKicker: "Groups",
    categoriesItemsSub: "units sold",
    categoriesTableCount: "{{count}} groups",
    categoriesTableCountOne: "{{count}} group",
    categoriesGroupColumn: "Group",
    categoriesTableHint: "click the name to see its items",
    dailyBillsMany: "{{count}} bills",
    dailyBillsOne: "{{count}} bill",
    dailyColTotal: "Total",
    dailyEmptyHint: "Change the period or check whether the reports have been issued.",
    dailyKpiReports: "Reports",
    dailyKpiReportsSub: "personal",
    dailyKpiRevenue: "Revenue",
    dailyListHint: "click a row to open the report",
    dailyOther: "Other",
    dailyReportsTitle: "Daily reports",
    dailySearchPlaceholder: "Search number or waiter",
    dailyTabEstablishment: "Establishment",
    dailyTabPersonal: "Personal",
    dailyWorkDay: "Working day",
    discountsBefore: "Before",
    discountsBillOpenFailed: "The bill could not be opened",
    discountsEmptyHint: "Change the period or clear the column filters.",
    discountsGiven: "Discount",
    discountsAdjustmentColumn: "Discount / surcharge",
    discountsKind: "Kind",
    discountsKpiAboveApproval: "Over {{percent}} %",
    discountsKpiAboveApprovalIsolated: "showing only these",
    discountsKpiAboveApprovalSub: "bills · need approval",
    discountsKpiAverage: "Average discount",
    discountsKpiAverageSub: "per bill",
    discountsKpiBills: "Bills with a discount",
    discountsKpiBillsShare: "{{share}} % of all bills",
    discountsKpiBillsUnit: "bills in the period",
    discountsKpiGiven: "Discounts given",
    discountsKpiGivenShare: "{{share}} % of gross",
    discountsKpiGivenSub: "for the period",
    discountsKpiSurcharges: "Surcharges",
    discountsKpiSurchargesNone: "none in this period",
    discountsSearchPlaceholder: "Search by bill, waiter or client",
    discountsTableHint: "Click a row to open the operation history",
    discountsTime: "Time",
    itemsAvgKicker: "Average per item",
    itemsAvgSub: "revenue / units",
    itemsCategoryFilterChip: "Category: {{name}}",
    itemsCategoryFilterClear: "Clear the category filter",
    itemsColCount: "Units",
    itemsColPrice: "Price",
    itemsColRevenue: "Revenue",
    itemsColShare: "Share",
    itemsEmptyHint: "Change the period or clear the column filters.",
    itemsRevenueKicker: "Item revenue",
    itemsRevenueSub: "as actually paid",
    itemsSoldKicker: "Units sold",
    itemsTableHint: "click a row to see its days",
    itemsTableKicker: "Items",
    itemsUniqueKicker: "Unique items",
    itemsUniqueSub: "with sales",
    stationsBillsColumn: "Bills",
    stationsCountKpi: "Stations",
    stationsCountKpiSub: "with revenue in the period",
    stationsDeltaColumn: "Δ prev. period",
    stationsDetailClose: "Close",
    stationsEmptyHint: "No closed bills with station items in the selected period.",
    stationsEmptyTitle: "No station revenue",
    stationsPageTitle: "Stations report",
    stationsRevenueColumn: "Revenue",
    stationsRevenueKpi: "Item revenue",
    stationsRevenueKpiSub: "from closed bills, after discounts",
    stationsRowMany: "{{count}} stations",
    stationsRowOne: "{{count}} station",
    stationsSearchPlaceholder: "Search station…",
    stationsShareColumn: "Share",
    stationsWaiters: "Waiters",
    stationsAllWaiters: "All waiters",
    stationsVoidsColumn: "Voids",
    stationsVoidsCount: "{{count}} pcs",
    stationsVoidsKpi: "Voids by station",
    voidsBatchChip: "×{{count}} together",
    waitersAvgBill: "Avg bill",
    waitersBillsMany: "{{count}} bills",
    waitersBillsOne: "{{count}} bill",
    waitersDayMany: "{{count}} days",
    waitersDayOne: "{{count}} day",
    waitersDaysHint: "Split by payment method",
    waitersDaysKicker: "By day",
    waitersDetailSubtitle: "Revenue by day for the selected period",
    waitersEmptyHint: "Change the period or check whether the bills are closed.",
    waitersEmptyTitle: "No closed bills in this period",
    waitersKicker: "Waiters",
    waitersOnShift: "{{count}} on shift",
    waitersRevenue: "Revenue",
    waitersRowHint: "Click opens the day-by-day detail",
    waitersSearchPlaceholder: "Search waiter…",
    waitersShare: "Share",
    dailyKpiReportsEstablishmentSub: "establishment",
    establishmentPreparedBy: "Prepared by",
    establishmentSearchPlaceholder: "Search number or author",
    invoicesCancelledKpi: "Cancelled",
    invoicesCancelledKpiSub: "included in the totals",
    invoicesColStatus: "Status",
    invoicesColType: "Type",
    invoicesEmptyHint: "Change the period or clear the search.",
    invoicesGrandKpi: "Total",
    invoicesGrandKpiSub: "incl. VAT",
    invoicesIssuedKpi: "Issued",
    invoicesIssuedKpiSub: "invoices in the period",
    invoicesStatusCancelled: "Cancelled",
    invoicesStatusIssued: "Issued",
    invoicesTableHint: "click opens the invoice",
    invoicesTaxableKpi: "Taxable base",
    invoicesTaxableKpiSub: "excl. VAT",
    invoicesVatKpi: "VAT",
    invoicesVatKpiSub: "charged tax",
    salesPeriodsReport: "Sales by periods",
    salesPeriodsPageTitle: "Sales by periods",
    salesPeriodsMode_grouped: "Grouped",
    salesPeriodsMode_timeline: "Over time",
    salesPeriodsUnit_hour: "Hours",
    salesPeriodsUnit_weekday: "Weekdays",
    salesPeriodsUnit_month: "Months",
    salesPeriodsUnit_date: "Dates",
    salesPeriodsUnit_year: "Years",
    salesPeriodsAllUsers: "All users",
    salesPeriodsUsers: "Users",
    salesPeriodsAllClients: "All clients",
    salesPeriodsClients: "Clients",
    salesPeriodsWithoutClient: "No client",
    salesPeriodsAllItems: "All items",
    salesPeriodsItems: "Items",
    salesPeriodsClearFilters: "Clear filters",
    salesPeriodsChartHint: "one bar = one table row",
    salesPeriodsTableKicker: "Periods",
    salesPeriodsRowOne: "{{count}} period",
    salesPeriodsRowMany: "{{count}} periods",
    salesPeriodsTableHint: "click a row to open its days",
    salesPeriodsSearchPlaceholder: "Search period…",
    salesPeriodsEmptyTitle: "No sales in this period",
    salesPeriodsEmptyHint: "Change the period or remove the filters.",
    salesPeriodsPeriodColumn: "Period",
    salesPeriodsBillsColumn: "Bills",
    salesPeriodsQuantityColumn: "Assortment count",
    salesPeriodsQuantityShort: "items",
    salesPeriodsRevenueColumn: "Amount",
    salesPeriodsVatColumn: "VAT",
    salesPeriodsNetColumn: "Net",
    salesPeriodsAvgPriceColumn: "Avg. price",
    salesPeriodsAvgBillColumn: "Average bill",
    salesPeriodsAvgBillShort: "avg.",
    salesPeriodsClientsColumn: "Clients count",
    salesPeriodsClientRevenueColumn: "Clients amount",
    salesPeriodsClientAvgColumn: "Clients average amount",
    salesPeriodsFiscalizedColumn: "Amount FP",
    salesPeriodsBaseColumn: "At base prices",
    salesPeriodsDiscountColumn: "Discounts",
    salesPeriodsMarkupColumn: "Surcharges",
    salesPeriodsDetailSubtitle: "{{bills}} bills · {{amount}}",
    methodAmountColumn: "{{method}} (as paid)",

    // --- Reservation reports (reservations/) ---
    reservationsReports: "Reservations",
    rvJournal: "Reservations log",
    rvOccupancy: "Occupancy",
    rvGuests: "Guests",
    rvStatus_upcoming: "Upcoming",
    rvStatus_seated: "Seated",
    rvStatus_completed: "Completed",
    rvStatus_cancelled: "Cancelled",
    rvStatus_expired: "No-show",
    rvKpiReservations: "Reservations",
    rvKpiReservationsSub: "upcoming {{upcoming}} · seated {{seated}}",
    rvKpiGuests: "Guests",
    rvKpiGuestsSub: "excluding cancelled and no-shows",
    rvKpiAverageParty: "{{value}} per reservation on average",
    rvKpiCompletedSub: "guests came and left",
    rvKpiCancelledSub: "cancelled before the time",
    rvKpiNoShowNone: "no no-shows",
    rvKpiNoShowRate: "{{value}} % no-show rate",
    rvKpiRevenue: "Revenue",
    rvKpiRevenueSub: "from the bills of seated guests",
    rvColumnTime: "Time",
    rvColumnPhone: "Phone",
    rvColumnGuests: "Guests",
    rvColumnTables: "Tables",
    rvColumnStatus: "Status",
    rvColumnName: "Name",
    rvColumnDuration: "Duration",
    rvColumnSeatedAt: "Seated at",
    rvColumnReleasedAt: "Left at",
    rvColumnAvgDuration: "Average duration",
    rvExportDurationMinutes: "Duration (min)",
    rvExportAvgDurationMinutes: "Average duration (min)",
    rvAllStatuses: "All statuses",
    rvStatuses: "Statuses",
    rvAllTables: "All tables",
    rvChartTitle: "Reservations by period",
    rvChartAverage: "average {{value}}",
    rvChartEmpty: "No reservations for the selected period and filters.",
    rvKpiPerPeriod: "Average per {{unit}}",
    rvKpiPerPeriodSub: "{{count}} periods in the table",
    rvPerUnit_hour: "hour",
    rvPerUnit_weekday: "weekday",
    rvPerUnit_month: "month",
    rvPerUnit_date: "day",
    rvPerUnit_year: "year",
    rvColumnStay: "Actual stay",
    rvColumnBill: "Bill",
    rvColumnCreatedBy: "Created by",
    rvColumnSeatedBy: "Seated by",
    rvSeatedByNoOrder: "No order yet",
    rvSeatedByNoOrderClosed: "No order",
    rvColumnNote: "Note",
    rvColumnCreatedAt: "Created at",
    rvColumnAvgStay: "Average actual stay",
    rvColumnNoShowRate: "No-show rate",
    rvColumnLastVisit: "Last visit",
    rvColumnLastReservation: "Last reservation",
    rvColumnFirstReservation: "First reservation",
    rvExportEnd: "End",
    rvExportStayMinutes: "Actual stay (min)",
    rvExportBillName: "Bill table",
    rvExportAvgStayMinutes: "Average actual stay (min)",
    rvJournalKicker: "Reservations by time",
    rvReservationOne: "{{count}} reservation",
    rvReservations: "{{count}} reservations",
    rvJournalHint: "the period is by reservation time",
    rvJournalSearchPlaceholder: "Search guest, phone, table…",
    rvNoData: "No reservations in the selected period",
    rvEmptyHint: "Widen the period or check whether the Reservations module is in use.",
    rvKpiGuestsCount: "Guests",
    rvKpiGuestsCountSub: "distinct phone numbers",
    rvKpiReservationsOfGuestsSub: "total in the period",
    rvKpiReturning: "Returning",
    rvKpiReturningSub: "with more than one reservation",
    rvKpiWithNoShow: "With no-shows",
    rvKpiWithNoShowSub: "at least one no-show",
    rvGuestsKicker: "Guests by phone",
    rvGuestOne: "{{count}} guest",
    rvGuestsMany: "{{count}} guests",
    rvGuestsHint: "the name comes from the guest's latest reservation",
    rvGuestsSearchPlaceholder: "Search name or phone…",
    rvGuestVisitsPeriod: "period {{period}}",
    rvGuestVisitsKpiVisits: "Visits",
    rvGuestVisitsOfReservationOne: "of {{count}} reservation",
    rvGuestVisitsOfReservationsMany: "of {{count}} reservations",
    rvGuestVisitsKpiAverageBill: "Average bill",
    rvGuestVisitsKpiAverageStay: "Average stay",
    rvGuestVisitsKpiCompletedSub: "over completed visits",
    rvGuestVisitsKpiRevenue: "Total spent",
    rvGuestVisitsKpiRevenueSub: "all bills in the period",
    rvGuestVisitsKpiCancelled: "Cancelled",
    rvGuestVisitsKpiNoShow: "No-shows",
    rvGuestVisitsKpiNoShowSub: "booked but did not come",
    rvGuestVisitsListTitle: "Reservations in the period",
    rvGuestVisitsEmpty: "This guest has no reservations in the selected period.",
    rvGuestVisitsFailed: "The guest history did not load. Try again.",
  },

  restoreBill: {
    title: "Restore bill",
    total: "Total: {{count}}",
    range30: "30 min",
    range60: "60 min",
    range120: "2 hours",
    noBills: "No closed bills in the selected period",
    restore: "Restore",
    fiscalize: "Fiscalize",
    changePaymentMethod: "Change payment",
    success: "Bill restored successfully",
    error: "Error restoring bill",
    cardWarning:
      "This bill was paid by card (RRN {{rrn}}). The storno prints as CASH — hand the amount back to the guest in cash from the till, not through the terminal.",
    cardWarningNoRrn:
      "This bill was paid by card. The storno prints as CASH — hand the amount back to the guest in cash from the till, not through the terminal.",
    cardRefundPlaceholder: "RRN, only if exceptionally refunded through the terminal (optional)",
  },

  language: {
    bg: "Български",
    en: "English",
  },

  changelog: {
    kicker: "VERSIONS",
    title: "Changelog",
    subtitle:
      "All versions, new features, fixes and improvements to the system.",
    currentVersion: "Current version",
    latest: "Latest",
    legend: "Change types",
    typeFeature: "New feature",
    typeFix: "Fix",
    typeImprovement: "Improvement",
    typeBreaking: "Breaking change",
    typeSecurity: "Security",
  },

  monitoring: {
    title: "Monitoring",
    subtitle: "Service access — audit, fiscal issues and AI assistant",
    auditLog: "Action audit log",
    auditLogDesc:
      "Records of every operation in the system — who changed what and when.",
    fiscalIssues: "Fiscal issues",
    fiscalIssuesDesc:
      "Bills and stornos without a confirmed fiscal receipt — review and retry.",
    assistant: "AI assistant",
    assistantDesc:
      "Questions about setup and problems — answered from the knowledge base and this restaurant's data.",
    assistantSubtitle: "Knowledge base and live restaurant data",
    assistantPlaceholder: "How do I… / Why doesn't…  (Enter — ask, Shift+Enter — new line)",
    assistantPlaceholderShort: "How do I… / Why doesn't…",
    assistantSend: "Ask",
    assistantNewConversation: "New conversation",
    assistantThinking: "Searching the knowledge base and data… {{seconds}} s",
    assistantEmptyTitle: "What should I check?",
    assistantEmpty:
      "Ask about a setting, a symptom, or a specific person, product, station or device.",
    assistantExample1: "Why can't Ivan see “Void”?",
    assistantExample2: "Why doesn't station Bar print?",
    assistantExample3: "Which bills today have no fiscal receipt?",
    confidenceConfirmed: "Confirmed",
    confidenceLikely: "Likely, unconfirmed",
    confidenceUnknown: "Don't know",
    confidenceClarify: "Needs clarification",
    assistantStatsSummary: "{{seconds}} s · {{rounds}} steps",
    assistantStatsCost: "~${{cost}}",
    assistantStatsTokens:
      "Input {{tokensIn}} (cache {{cacheRead}}) · output {{tokensOut}} · {{model}}",
    assistantStatsTools: "Tools and sections",
    assistantHelpful: "Was this helpful?",
    assistantVoteUp: "Helpful",
    assistantVoteDown: "Not helpful",
    assistantCommentPlaceholder:
      "What is wrong / what is the correct answer? (optional)",
    assistantCommentSend: "Send",
    assistantFeedbackSaved: "Feedback saved.",
    assistantFeedbackError: "Feedback was not saved.",
    assistantNotRecorded: "The answer was not recorded — feedback is not possible.",
    assistantErrNotConfigured:
      "The AI assistant is not configured on the server (OpenAI key missing).",
    assistantErrExpired: "The conversation expired — starting a new one. Please ask again.",
    assistantErrTimeout: "The answer took too long — ask a more specific question.",
    assistantErrUpstream: "OpenAI did not respond — try again in a moment.",
    assistantErrForbidden: "Only the UnrealSoft service account has access.",
    assistantErrGeneric: "The question failed — please try again.",
  },

  auditLog: {
    title: "Activity log",
    subtitle: "Who did what, when — across every module.",
    searchPlaceholder: "Search across all fields…",
    noResults: "No records found",
    emptyHint:
      "Widen the period or drop one of the filters. Reads (GET) stay hidden until you turn them on.",
    tableKicker: "LOG",
    tableCount: "{{count}} records",
    tableCountOne: "{{count}} record",
    tableHint: "click opens the full record",
    statusClass: {
      "2xx": "Successful (2xx)",
      "4xx": "Client error (4xx)",
      "5xx": "Server error (5xx)",
    },
    col: {
      timestamp: "Time",
      outcome: "Outcome",
      operation: "Action",
      module: "Module",
      impact: "Kind",
      table: "Table",
      billId: "Bill",
      user: "User",
      userId: "User id",
      method: "Method",
      status: "Status",
      duration: "Duration",
      path: "URL",
      entryId: "Record id",
    },
    module: {
      ordering: "Marking",
      cash: "Cash register",
      reports: "Reports",
      invoicing: "Invoices",
      inventory: "Inventory",
      configuration: "Configuration",
      access: "Access",
      system: "System",
    },
    impact: {
      money: "Money",
      settings: "Settings",
      data: "Data",
      read: "Read",
    },
    outcome: {
      ok: "Successful",
      failed: "Receipt was not printed",
      rejected: "Rejected",
      error: "Server error",
    },
    quick: {
      problems: "Problems only",
      money: "Money",
      settings: "Settings",
      slow: "Slower than {{ms}} ms",
      reads: "Include reads (GET)",
      live: "Live",
    },
    stats: {
      actions: "ACTIONS",
      actionsSub: "changes: {{writes}} · reads: {{reads}}",
      problems: "PROBLEMS",
      problemsSub: "failed or rejected",
      fiscalFailedSub: "incl. {{count}} unprinted receipts",
      fiscalFailedSubOne: "incl. 1 unprinted receipt",
      money: "MONEY",
      moneySub: "payment, void, discount, cash",
      settings: "SETTINGS",
      settingsSub: "configuration and devices",
      people: "PEOPLE",
      lastAt: "last at {{when}}",
      slowest: "SLOWEST",
      avg: "avg {{value}}",
      byHour: "BY HOUR",
      byDay: "BY DAY",
      barsHint: "click narrows the period",
      barTitle: "{{when}} · actions: {{count}} · problems: {{problems}}",
    },
    breakdown: {
      byModule: "BY MODULE",
      byUser: "BY USER",
      clickHint: "click narrows the list",
      anonymous: "No user",
    },
    trace: {
      kicker: "TRACE",
      billTitle: "Bill #{{id}} · table {{table}}",
      tableTitle: "Table {{name}}",
      billHint: "Trace bill #{{id}}",
      tableHint: "Trace table {{name}}",
      count: "{{count}} records",
      countOne: "{{count}} record",
      periodOff: "period is off — the whole life is shown",
      exit: "Exit",
    },
  },

  auditLogDetail: {
    request: "Request",
    response: "Response",
    headers: "Headers",
    query: "Query string",
    body: "Body",
    empty: "None",
    getNoBody: "GET requests have no body — data lives in the query string",
    copy: "Copy",
    copied: "Copied!",
    togglePretty: "Pretty-print",
    toggleRaw: "Single line",
    loadError: "Failed to load details",
    truncatedTitle: "Payload was too large",
    truncatedBody:
      "Body was {{size}} KB. We didn't store the whole thing to avoid bloating the database (limit: 10 KB).",
    truncatedBodyUnknown:
      "Body exceeded the limit (10 KB) and wasn't stored in full.",
    human: {
      rawTitle: "Technical details (raw record)",
      summaryKicker: "What happened",
      who: "User",
      when: "Time",
      table: "Table",
      noDetails:
        "This operation has no detailed human view — see the technical details below.",
      total: "Total",
      product: "Product",
      products: "Products",
      unknownProduct: "Unknown product",
      method: "Method",
      modifier: "Modifier",
      discount: "Discount",
      percent: "Percent",
      client: "Client",
      discountType: "Discount type",
      removedDiscountNote: "The discount was removed from the bill.",
      cancelled: "Cancelled",
      reason: "Reason",
      defective: "Defective",
      yes: "Yes",
      no: "No",
      cancelledAllNote: "The entire bill was voided.",
      paymentMethod: "Payment method",
      cash: "Cash",
      card: "Card",
      paidTotal: "Amount paid",
      addedAmount: "Amount added",
      restoredNote: "The closed bill was reopened (same id).",
      targetTable: "To table",
      movedItems: "Items moved",
      movedProducts: "Moved products",
      newName: "New name",
      waiter: "Waiter",
      cashIn: "Deposited",
      cashOut: "Withdrawn",
      successLabel: "Success",
      failedLabel: "Failed",
      prevOp: "Previous operation on this bill",
      nextOp: "Next operation on this bill",
      timelineTitle: "Bill timeline",
      billLabel: "Bill",
      operationsWord: "operations",
      flagDiscountReverted: "Discount applied and then removed",
      flagCancelAfterClose: "Void after the bill was closed",
      flagFailed: "The operation failed",
      // Bill: removed line discount and price changes
      removedItemDiscountNote: "The discount on this product was removed.",
      newPrices: "New prices",
      itemDiscounts: "Item discounts",
      additionLine: "addon",
      // Warehouse
      recipe: "Recipe",
      documentNo: "Document",
      warehouse: "Warehouse",
      fromWarehouse: "From warehouse",
      toWarehouse: "To warehouse",
      supplier: "Supplier",
      invoiceNumber: "Invoice no.",
      deliveryValue: "Delivery value",
      writeoffKind: "Type",
      writeoffKindScrap: "Scrap",
      writeoffKindNormal: "Write-off",
      substitutions: "Substitutions",
      countedProducts: "Counted products",
      postedProducts: "Posted products",
      reportNo: "Report",
      hint: "Hint",
      coefficient: "Coefficient",
      groupMembers: "Products in the group",
      vatRegistered: "VAT registered",
      defaultWarehouse: "Default warehouse",
      productWarehouse: "The product's own warehouse",
      productWarehouseRemovedNote:
        "The product's own warehouse is removed — from now on the station's warehouse decides.",
      restoredDocument: "Restored document",
      deliveryNotes: "Delivery notes",
      closeReason: "Reason",
      markedPaid: "Marked as paid",
      detachedNotesNote: "The delivery notes were taken out of their invoice; their stock stays received.",
      closedNotesNote: "The delivery notes were closed without an invoice — no invoice is awaited for them any more.",
      reopenedNotesNote: "The delivery notes are awaiting an invoice again.",
      restoredDocumentNote: "A cancelled warehouse document was restored.",
      cancelledDocumentNote: "The document was cancelled.",
      deletedGroupNote: "The interchangeable products group was deleted permanently.",
      truncatedBodyNote:
        "The request body was too large to store — open the document in Warehouse.",
      // Configuration, printing, devices, voice
      newValue: "New value",
      turnedOn: "On",
      turnedOff: "Off",
      invoiceNo: "Invoice",
      result: "Result",
      errorLabel: "Error",
      requestId: "Request id",
      device: "Device",
      deviceUpdateNote:
        "A command was sent for the register to update to the latest FiscalAPI version.",
      fiscalRetryNote: "Retry of the fiscal receipt.",
      stornoRetryNote: "Retry of the credit note.",
      recognizedProducts: "Recognised products",
      voiceAmbiguous: "ambiguous, candidates",
      voiceNoMatch: "no match in the menu",
      // Reservations
      reservationGuest: "Guest",
      reservationTables: "Tables",
      reservationTime: "Time",
      reservationGuests: "Guests",
      reservationDuration: "Duration",
      reservationBill: "Bill",
      reservationBillNew: "No. {{id}} — new empty bill",
      reservationBillAttached: "No. {{id}} — attached to an open bill",
      reservationBills: "Bills",
      reservationBillsSeparate: "{{ids}} — a separate bill for each table",
      reservationReleasedNote:
        "The guests left — the reservation is completed; the bill stays open until it is closed in Ordering.",
      reservationUnseatedNote:
        "Seating undone — the reservation is upcoming again; the bill was not touched.",
      reservationUnseatedBillClosedNote:
        "Seating undone — the reservation is upcoming again; the empty bill opened by the seating (no orders) was removed.",
      reservationCancelledNote: "The reservation was cancelled — the tables are free.",
      reservationRestoredNote: "The reservation is back in upcoming.",
      reservationOrigin: "Source",
      reservationOriginEdge: "The till, without internet — uploaded at sync",
    },
  },

  push: {
    menuLabel: "Notifications",
    settingsTitle: "Notifications",
    settingsSubtitle:
      "Kitchen notifications when orders are cooked or served out.",
    toggleLabel: "Receive notifications on this device",
    unsupportedHint:
      "This browser does not support notifications. On iPhone the app must first be installed on the home screen (Share → “Add to Home Screen”).",
    permissionDeniedHint:
      "Notifications are blocked by the browser for this site. Allow them in the browser's site settings and try again.",
    enableError: "Could not enable notifications. Please try again.",
    disableError: "Could not disable notifications. Please try again.",
    deviceTitle: "Device notifications",
    deviceSubtitle: "Who receives notifications on this terminal.",
    deviceEmpty: "No one is subscribed to notifications on this device.",
    remove: "Remove",
    removeError: "Removal failed. Please try again.",
  },
  // Local fallback — working against the main till without internet (docs/edge.md)
  edge: {
    bannerTitle: "Local mode",
    bannerText: "no internet, you are working against the till",
    bannerSyncing:
      "internet is back — waiting for the till to send what was done offline",
    bannerRestricted: "log in with a code or a password",
    bannerUnresolved_one:
      "{{count}} action has not reached the cloud — check the till",
    bannerUnresolved_other:
      "{{count}} actions have not reached the cloud — check the till",
    restrictedRequest: "Local mode — log in with a code or a password",
    returnedToPin:
      "Internet is back and work continues against the cloud — log in again with your code.",
    loginWithPassword: "Log in with a username and password",
    unavailableOffline: "Unavailable without internet",
    panelKicker: "Offline fallback",
    panelTitle: "Local fallback",
    stateReady: "Ready",
    stateUnreachable: "No access",
    stateDisabled: "Disabled",
    stateUnknown: "Not checked",
    stateNoMaster: "Till not connected",
    stateLocalNow: "In local mode",
    // The "Offline fallback" panel has two parts (Configuration → Devices):
    // the switch is for the WHOLE site, preparation is per device.
    sectionSite: "The site",
    sectionDevice: "This device",
    stateEnabled: "Enabled",
    siteEnabledHint:
      "If the internet drops, every prepared terminal and monitor continues against the main till.",
    siteOnceHint:
      "There is one fallback for the whole site — it is switched on once, from any terminal. Switching it off also applies to all.",
    deviceNeedsSite:
      "First enable the fallback for the site — then prepare each device separately.",
    disableTitle: "Switch off the offline fallback",
    disableQuestion:
      "The fallback is switched off for the WHOLE site — if the internet drops, every terminal stops, not just this one. Switch it off?",
    disableConfirm: "Yes, switch off for the site",
    compactSummary: "Enabled for the site · this device is ready to work without internet",
    showDetails: "Details",
    hideDetails: "Hide",
    hintReady:
      "The browser reaches the main till at {{address}}. If the internet drops, ordering, kitchen tickets and closing continue against it.",
    hintUnreachable:
      "The browser cannot reach the till at {{address}}. Allow local network access (Chrome/Edge), make sure FiscalAPI on the main till has the fallback enabled, and press “Check”.",
    hintNoMaster:
      "No FiscalAPI installation announces a fallback. Enable it in the main till's portal and wait for it to announce itself.",
    hintDisabled:
      "If the internet drops, the terminals stop. Enable the fallback so ordering continues against the main till.",
    hintUnknown:
      "Not yet checked whether the browser reaches the till — press “Check”.",
    hintLocalNow:
      "This terminal is currently working against the till. It returns to the cloud automatically once the internet is back and the till has sent its journal.",
    hintUnresolved_one:
      "The till is holding {{count}} action the cloud refused — it goes up only after a person acts at the till (“Try again” or “Write off” in its portal). Until then the terminals stay in local mode so that nothing done offline is lost.",
    hintUnresolved_other:
      "The till is holding {{count}} actions the cloud refused — they go up only after a person acts at the till (“Try again” or “Write off” in its portal). Until then the terminals stay in local mode so that nothing done offline is lost.",
    cloudModeLocal:
      "The cloud considers the restaurant “in local mode” — closing is possible only from the till until it sends its journal.",
    forceCloudHint:
      "“Return to cloud” only if the till is down and has nothing to send — otherwise what was done offline will arrive late.",
    // Readiness of the SITE, not of the till: the fallback is switched on once,
    // but local network access is granted on every device separately.
    clientsSeen_one: "{{count}} device has used it in the last 24 h",
    clientsSeen_other: "{{count}} devices have used it in the last 24 h",
    clientsSeenNone:
      "No device has used it in the last 24 h — if the internet drops, all of them go quiet. Prepare every terminal and monitor from the button on its own screen.",
    presenceStale: "The till is silent — last seen at {{time}}.",
    pendingEvents_one:
      "The till is holding {{count}} event it has not sent yet — it goes up on its own.",
    pendingEvents_other:
      "The till is holding {{count}} events it has not sent yet — they go up on their own.",
    // The button that raises the browser prompt (it only comes on a human
    // action) and pulls in one go what the screen will need without internet.
    // Shown next to the browser prompt when it comes up on its own at the
    // first touch (`lib/edge/edgePermission.ts`): a refusal is final, and
    // Chrome's own text does not say why this device needs the till.
    lnaPrompt:
      "The browser is asking for access to the till on the local network — press “Allow” so this device keeps working without internet.",
    lnaDenied:
      "Access to the till on the local network is denied on this device — allow it from the icon to the left of the address, otherwise the screen goes quiet without internet.",
    prepareDevice: "Prepare this device",
    prepareDeviceHint:
      "Asks for local network access and downloads the data this screen will use without internet.",
    preparing: "Preparing…",
    prepareReady: "Done — this device will work offline.",
    prepareBlocked:
      "The browser refused local network access — allow it from the icon to the left of the address and press again.",
    prepareDisabled:
      "The local fallback is switched off for this site — enable it in Configuration → Devices.",
    prepareNoMaster:
      "The till has not announced an address yet — enable the fallback in its portal and try again.",
    prepareLocalNow:
      "This screen is already working against the till — there is nothing to prepare.",
    masterClientId: "Main till",
    address: "Address",
    check: "Check",
    checking: "Checking…",
    enable: "Enable",
    disable: "Disable",
    forceCloud: "Return to cloud",
    updateError: "The setting was not saved",
    // Titles ("kicker") of the banner — one per state. They live here too, not
    // only as a `defaultValue` in the component: otherwise an English screen
    // shows Bulgarian in the middle of the sentence.
    linkTitleSlow: "Slow connection",
    linkTitleSuspect: "Checking the connection",
    linkTitleDeviceOffline: "Only this device",
    linkTitleAppDown: "The cloud is updating",
    linkSlow: "The connection is slow — hold on, the request is still running.",
    linkSuspect: "Checking the connection to the cloud…",
    linkDeviceOffline:
      "Only this device is offline — the till is online. Check its Wi-Fi; the others are working normally.",
    linkAppDown:
      "The cloud is not answering, but the network is fine — it is most likely updating. Please wait.",
    linkLocal: "Local mode — working against the till.",
    tillBackTerminalBlind:
      "The till is back on the cloud, but this device still has no internet — new actions will be refused. Check the Wi-Fi on this terminal.",
    linkLostNoFallback:
      "No connection to the cloud, and the local fallback is off for this venue — turn it on from Configuration → Devices.",
    forceLocal: "Switch to local mode",
    forceLocalHint:
      "Freezes the WHOLE venue against the till — every terminal starts working against it. Use it only when the venue is stuck and the automatics have not switched.",
    forceLocalQuestion:
      "The whole venue switches to working against the till. Switch now?",
    forceLocalConfirm: "Yes, switch to local mode",
    forceLocalEntered: "Done — the terminals are working against the till.",
    forceLocalNoPermission:
      "You are not allowed to switch the mode — call the person responsible for the till.",
    forceLocalDisabled:
      "The local fallback is off for this venue — turn it on from Configuration → Devices.",
    forceLocalNoTill: "The till has not announced an address — there is nothing to switch to.",
    forceLocalFailed:
      "The till did not take over — check that FiscalAPI on the main till is running and try again.",
    // The till is HELD in local mode by a manual decision (§19.5).
    bannerManualHold: "the till is in local mode by a manual decision",
    bannerManualHoldCloudBack:
      "internet is back, but the till is held in local mode by hand — return it to the cloud",
    releaseLocal: "Return to the cloud",
    releaseLocalHint:
      "The till sends what was done offline and the whole venue returns to the cloud. Do it only if the internet works on the terminals.",
    releaseLocalQuestion: "Return the venue to the cloud now?",
    releaseLocalConfirm: "Yes, return to the cloud",
    releaseLocalFailed:
      "Returning did not work. Try again or do it from the till.",
  },
  lunchMenu: {
    title: "Lunch menu",
    tag: "Lunch",
    backToTables: "Back to tables",
    kickerToday: "Today",
    kickerUpcoming: "Upcoming",
    kickerPast: "Past",
    statusLive: "Active now",
    statusToday: "Later today",
    statusTomorrow: "Tomorrow",
    statusUpcoming: "Upcoming",
    statusRecurring: "Recurring",
    statusPast: "Expired",
    newMenu: "New lunch menu",
    edit: "Edit",
    copy: "Copy",
    print: "Print",
    finishNow: "End now",
    delete: "Delete",
    colProduct: "Product",
    colSize: "Size",
    colPortions: "Portions/day",
    colMain: "Regular",
    colLunch: "Lunch",
    changedSizeHint: "Struck through is the product value, next to it — the lunch one. The product itself is unchanged.",
    createdBy: "Created by {{name}}",
    productsCount_one: "{{count}} product",
    productsCount_other: "{{count}} products",
    pickMenu: "Pick a menu from the list.",
    missingProduct: "The product is no longer on the menu",
    everyWeekdays: "every {{days}}",
    everyDay: "every day",
    periodOpenEnded: "from {{from}}",
    defaultName: "Lunch · {{day}} · {{date}}",
    creatingTitle: "Creating a lunch menu",
    editingTitle: "Editing a lunch menu",
    emptyTitle: "No lunch menus yet",
    emptyText: "Create the first one — pick products from the catalog, give them a lunch price and choose when it shows in Ordering.",
    weekdayShort0: "Sun",
    weekdayShort1: "Mon",
    weekdayShort2: "Tue",
    weekdayShort3: "Wed",
    weekdayShort4: "Thu",
    weekdayShort5: "Fri",
    weekdayShort6: "Sat",
    weekdayLong0: "Sunday",
    weekdayLong1: "Monday",
    weekdayLong2: "Tuesday",
    weekdayLong3: "Wednesday",
    weekdayLong4: "Thursday",
    weekdayLong5: "Friday",
    weekdayLong6: "Saturday",
    dayButton0: "Su",
    dayButton1: "Mo",
    dayButton2: "Tu",
    dayButton3: "We",
    dayButton4: "Th",
    dayButton5: "Fr",
    dayButton6: "Sa",
    confirmDeleteTitle: "Delete lunch menu",
    confirmDeleteMessage: "“{{name}}” will disappear from the list. Its sales stay in the reports.",
    confirmFinishTitle: "End lunch menu",
    confirmFinishMessage: "“{{name}}” stops right away and moves to “Past”.",
    confirmFinish: "End",
    saved: "Lunch menu saved",
    deleted: "Lunch menu deleted",
    finished: "Lunch menu ended",
    loadError: "The lunch menu could not be loaded",
    editorKicker: "Lunch menu",
    products: "Products",
    priceColumnsHint: "lunch / regular price",
    emptySelection: "Pick products from the catalog on the left.",
    emptySelectionNarrow: "Pick products from the catalog.",
    save: "Save",
    reviewAndSave: "Review and save",
    scheduleChange: "Change",
    scheduleSingleDate: "single date",
    scheduleTitle: "When it is active",
    scheduleSubtitle: "Outside this period the menu is hidden in Ordering.",
    name: "Name",
    modeSingle: "Single date",
    modeRecurring: "Recurring",
    date: "Date",
    today: "Today",
    tomorrow: "Tomorrow",
    weekdays: "Days of the week",
    period: "Period",
    periodFrom: "from",
    periodTo: "to",
    noEndDate: "no end date",
    hours: "Hours",
    done: "Done",
    errorName: "Enter a menu name.",
    errorItems: "Pick at least one product.",
    errorTime: "The start time must be before the end time.",
    errorWeekdays: "Pick at least one day of the week.",
    errorPeriod: "The end date is before the start date.",
    errorPastDate: "The date must be today or later.",
    errorFarDate: "The date can be at most 2 years ahead.",
    priceTooHigh: "At most {{max}}",
    reorderEnded: "The lunch menu has ended — this product can't be reordered from it.",
    orderEnded: "The lunch menu has ended — its products switched to the regular price. Check the order and send it again.",
    recipeSizeFixed: "You work with a warehouse — the quantity is the recipe's and cannot change. The lunch menu changes only the price.",
    cartSwitchedToRegular_one: "The lunch menu has ended — 1 product in the order now has the regular price.",
    cartSwitchedToRegular_other: "The lunch menu has ended — {{count}} products in the order now have the regular price.",
    modalClosedEnded: "The lunch menu has ended — the lunch product window was closed.",
    cartRemoved_one: "1 lunch product is no longer in the menu and was removed from the order.",
    cartRemoved_other: "{{count}} lunch products are no longer in the menu and were removed from the order.",
    errorPrice: "Check the prices — one is empty or invalid.",
    dailyLimitTitle: "Portions per day",
    dailyLimitInvalid: "A whole number from 0 to {{max}}",
    dailyLimitHint: "0 = no limit · e.g. 20 → after 20 sold in a day it shows “Sold out”",
    dailyLimitShort: "{{count}}/day",
    dailyLimitUnlimited: "No limit",
    errorDailyLimit: "Check the portions per day — a whole number from 0 to 9,999.",
    countLeft: "{{count}} left",
    tagSoldOut: "Sold out",
    remainingTitle_one: "{{count}} portion left today",
    remainingTitle_other: "{{count}} portions left today",
    limitSoldOut: "“{{name}}” from the lunch menu is sold out for today.",
    limitOnlyLeft_one: "“{{name}}” — only {{count}} portion left today from the lunch menu.",
    limitOnlyLeft_other: "“{{name}}” — only {{count}} portions left today from the lunch menu.",
    limitReachedOrder: "Not enough lunch portions: {{items}}. The count is refreshed — lower the quantity.",
    limitReachedItem: "“{{name}}” ({{count}} left)",
    lunchPrice: "Lunch price",
    mainPriceSubtitle: "regular price {{price}}",
    resetPrice: "Restore the regular price",
    mainPriceShort: "regular",
    sizeTitle: "Unit and size",
    byLanguage: "per language",
    inAssortment: "product: {{value}}",
    fromAssortment: "empty → from the product",
    sizeScopeHint: "The change applies to this lunch menu only — the product is not touched.",
    remove: "Remove",
    apply: "Apply",
    overlapTitle: "Another lunch menu covers this period",
    overlapMessage: "“{{name}}” ({{schedule}}) overlaps with the new one. Only one lunch menu can be active at a time.",
    finishOld: "End the old one",
    finishOldHint: "The old one stays valid until {{endDate}} inclusive, because the new one starts on {{startDate}} and the two can’t be active at the same time. It then moves to “Past”.",
    finishOldNowHint: "It stops right away and stays under “Past” — the new one applies from now.",
    deleteOld: "Delete the old one",
    deleteOldHint: "It disappears from the list. Its sales stay in the reports.",
    finishAndSave: "End and save",
    deleteAndSave: "Delete and save",
    printPricesNote: "Prices are in euro, VAT included.",
    printAllergensTitle: "Allergens",
  },
  // "Seat" from Ordering (src/pages/ordering/components/tables/seat/).
  seatReservation: {
    action: "Seat",
    title: "Seat a reservation",
    lateShort: "−{{duration}}",
    soonShort: "in {{duration}}",
    now: "now",
    tables_one: "Table {{tables}}",
    tables_other: "Tables {{tables}}",
    emptyHint: "Today's reservations will appear here.",
    noResults: "No reservations match the search",
    loadError: "The reservations could not be loaded",
    retry: "Try again",
    noTable: "The reservation's tables were removed from the layout",
    expired: "The reservation's time is over",
    notUpcoming: "The reservation is no longer upcoming",
    seat: "Seat",
    takenBy: "Table {{table}} is taken by {{waiter}}",
    takenByOther: "Table {{table}} is taken by another waiter",
    // The "where to seat" question — one template for every case (useSeatChoiceText.ts).
    choice: {
      title: "Seat {{guest}}?",
      pickBill: "Which bill",
      pickBillOnTable: "Which bill on table {{table}}",
      outcome: "What happens",
      opens: "Will open",
      open: "Open",
      newTable: "New table {{table}}",
      onWaiter: "by {{waiter}}",
      // The disabled "Seat" while none of several options is chosen.
      pickTable: "Choose a bill",
    },
    // The bills of a reservation with several tables (SeatBillsSection.tsx); the
    // other texts are shared with "Reservations" (reservations.seatBills).
    bills: {
      label: "How to keep the bills",
      singleSummary: "All tables on one bill",
    },
    success: "{{guest}} — seated at table {{table}}",
    successSeparate: "{{guest}} — seated at tables {{tables}}, a separate bill for each table",
  },
  // "Reservations" module (src/pages/reservations/).
  reservations: {
    title: "Reservations",
    counts: {
      reservations_one: "{{count}} reservation",
      reservations_other: "{{count}} reservations",
      guests_one: "{{count}} guest",
      guests_other: "{{count}} guests",
      seats_one: "{{count}} seat",
      seats_other: "{{count}} seats",
    },
    duration: {
      minutes: "{{minutes}} min",
      hours: "{{hours}} h",
      hoursMinutes: "{{hours}} h {{minutes}} min",
    },
    day: {
      today: "Today, {{date}}",
      tomorrow: "Tomorrow, {{date}}",
      yesterday: "Yesterday, {{date}}",
      previous: "Previous day",
      next: "Next day",
      pick: "Pick a date",
    },
    newReservation: "New reservation",
    timeline: {
      now: "Now",
      preview: "Preview at",
      earlier: "Earlier",
      backToNow: "Now",
      slider: "Time of day",
      outside: "Outside {{from}}–{{to}}",
      inFuture: "in {{duration}}",
      inPast: "{{duration}} ago",
    },
    legend: {
      free: "Free",
      reserved: "Reserved",
      occupied: "Occupied",
      nextReservation: "Time of the next reservation",
      fits: "Fits the party",
      selected: "Selected",
      blocked: "Taken at this time",
    },
    table: {
      name: "Table {{name}}",
      hover: "Table {{name}} · {{seats}}",
      hoverReserved: "reserved {{time}} · {{guest}}",
      hoverOccupied: "occupied {{duration}}",
      hoverNext: "next reservation {{time}}",
    },
    popover: {
      close: "Close",
      status: {
        free: "Free",
        reserved: "Reserved",
        occupied: "Occupied",
      },
      late: "Late by {{duration}}",
      arriving: "Arrives in {{duration}}",
      seatedAt: "Seated at {{time}} · {{duration}}",
      finished: "Finished reservation",
      reservedMeanwhile: "Reservation at this time",
      occupiedSince: "Occupied · since {{time}} · {{duration}}",
      freeUntil: "Free until {{time}}",
      freeAllDay: "Free until the end of the day",
      next: "Next reservation",
      nextDetails_one: "{{time}} · {{guest}}, {{count}} guest",
      nextDetails_other: "{{time}} · {{guest}}, {{count}} guests",
      seat: "Seat the guests",
      cancel: "Cancel reservation",
      release: "Guests have left",
      reserveTable: "Reserve this table",
      seatBlockedOccupied_one: "Table {{tables}} is occupied by a seated reservation",
      seatBlockedOccupied_other: "Tables {{tables}} are occupied by a seated reservation",
      seatBlockedEarly: "Can be seated after {{time}}",
      seatBlockedPreviousBill: "The bill of the previous reservation on table {{table}} ({{waiter}}) is not closed yet",
      seatBlockedPreviousBillNoWaiter: "The bill of the previous reservation on table {{table}} is not closed yet",
      seatAttachHint: "Will be attached to {{waiter}}'s open bill",
      seatAttachHintNoWaiter: "Will be attached to the table's open bill",
      seatPickHint: "The table has several open bills — you will choose the waiter",
      edit: "Edit",
      unseat: "Undo seating",
      restore: "Restore to upcoming",
    },
    confirmCancel: {
      title: "Cancel this reservation?",
      content_one: "{{guest}} · {{time}} · Table {{tables}}. The table becomes free for this time.",
      content_other: "{{guest}} · {{time}} · Tables {{tables}}. The tables become free for this time.",
      ok: "Cancel reservation",
      back: "Back",
    },
    confirmUnseat: {
      title: "Undo the seating?",
      content_one: "{{guest}} · Table {{tables}}. The reservation goes back to “Upcoming”. The bill stays in Ordering.",
      content_other: "{{guest}} · Tables {{tables}}. The reservation goes back to “Upcoming”. The bill stays in Ordering.",
      contentBillCloses_one: "{{guest}} · Table {{tables}}. The reservation goes back to “Upcoming”. The empty bill on table {{billTable}} will be removed — nobody has ordered yet.",
      contentBillCloses_other: "{{guest}} · Tables {{tables}}. The reservation goes back to “Upcoming”. The empty bill on table {{billTable}} will be removed — nobody has ordered yet.",
      contentBillsClose_one: "{{guest}} · Table {{tables}}. The reservation goes back to “Upcoming”. The empty bills on tables {{billTables}} will be removed — nobody has ordered yet.",
      contentBillsClose_other: "{{guest}} · Tables {{tables}}. The reservation goes back to “Upcoming”. The empty bills on tables {{billTables}} will be removed — nobody has ordered yet.",
      ok: "Undo seating",
      back: "Back",
    },
    seatBills: {
      group: "The reservation's bills",
      single: "One bill",
      singlePick: "The tables have several open bills — choose which one",
      separate: "A separate bill for each table",
      separateSummary_one: "{{count}} bill of the reservation",
      separateSummary_other: "{{count}} bills of the reservation — one per table",
      separateLocalMode: "Online only — the till opens one bill",
      // "Separate…" with several bills on a table (seatBills.ts `separatePlanOf`, orderingSeatBills.ts).
      pickOnTable: "Table {{table}} has several open bills — choose which one",
      noBillOfWaiter: "Table {{table}} has several open bills and none of them is {{waiter}}'s",
      noOwnBill: "Table {{table}} has several open bills and none of them is yours",
      confirm: "Seat",
      back: "Back",
    },
    seatPicker: {
      title: "Which waiter should seat them?",
      subtitle_one: "{{guest}} · Table {{tables}}. The table has several open bills — the reservation will be attached to the chosen one.",
      subtitle_other: "{{guest}} · Tables {{tables}}. The tables have several open bills — the reservation will be attached to the chosen one.",
      unknownWaiter: "Unknown waiter",
      table: "Table {{name}}",
      openedAgo: "opened {{duration}} ago",
      openedJustNow: "opened just now",
      close: "Back",
    },
    messages: {
      seated: "Guests seated — the table's bill is open",
      seatedNewBill: "Guests seated. The table will belong to the waiter who places the first order",
      seatedAttached: "Guests seated with {{waiter}}",
      seatedAttachedNoWaiter: "Guests seated — the reservation is attached to the open bill",
      seatedSeparate: "Guests seated — a separate bill for each table ({{tables}})",
      unseated: "Seating undone",
      unseatedBillClosed: "Seating undone. The empty bill on table {{table}} was removed",
      unseatedBillsClosed: "Seating undone. The empty bills on tables {{tables}} were removed",
      unseatedBillClosedNoTable: "Seating undone. The empty bill was removed",
      restored: "The reservation is back in “Upcoming”",
      restoredExpired: "Its time has passed — the reservation stays in “Past” as “Not fulfilled”",
      released: "The reservation is completed",
      releasedBillOpen: "The reservation is completed. The table stays occupied until the bill is closed in Ordering",
      cancelled: "Reservation cancelled",
      seatUnavailable: "The reservation can no longer be seated",
      actionFailed: "The action could not be completed",
    },
    offlineHint: "Reservations work only online — the till has no layout or reservations.",
    offlineStaleHint: "The till has no fresh data for this day — pick a day near today or wait for the internet.",
    dataAsOf: "data as of {{time}}",
    loadError: "Reservations could not be loaded",
    loadErrorHint: "Check the connection and try again in a moment.",
    noSchema: {
      title: "No restaurant layout yet",
      description: "Draw the rooms and tables in Reservations → Floor plan — reservations are made on it.",
    },
    noTables: {
      title: "No tables yet",
      description:
        "Reservations are made on the tables of the floor plan. Arrange the rooms and tables there — then you will see them here too.",
      action: "Go to the floor plan",
    },
    noTablesInRegion: {
      title: "This area has no tables yet",
      description: "Arrange its tables on the floor plan — then you will see them here too.",
    },
    regionAll: "All",
    emptyRegion: "This area has no tables yet.",
    list: {
      tabs: {
        upcoming: "Upcoming",
        seated: "Seated",
        past: "Past",
      },
      search: "Name, phone or table",
      clearSearch: "Clear search",
      seat: "Seat",
      release: "Guests left",
      tableInRegion: "{{table}} · {{region}}",
      late: "Late {{duration}}",
      soon: "In {{duration}}",
      seatedUntil: "Seated {{time}} – {{until}}",
      completed: "Completed",
      cancelled: "Cancelled",
      expired: "Not fulfilled",
      empty: {
        upcoming: "No upcoming reservations for the day",
        seated: "No seated guests",
        past: "No past reservations yet",
      },
      noMatches: "No reservations match the search",
      noTable: "No table",
      cancel: "Cancel",
      restore: "Restore",
      edit: "Edit reservation",
      unseat: "Undo seating",
      moment: {
        tabHasActive: "Has reservations at the tables at {{time}}",
      },
    },
    form: {
      title: "New reservation",
      editTitle: "Edit reservation",
      close: "Close",
      when: "When",
      today: "Today",
      tomorrow: "Tomorrow",
      otherDate: "Other date…",
      guests: "Guests",
      fewerGuests: "Fewer guests",
      moreGuests: "More guests",
      duration: "Duration",
      tables: "Tables",
      suggest: "Suggest a table",
      noSuggestion_one: "No free table for {{count}} guest at this time",
      noSuggestion_other: "No free table for {{count}} guests at this time",
      tablesEmpty: "Click a free table on the layout. You can pick several for a larger group.",
      tableChip: "Table {{name}} · {{seats}}",
      removeTable: "Remove table {{name}}",
      seatsEnough: "{{seats}} for {{guests}}",
      seatsShort: "Not enough seats: {{seats}} for {{guests}}",
      freeInRegion: "Free tables for {{guests}} in “{{region}}” · {{time}}. Pick one or more.",
      noFreeInRegion: "No free table for {{guests}} here — try another area or show all tables.",
      showAllTables: "Show all tables ({{count}})",
      showOfferedOnly: "Show free tables only",
      regionHasSelected: "Has a selected table",
      region: "Area",
      freeCount_one: "{{count}} free",
      freeCount_other: "{{count}} free",
      tablesBusy: "Taken at this time: {{names}} — pick another table",
      guest: "Guest",
      phone: "Phone",
      name: "Name",
      note: "Note — occasion, allergies, high chair…",
      cancel: "Cancel",
      create: "Create reservation",
      created: "The reservation was created",
      createFailed: "The reservation was not created",
      save: "Save changes",
      saved: "Changes saved",
      saveFailed: "The changes were not saved",
      dayLoading: "Loading table availability for the day…",
      dayError: "Table availability for the day could not be loaded",
      retry: "Try again",
      slotPast: "The selected time has passed — pick another",
      dayClosed: "Day off — no reservations",
      slotOutsideHours: "The time is outside working hours — pick another",
      afterMidnightHint: "The hours after midnight ({{from}}–{{to}}) are on {{day}}",
      errors: {
        startAt: "Invalid reservation date or time",
        durationMinutes: "The duration must be between 30 and 480 minutes",
        guests: "The number of guests must be between 1 and 500",
        guestName: "Enter a name (up to 100 characters)",
        phone: "Invalid phone number",
        nameRequired: "Enter a name",
        phoneInvalid: "Invalid phone number",
        note: "Invalid note",
        tableIds: "Select at least one table",
      },
      // Known phone: the chip in the "Phone" field and the history card.
      guestHistory: {
        title: "History",
        show: "Show the guest's history",
        hide: "Hide the guest's history",
        misses: "Has cancelled or no-show reservations",
        reservations_one: "{{count}} reservation",
        reservations_other: "{{count}} reservations",
        visits_one: "{{count}} visit",
        visits_other: "{{count}} visits",
        lastVisit: "Last visit",
        averageBill: "Average bill",
        expired_one: "{{count}} no-show",
        expired_other: "{{count}} no-shows",
        cancelled_one: "{{count}} cancelled",
        cancelled_other: "{{count}} cancelled",
        upcoming_one: "{{count}} upcoming",
        upcoming_other: "{{count}} upcoming",
        seated_one: "{{count}} seated now",
        seated_other: "{{count}} seated now",
        lastNote: "Last note",
      },
    },
    picker: {
      badgeSelected: "Selected",
      badgeBusy: "Taken",
      hoverBusy: "taken at this time",
      hoverSelected: "selected",
      hoverFits: "fits the guests",
    },
  },
  reservationsTable: {
    title: "Reservations table",
    table: "Table",
    search: "Search by table or guest…",
    noMatch: "No tables match this search.",
    hideUntil: "Hide until the 1st reservation",
    hideUntilHint: "Hides the hours before the first upcoming or seated reservation",
    showWholeDay: "Show the whole day",
    hourLoadHint: "Reservations | guests in this hour",
    newAt: "New reservation — {{table}}, {{time}}",
    now: "now {{time}}",
    onlyUpcomingEditable: "Only an upcoming reservation can be edited",
    legend: {
      expired: "No-show",
      completed: "Completed",
    },
  },
};

export default en;
